/**
 * Dashboard. Auth gate first (/api/me → login form), then the operator console:
 * an app shell (sidebar on desktop, bottom tab bar on phones) with the live
 * sections — Panoramica, Partite, Segnali manuali, Storico, Sistema, Guida —
 * plus the M2 controls (kill-switch, cancel-all, start/stop/restart) with
 * confirmation modals for everything except ENGAGING the kill-switch, which is
 * the safe direction and must be instant.
 *
 * Every section stays mounted (hidden via CSS) so in-progress edits — config
 * editor text, wallet form, manual-lane selection — survive switching tabs.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { marked } from "marked";
import { useLiveState } from "./ws.ts";
import { useSignalAlerts, ALERT_TYPES } from "./alerts.ts";
import { useAutoOpen, openVideo } from "./auto-open.ts";
import { api, post, del, ApiError } from "./api.ts";
import { signalSourceLabel } from "./source-label.ts";
import { polymarketEventUrl } from "./polymarket-link.ts";
import { iconaMarchio } from "./brand-icons.ts";
import { sportradarUrl } from "./match-links.ts";
import { LANE_TINTS, clearLaneMarks, moveLane, nudgeLane, setLaneOrder, setLaneTint, sortLanes, syncLaneMarks, tintHex, useLaneMarks } from "./lane-marks.ts";
import { missingCategories, rowMarket } from "./market-search.ts";
import { FAMILY_LABELS, familyOfKind, type CategoryNeeds, type MarketFamily } from "../market-needs.ts";
import { BET_CATEGORIES, type BetCategory, type CanceledGoalRow, type ClosedPosition, type Decision, type GoalStats, type LogLine, type MarketResolution, type MarketSearchState, type MatchSignal, type MatchStateEvent, type OpenOrder, type Position, type PrecheckRow, type UnitStatus, type WalletView, type WatchGroup } from "../types.ts";
import type { PositionLabel } from "../position-labels.ts";
import { betName } from "./position-name.ts";
import type { WalletStatus } from "../wallets.ts";

type Me = { name: string; role: "owner" | "operator" };
type Confirm = { message: string; action: () => Promise<void>; requireText?: string } | null;

const hhmmss = (iso?: string) => (iso ? new Date(iso).toLocaleTimeString("en-GB") : "");

function chipClass(type: string): string {
  if (type.includes("CANCELED") || type === "RED") return "chip bad";
  if (type.startsWith("POSSIBLE")) return "chip possible";
  if (type.includes("GOAL") || type === "PENALTY_GOAL") return "chip goal";
  if (type.startsWith("VAR") || type === "PENALTY") return "chip var";
  if (type === "YELLOW") return "chip card-y";
  return "chip neutral";
}

const DECISION_LABEL: Record<Decision["category"], string> = {
  try: "Prova",
  open: "Apre",
  close: "Chiude",
  rejected: "Rifiutata",
};
const decisionChip = (c: Decision["category"]) => `chip dec-${c}`;

// ── inline icon set (stroke, currentColor) ───────────────────────────────────
type IconName = "overview" | "positions" | "matches" | "signals" | "history" | "stats" | "fair" | "refresh" | "system" | "docs" | "wallet" | "bell" | "bellOff" | "video" | "videoOff" | "power" | "logout" | "more" | "close" | "alert" | "grip";

const ICON_PATHS: Record<IconName, React.ReactNode> = {
  overview: (
    <>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="4.5" rx="1.5" />
      <rect x="13.5" y="10.5" width="7.5" height="10.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </>
  ),
  positions: (
    <>
      <rect x="3" y="6" width="18" height="4.5" rx="2.25" />
      <rect x="3" y="13.5" width="10.5" height="4.5" rx="2.25" />
    </>
  ),
  matches: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.4l4.4 3.2-1.7 5.2H9.3l-1.7-5.2z" />
    </>
  ),
  signals: (
    <>
      <path d="M11 5 6 9H3v6h3l5 4z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
      <path d="M18.5 6a9 9 0 0 1 0 12" />
    </>
  ),
  history: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.2 2" />
    </>
  ),
  system: (
    <>
      <path d="M4 6h16M4 12h16M4 18h16" />
      <circle cx="9" cy="6" r="2" fill="var(--surface)" />
      <circle cx="15" cy="12" r="2" fill="var(--surface)" />
      <circle cx="7" cy="18" r="2" fill="var(--surface)" />
    </>
  ),
  stats: (
    <>
      <path d="M3 20.5h18" />
      <rect x="4.5" y="12" width="4" height="6.5" rx="1" />
      <rect x="10" y="7.5" width="4" height="11" rx="1" />
      <rect x="15.5" y="3.5" width="4" height="15" rx="1" />
    </>
  ),
  // una freccia che torna su se stessa: rileggere, non ricaricare la pagina
  refresh: (
    <>
      <path d="M20.5 12a8.5 8.5 0 1 1-2.5-6" />
      <path d="M20.5 3.5v6h-6" />
    </>
  ),
  // una bilancia: due lavagne a confronto, che è quello che il fair value fa
  fair: (
    <>
      <path d="M12 3.5v17" />
      <path d="M5 6.5h14" />
      <path d="M8.5 6.5 5.5 13h6z" />
      <path d="M15.5 6.5 12.5 13h6z" />
      <path d="M8.5 20.5h7" />
    </>
  ),
  docs: (
    <>
      <path d="M2.5 4.5H9a3.5 3.5 0 0 1 3.5 3.5v12A3 3 0 0 0 9.5 17h-7z" />
      <path d="M21.5 4.5H15A3.5 3.5 0 0 0 11.5 8v12a3 3 0 0 1 3-3h7z" />
    </>
  ),
  wallet: (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
      <path d="M3 9.5h18M15.5 14h2" />
    </>
  ),
  bell: (
    <>
      <path d="M18 8.5a6 6 0 1 0-12 0c0 6.5-2.5 7.5-2.5 7.5h17S18 15 18 8.5" />
      <path d="M13.7 19.5a2 2 0 0 1-3.4 0" />
    </>
  ),
  bellOff: (
    <>
      <path d="M18 8.5a6 6 0 0 0-9.7-4.7M6.3 6.3C6.1 7 6 7.7 6 8.5c0 6.5-2.5 7.5-2.5 7.5h14" />
      <path d="M13.7 19.5a2 2 0 0 1-3.4 0" />
      <path d="M3 3l18 18" />
    </>
  ),
  video: (
    <>
      <rect x="2.5" y="5.5" width="13" height="13" rx="2.5" />
      <path d="M15.5 10.5l6-3.5v10l-6-3.5z" />
    </>
  ),
  videoOff: (
    <>
      <path d="M8.5 5.5H13a2.5 2.5 0 0 1 2.5 2.5v4M15.5 15.5A2.5 2.5 0 0 1 13 18.5H5A2.5 2.5 0 0 1 2.5 16V8a2.5 2.5 0 0 1 2-2.45" />
      <path d="M15.5 10.5l6-3.5v10l-2.6-1.5" />
      <path d="M3 3l18 18" />
    </>
  ),
  power: (
    <>
      <path d="M18.36 6.64a9 9 0 1 1-12.72 0" />
      <path d="M12 2.5V11" />
    </>
  ),
  logout: (
    <>
      <path d="M9 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </>
  ),
  more: (
    <>
      <circle cx="12" cy="5" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1.6" fill="currentColor" stroke="none" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6L6 18" />,
  // la presa: due colonne di punti, il gesto universale del "questo si trascina"
  grip: (
    <>
      <circle cx="9" cy="6" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15" cy="6" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="9" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="9" cy="18" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15" cy="18" r="1.5" fill="currentColor" stroke="none" />
    </>
  ),
  alert: (
    <>
      <path d="M12 3.5 22 20H2z" />
      <path d="M12 10v4M12 17.2v.3" />
    </>
  ),
};

function Icon({ name, size = 18 }: { name: IconName; size?: number }) {
  return (
    <svg className="icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICON_PATHS[name]}
    </svg>
  );
}

// ── auth gate ────────────────────────────────────────────────────────────────
export function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  useEffect(() => {
    api<Me>("/api/me")
      .then(setMe)
      .catch(() => setMe(null));
  }, []);
  if (me === undefined) return <div className="login-wrap muted">Loading…</div>;
  if (me === null) return <Login onLogin={setMe} />;
  return <Dashboard me={me} />;
}

function Login({ onLogin }: { onLogin: (me: Me) => void }) {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      onLogin(await post<Me>("/api/login", { name, password }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "network error");
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <form className="login" onSubmit={submit}>
        <div className="login-brand" aria-hidden="true">TP</div>
        <div className="login-title">Trading Panel</div>
        <div className="login-subtitle">Accedi alla console operativa</div>
        <label className="field-label" htmlFor="login-name">Username</label>
        <input id="login-name" placeholder="Il tuo username" value={name} onChange={(e) => setName(e.target.value)} autoFocus autoComplete="username" />
        <label className="field-label" htmlFor="login-password">Password</label>
        <input id="login-password" placeholder="La tua password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
        {error && <div className="login-error">{error}</div>}
        <button className="btn primary" disabled={busy || !name || !password}>
          {busy ? "Accesso…" : "Accedi"}
        </button>
      </form>
    </div>
  );
}

// ── app shell ────────────────────────────────────────────────────────────────
type View = "overview" | "positions" | "matches" | "signals" | "history" | "stats" | "fair" | "system" | "docs";

const VIEW_TITLE: Record<View, { title: string; sub: string }> = {
  overview: { title: "Panoramica", sub: "Saldo, posizioni e segnali in tempo reale" },
  positions: { title: "Posizioni", sub: "Quanto c’è a mercato adesso, partita per partita" },
  matches: { title: "Partite", sub: "Watch-list e mercati Polymarket" },
  signals: { title: "Segnali manuali", sub: "Anticipa i siti — invia quello che vedi" },
  history: { title: "Storico", sub: "Decisioni del bot e posizioni chiuse" },
  stats: { title: "Statistiche", sub: "Gol e gol annullati, per lega e per minuto" },
  fair: { title: "Quote", sub: "Fair value dai bookmaker, e quali gate li fermano" },
  system: { title: "Sistema", sub: "Processi, log, configurazione e audit" },
  docs: { title: "Guida", sub: "Documentazione operativa del pannello" },
};

function Dashboard({ me }: { me: Me }) {
  const s = useLiveState();
  const alerts = useSignalAlerts(s.signals, s.watchGroups);
  const autoOpen = useAutoOpen(s.signals, s.watchGroups);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [flash, setFlash] = useState("");

  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      setFlash("");
    } catch (err) {
      setFlash(err instanceof ApiError ? `${err.status}: ${err.message}` : "network error");
    }
  };
  const ask = (message: string, fn: () => Promise<unknown>, requireText?: string) => setConfirm({ message, action: () => run(fn), requireText });

  const [view, setView] = useState<View>("overview");
  // copy-trade wallets popup + its "restart required" reminder — lifted here so
  // the ⚠ survives closing the popup and clears only when the bot restarts
  const [walletsOpen, setWalletsOpen] = useState(false);
  const [walletsDirty, setWalletsDirty] = useState(false);
  // Mobile actions sheet (⋯). Desktop keeps every control inline in the topbar.
  const [sheetOpen, setSheetOpen] = useState(false);
  const snap = s.snapshot;
  const mode = snap?.mode ?? (s.mode.toUpperCase() as "PAPER" | "LIVE");

  // Recent closed round-trips, lifted here so the open list can hide phantoms (a
  // position already closed on Polymarket — won by redeem, or sold on the site —
  // that the bot ledger hasn't flattened yet) AND the Closed card can render them
  // without a second fetch. Refresh on mode switch, every 30s, and on each new fill.
  const [closedRows, setClosedRows] = useState<ClosedPosition[]>([]);
  useEffect(() => {
    const load = () => void api<ClosedPosition[]>("/api/history/closed-positions?limit=100").then(setClosedRows).catch(() => {});
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [mode, s.counts.fills]);
  const closedTokens = new Set(closedRows.map((r) => r.tokenId));

  // The bot keeps flat (size 0) rows in the snapshot so their realised PnL still
  // counts toward the header total — but they are CLOSED, not open exposure, so
  // the Positions card lists only the ones we actually hold AND that aren't already
  // recorded closed (a resolved token never re-trades, so this can't hide a re-open).
  const realized = snap?.positions.reduce((x, p) => x + p.realizedPnl, 0) ?? 0;
  const openPositions = snap?.positions.filter((p) => Math.abs(p.size) > 1e-9 && !closedTokens.has(p.tokenId)) ?? [];
  const botRunning = s.units.find((u) => u.name === "bot")?.running ?? false;

  // signal.match IS both the watch-group id and the market-resolution key —
  // indexed so every signal row can reach its match's Sportradar/Polymarket links.
  const groupsById = useMemo(() => Object.fromEntries(s.watchGroups.map((g) => [g.id, g])), [s.watchGroups]);
  const marketSlugs = useMemo(() => marketSlugByMatch(s.resolutions), [s.resolutions]);
  const srUrls = useMemo(() => sportradarUrlByMatch(s.signals), [s.signals]);

  // matches needing operator attention → badge on the Partite nav item.
  // A match still being searched is NOT attention: nothing to do but wait, and
  // 'needs-link' is the row's value from insert, so counting it raised the alarm
  // for every match the instant it was added.
  const needsMarket = s.watchGroups.filter(
    (g) => g.enabled && rowMarket(g.status, s.marketSearch[g.id]?.phase, botRunning).tone === "warn",
  ).length;
  const pendingResolutions = s.resolutions.filter((r) => r.status === "unresolved").length;
  const matchIssues = needsMarket + pendingResolutions;

  const nav: Array<{ id: View; label: string; icon: IconName; badge?: number; ownerOnly?: boolean }> = [
    { id: "overview", label: "Panoramica", icon: "overview" },
    { id: "positions", label: "Posizioni", icon: "positions" },
    { id: "matches", label: "Partite", icon: "matches", badge: matchIssues },
    { id: "signals", label: "Segnali", icon: "signals" },
    { id: "history", label: "Storico", icon: "history" },
    { id: "stats", label: "Statistiche", icon: "stats" },
    { id: "fair", label: "Quote", icon: "fair" },
    { id: "system", label: "Sistema", icon: "system" },
    { id: "docs", label: "Guida", icon: "docs", ownerOnly: true },
  ];
  const visibleNav = nav.filter((n) => !n.ownerOnly || me.role === "owner");

  const go = (v: View) => {
    setView(v);
    setSheetOpen(false);
  };

  const toggleAlerts = () => {
    if (alerts.permission !== "granted") alerts.requestPermission();
    alerts.setEnabled(!alerts.enabled);
  };

  const killButton = snap?.killed ? (
    <button className="btn kill-btn resume" onClick={() => ask("Rilasciare il kill-switch e riprendere il trading?", () => post("/api/bot/kill", { on: false }))} disabled={!botRunning}>
      <Icon name="power" size={15} /> Riprendi
    </button>
  ) : (
    <button className="btn danger kill-btn" onClick={() => void run(() => post("/api/bot/kill", { on: true }))} disabled={!botRunning} title="Blocca immediatamente tutti i nuovi ordini">
      <Icon name="power" size={15} /> STOP
    </button>
  );

  const modeSwitchButton = mode === "PAPER" ? (
    <button
      className="btn ghost mode-switch"
      onClick={() =>
        ask(
          `Stai passando il bot in modalità LIVE con SOLDI VERI. ${snap?.positions.length ? `Le ${snap.positions.length} posizioni paper aperte NON verranno trasferite. ` : ""}Il bot verrà riavviato. Scrivi LIVE per confermare.`,
          () => post("/api/mode", { mode: "live", confirmPhrase: "LIVE" }),
          "LIVE",
        )
      }
    >
      Vai LIVE
    </button>
  ) : (
    <button className="btn ghost mode-switch" onClick={() => ask("Riportare il bot in PAPER (dry-run)? Le posizioni LIVE aperte restano su Polymarket — chiudile prima se serve. Il bot verrà riavviato.", () => post("/api/mode", { mode: "paper" }))}>
      Vai PAPER
    </button>
  );

  const autoSellButton = snap?.autoSellBlocked ? (
    <button className="btn autosell" onClick={() => ask("Riattivare le vendite automatiche (trailing stop, timeout 20 min, chiusure su segnale)?", () => post("/api/bot/auto-sell-block", { on: false }))} disabled={!botRunning}>
      Riattiva vendite auto
    </button>
  ) : (
    <button
      className="btn warn autosell"
      onClick={() => ask("Bloccare tutte le vendite automatiche TRANNE il take-profit a 99.9? Il timeout di 20 minuti e gli altri stop automatici non venderanno più finché non lo riattivi.", () => post("/api/bot/auto-sell-block", { on: true }))}
      disabled={!botRunning}
      title="Blocca trailing stop, timeout 20 min e chiusure su segnale — lascia attivo solo il take-profit a 99.9"
    >
      Blocca vendite auto
    </button>
  );

  const cancelAllButton = (
    <button className="btn ghost cancel-orders" onClick={() => ask("Cancellare TUTTI gli ordini appoggiati sul book?", () => post("/api/bot/cancel-all"))} disabled={!botRunning}>
      Cancella ordini
    </button>
  );

  const servicesStatus = (
    <div className="services" aria-label="Stato servizi">
      {s.units.map((u) => (
        <span className="service" key={u.name}>
          <span className={`dot ${u.running ? "on" : "off"}`} /> {u.name}
        </span>
      ))}
      <span className="service" title={s.tap.detail}>
        <span className={`dot ${s.tap.connected ? "on" : "off"}`} /> signals
      </span>
    </div>
  );

  return (
    <div className="shell">
      {/* ── sidebar (desktop) ─────────────────────────────────────────────── */}
      <aside className="sidebar">
        <div className="side-brand">
          <span className="brand-mark" aria-hidden="true">TP</span>
          <span className="brand-copy">
            <span className="brand-name">Trading Panel</span>
            <span className="brand-sub">Control room</span>
          </span>
        </div>

        <nav className="side-nav" aria-label="Navigazione principale">
          {visibleNav.map((n) => (
            <button key={n.id} className={`nav-item ${view === n.id ? "active" : ""}`} onClick={() => go(n.id)}>
              <Icon name={n.icon} />
              <span className="nav-label">{n.label}</span>
              {!!n.badge && <span className="nav-badge">{n.badge}</span>}
            </button>
          ))}
        </nav>

        <div className="side-foot">
          {servicesStatus}
          <div className="side-user">
            <span className="user-avatar">{me.name.slice(0, 1).toUpperCase()}</span>
            <span className="user-meta">
              <span className="user-name">{me.name}</span>
              <span className="user-role">{me.role}</span>
            </span>
            <button className="btn ghost sm icon-btn" title={`Esci da ${me.name}`} onClick={() => void run(async () => (await post("/api/logout"), location.reload()))}>
              <Icon name="logout" size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── main column ───────────────────────────────────────────────────── */}
      <div className="main">
        {!s.wsConnected && <div className="ws-banner">Connessione al pannello persa — riconnessione…</div>}
        {flash && (
          <div className="flash" onClick={() => setFlash("")}>
            ⚠ {flash} <span className="muted">(clicca per chiudere)</span>
          </div>
        )}
        {/* the browser refused to auto-open a video (pop-ups not allowed for this
            origin) — the event isn't lost, one click here opens it, and a click IS
            a user gesture so it always goes through */}
        {autoOpen.blocked.map((b) => (
          <div className="popup-blocked" key={b.id}>
            <span className="grow">📺 {b.label} — apertura automatica bloccata dal browser</span>
            <button className="btn primary sm" onClick={() => { openVideo(b.id, b.url); autoOpen.dismissBlocked(b.id); }}>Apri</button>
            <button className="btn ghost sm" onClick={() => autoOpen.dismissBlocked(b.id)}>Ignora</button>
          </div>
        ))}

        <header className="topbar">
          <span className="brand-mark tb-brand" aria-hidden="true">TP</span>
          <div className="tb-title">
            <h1>{VIEW_TITLE[view].title}</h1>
            <span className="tb-sub">{VIEW_TITLE[view].sub}</span>
          </div>

          <div className="tb-status">
            <span className={`badge ${mode === "LIVE" ? "mode-live" : "mode-paper"}`}>
              {mode === "LIVE" ? "● LIVE" : "PAPER"}
              {!snap && " · offline"}
            </span>
            {snap?.killed && <span className="badge killed">STOP</span>}
            {snap?.autoSellBlocked && <span className="badge autosell-blocked">SOLO TP 99.9</span>}
            <span className={`sync-state ${s.wsConnected ? "connected" : "disconnected"}`} title={s.wsConnected ? "Dati in tempo reale" : "Riconnessione…"}>
              <span className="dot" />
              <span className="sync-label">{s.wsConnected ? "in tempo reale" : "riconnessione…"}</span>
            </span>
          </div>

          <div className="tb-actions">
            {cancelAllButton}
            {autoSellButton}
            {modeSwitchButton}
            <button className={`btn ghost icon-btn ${alerts.enabled ? "on" : ""}`} onClick={toggleAlerts} title="Notifiche browser + audio per goal/VAR sulle partite attive">
              <Icon name={alerts.enabled ? "bell" : "bellOff"} size={17} />
            </button>
            <button
              className={`btn ghost icon-btn ${autoOpen.enabled ? "on" : ""}`}
              onClick={() => autoOpen.setEnabled(!autoOpen.enabled)}
              title={autoOpen.enabled ? "Apertura automatica del video live ATTIVA — clicca per bloccarla" : "Apertura automatica del video live BLOCCATA — clicca per attivarla (solo desktop, richiede il link video sulla partita)"}
            >
              <Icon name={autoOpen.enabled ? "video" : "videoOff"} size={17} />
            </button>
            <button className="btn ghost icon-btn wallet-btn" onClick={() => { setWalletsOpen(true); setSheetOpen(false); }} title="Gestisci i wallet secondari su cui replicare gli ordini (copy-trade)">
              <Icon name="wallet" size={17} />
              {walletsDirty && <span className="wallet-dirty-dot" title="modifiche in attesa di riavvio del bot" />}
            </button>
          </div>

          {killButton}

          <button className="btn ghost icon-btn tb-more" aria-label={sheetOpen ? "Chiudi azioni" : "Apri azioni"} aria-expanded={sheetOpen} onClick={() => setSheetOpen((o) => !o)}>
            <Icon name={sheetOpen ? "close" : "more"} size={19} />
          </button>
        </header>

        <main className="content">
          {/* ── Panoramica ──────────────────────────────────────────────── */}
          <section className={`page ${view === "overview" ? "active" : ""}`}>
            <div className="metric-grid">
              <div className="metric"><span className="metric-label">Balance</span><strong className="num">${(snap?.usdc ?? 0).toFixed(2)}</strong><span className="metric-meta">USDC disponibile</span></div>
              <div className="metric"><span className="metric-label">PnL realizzato</span><strong className={`num ${realized >= 0 ? "pnl-pos" : "pnl-neg"}`}>{realized >= 0 ? "+" : ""}{realized.toFixed(2)}</strong><span className="metric-meta">Performance corrente</span></div>
              <div className="metric"><span className="metric-label">Posizioni</span><strong className="num">{openPositions.length}</strong><span className="metric-meta">{snap?.openOrders ?? 0} ordini aperti</span></div>
              <div className="metric"><span className="metric-label">Bot</span><strong className={botRunning ? "status-good" : "status-off"}>{botRunning ? "Operativo" : "Offline"}</strong><span className="metric-meta">Modalità {mode.toLowerCase()}</span></div>
            </div>

            {matchIssues > 0 && (
              <button className="banner-alert" onClick={() => go("matches")}>
                <Icon name="alert" size={16} />
                {matchIssues} {matchIssues === 1 ? "partita richiede" : "partite richiedono"} attenzione (mercato Polymarket mancante) — vai a Partite
              </button>
            )}

            {/* Row 1 pairs the quick-send lanes with Segnali live (`equal`: the signal
                feed sets the row height and the panel next to it matches). Posizioni
                and Attività each take a full row below — the lanes are what the
                operator reaches for mid-match, so they get the top-left corner. */}
            <div className="page-grid two equal">
              <QuickSignalPanel groups={s.watchGroups} signals={s.signals} matchScores={s.matchScores} resolutions={s.resolutions} />

              <div className="card">
                <div className="card-head">
                  Segnali live <span className="count">({s.counts.signals})</span>
                </div>
                <div className="card-body">
                  {s.signals.length === 0 ? <div className="empty">In attesa di eventi dalle partite…</div> : [...s.signals].slice(-60).reverse().map((sig) => <SignalRow s={sig} group={groupsById[sig.match]} marketSlug={marketSlugs[sig.match]} srFallback={srUrls[sig.match]} key={sig.id} />)}
                </div>
              </div>

              <div className="card span-all">
                <div className="card-head">
                  Posizioni <span className="count">({openPositions.length}) · ordini aperti {snap?.openOrders ?? 0}</span>
                </div>
                <div className="card-body">
                  {!snap || openPositions.length === 0 ? (
                    <div className="empty">{botRunning ? "Flat — nessuna posizione aperta." : "Il bot non è in esecuzione."}</div>
                  ) : (
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>Token</th>
                          <th>Match</th>
                          <th>Size</th>
                          <th>Avg</th>
                          <th>Realized</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {openPositions.map((p) => (
                          <tr key={p.tokenId}>
                            <td className="num" data-label="Token">{p.tokenId.slice(0, 10)}…</td>
                            <td data-label="Match">{p.match ?? "—"}</td>
                            <td className={`num ${p.size >= 0 ? "pnl-pos" : "pnl-neg"}`} data-label="Size">{p.size.toFixed(1)}</td>
                            <td className="num" data-label="Avg">{p.avgPrice.toFixed(2)}</td>
                            <td className={`num ${p.realizedPnl >= 0 ? "pnl-pos" : "pnl-neg"}`} data-label="Realized">{p.realizedPnl.toFixed(2)}</td>
                            <td data-label="">
                              <button
                                className="btn ghost sm"
                                disabled={!botRunning || p.size <= 0}
                                title="Vendi a mercato l'intera posizione"
                                onClick={() =>
                                  ask(
                                    `Vendi l'INTERA posizione su ${p.match ?? `${p.tokenId.slice(0, 10)}…`} (${p.size.toFixed(1)} shares)? Piazza un ordine SELL a mercato che prende qualsiasi prezzo nel book.`,
                                    () => post(`/api/bot/sell/${p.tokenId}`),
                                  )
                                }
                              >
                                Vendi
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <OpenOrders orders={snap?.openOrdersList ?? []} botRunning={botRunning} run={run} />
                  <PnlChart mode={mode} />
                </div>
              </div>

              <div className="card span-all">
                <div className="card-head">
                  Attività del bot <span className="count">(ordini {s.counts.orders} · fill {s.counts.fills} · rifiutati {s.counts.rejected})</span>
                </div>
                <div className="card-body">
                  {s.events.length === 0 ? (
                    <div className="empty">Nessuna attività per ora.</div>
                  ) : (
                    [...s.events].slice(-80).reverse().map((e, i) => (
                      <div className="row" key={i}>
                        <span className="ts">{hhmmss(e.ts)}</span>
                        <span className={`grow ev-${e.kind}`}>
                          {e.text}
                          {e.count > 1 && <span className="muted"> ×{e.count}</span>}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* ── Partite ─────────────────────────────────────────────────── */}
          {/* ── Posizioni ───────────────────────────────────────────────── */}
          <section className={`page ${view === "positions" ? "active" : ""}`}>
            <PositionsView
              active={view === "positions"}
              positions={openPositions}
              orders={snap?.openOrdersList ?? []}
              groups={s.watchGroups}
              signals={s.signals}
              matchScores={s.matchScores}
              botRunning={botRunning}
              ask={ask}
              run={run}
            />
          </section>

          <section className={`page ${view === "matches" ? "active" : ""}`}>
            <div className="page-grid two">
              <WatchlistCard groups={s.watchGroups} matchScores={s.matchScores} marketSearch={s.marketSearch} resolutions={s.resolutions} categoryNeeds={s.categoryNeeds} run={run} ask={ask} botRunning={botRunning} />
              <MarketResolutions resolutions={s.resolutions} errors={s.resolveErrors} run={run} botRunning={botRunning} precheck={s.precheck} />
              {/* a tutta larghezza sotto la coppia, come ConfigEditor in Sistema:
                  dentro la griglia, così prende il gap invece di incollarsi */}
              <div className="span-all">
                <ImportCard run={run} />
              </div>
            </div>
          </section>

          {/* ── Segnali manuali ─────────────────────────────────────────── */}
          <section className={`page ${view === "signals" ? "active" : ""}`}>
            <ManualSignalView groups={s.watchGroups} signals={s.signals} matchScores={s.matchScores} resolutions={s.resolutions} />
          </section>

          {/* ── Storico ─────────────────────────────────────────────────── */}
          <section className={`page ${view === "history" ? "active" : ""}`}>
            <div className="page-grid">
              <Decisions activity={s.counts.orders + s.counts.fills + s.counts.rejected} />
              <ClosedPositions rows={closedRows} mode={mode} />
            </div>
          </section>

          {/* ── Statistiche ─────────────────────────────────────────────── */}
          <section className={`page ${view === "stats" ? "active" : ""}`}>
            <GoalStatsView active={view === "stats"} />
          </section>

          {/* ── Quote (fair value) ──────────────────────────────────────── */}
          <section className={`page ${view === "fair" ? "active" : ""}`}>
            <FairValuesView active={view === "fair"} />
          </section>

          {/* ── Sistema ─────────────────────────────────────────────────── */}
          <section className={`page ${view === "system" ? "active" : ""}`}>
            <div className="page-grid two">
              <div className="card">
                <div className="card-head">Processi</div>
                <Processes units={s.units} ask={ask} run={run} />
              </div>
              <Logs logs={s.logs} />
              <div className="span-all">
                <ConfigEditor me={me} ask={ask} />
              </div>
              {me.role === "owner" && (
                <div className="span-all">
                  <Audit />
                </div>
              )}
            </div>
          </section>

          {/* ── Guida (owner) ───────────────────────────────────────────── */}
          {me.role === "owner" && (
            <section className={`page ${view === "docs" ? "active" : ""}`}>
              <DocsView />
            </section>
          )}
        </main>

        {/* ── bottom tab bar (mobile) ─────────────────────────────────────── */}
        <nav className="bottomnav" aria-label="Navigazione principale">
          {nav.filter((n) => !n.ownerOnly).map((n) => (
            <button key={n.id} className={`bn-item ${view === n.id ? "active" : ""}`} onClick={() => go(n.id)}>
              <span className="bn-icon">
                <Icon name={n.icon} size={20} />
                {!!n.badge && <span className="nav-badge">{n.badge}</span>}
              </span>
              <span className="bn-label">{n.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* ── mobile actions sheet ────────────────────────────────────────────── */}
      {sheetOpen && (
        <div className="overlay sheet-overlay" onClick={() => setSheetOpen(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-grab" aria-hidden="true" />
            <div className="sheet-user">
              <span className="user-avatar">{me.name.slice(0, 1).toUpperCase()}</span>
              <span className="user-meta">
                <span className="user-name">{me.name}</span>
                <span className="user-role">{me.role}</span>
              </span>
              <button className="btn ghost sm" onClick={() => void run(async () => (await post("/api/logout"), location.reload()))}>
                <Icon name="logout" size={15} /> Esci
              </button>
            </div>
            {servicesStatus}
            <div className="sheet-actions">
              {modeSwitchButton}
              {autoSellButton}
              {cancelAllButton}
              <button className="btn ghost" onClick={() => { setWalletsOpen(true); setSheetOpen(false); }}>
                <Icon name="wallet" size={16} /> Wallet copy-trade
                {walletsDirty && <span className="wallet-dirty-dot" title="modifiche in attesa di riavvio del bot" />}
              </button>
              <button className="btn ghost" onClick={toggleAlerts}>
                <Icon name={alerts.enabled ? "bell" : "bellOff"} size={16} /> Avvisi {alerts.enabled ? "attivi" : "spenti"}
              </button>
              {/* the opening itself is desktop-only, but the switch stays reachable
                  here so it can be armed/disarmed from a phone for the desktop tab */}
              <button className="btn ghost" onClick={() => autoOpen.setEnabled(!autoOpen.enabled)}>
                <Icon name={autoOpen.enabled ? "video" : "videoOff"} size={16} /> Video auto {autoOpen.enabled ? "attivo" : "bloccato"}
              </button>
              {me.role === "owner" && (
                <button className="btn ghost" onClick={() => go("docs")}>
                  <Icon name="docs" size={16} /> Guida
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {walletsOpen && (
        <div className="overlay" onClick={() => setWalletsOpen(false)}>
          <div className="modal wallets-modal" onClick={(e) => e.stopPropagation()}>
            <button className="btn ghost sm wallets-close" aria-label="Chiudi" onClick={() => setWalletsOpen(false)}>
              <Icon name="close" size={16} />
            </button>
            <WalletsCard wallets={s.wallets} run={run} ask={ask} botRunning={botRunning} dirty={walletsDirty} setDirty={setWalletsDirty} />
          </div>
        </div>
      )}

      {confirm && <ConfirmModal confirm={confirm} dismiss={() => setConfirm(null)} />}
    </div>
  );
}

function ConfirmModal({ confirm, dismiss }: { confirm: NonNullable<Confirm>; dismiss: () => void }) {
  const [typed, setTyped] = useState("");
  const armed = !confirm.requireText || typed === confirm.requireText;
  return (
    <div className="overlay" onClick={dismiss}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-msg">{confirm.message}</div>
        {confirm.requireText && (
          <input className="modal-input" placeholder={`scrivi ${confirm.requireText} qui`} value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus spellCheck={false} />
        )}
        <div className="modal-actions">
          <button className="btn ghost" onClick={dismiss} autoFocus={!confirm.requireText}>
            No, indietro
          </button>
          <button
            className="btn danger"
            disabled={!armed}
            onClick={() => {
              const a = confirm.action;
              dismiss();
              void a();
            }}
          >
            Sì, procedi
          </button>
        </div>
      </div>
    </div>
  );
}

function Sparkline({ points, width = 260, height = 42 }: { points: number[]; width?: number; height?: number }) {
  if (points.length < 2) return <span className="config-hint">Il grafico PnL appare dopo qualche minuto di storico.</span>;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const coords = points.map((p, i) => `${((i / (points.length - 1)) * width).toFixed(1)},${(height - 3 - ((p - min) / span) * (height - 6)).toFixed(1)}`);
  const up = (points[points.length - 1] ?? 0) >= (points[0] ?? 0);
  return (
    <svg viewBox={`0 0 ${width} ${height}`} height={height} preserveAspectRatio="none" className="spark">
      <polyline points={coords.join(" ")} fill="none" stroke={up ? "var(--green)" : "var(--red)"} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/** match key → the Polymarket event slug the bot resolved for it. Only resolved
 *  rows carry a slug; the map is keyed the same way signals are (`signal.match`). */
function marketSlugByMatch(resolutions: MarketResolution[]): Record<string, string> {
  return Object.fromEntries(resolutions.filter((r) => r.slug).map((r) => [r.match, r.slug!]));
}

/** match key → SIR page, harvested from the match's own sportradar-sourced
 *  signals. Lets every row of a match link to Sportradar (not just the rows the
 *  SIR feed produced) even after the match left the watch-list. */
function sportradarUrlByMatch(signals: MatchSignal[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const sig of signals) {
    if (map[sig.match] || !sig.url) continue;
    const url = sportradarUrl([sig.url]);
    if (url) map[sig.match] = url;
  }
  return map;
}

/** Polymarket badge — a "P" monogram in the same line-art style as ICON_PATHS
 *  (kept out of that set because it's a per-row brand mark, not nav chrome). */
function PolymarketMark({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="4.5" />
      <path d="M9.5 17.2V7.2h3.4a2.9 2.9 0 0 1 0 5.9H9.5" />
    </svg>
  );
}

/** One signal line. `group` (the watch-list entry this signal belongs to —
 *  `s.match` IS the group id) and `marketSlug` (the market the bot resolved for
 *  that same match key) back the two deep links that aren't on the signal
 *  itself: the match name → Sportradar, and the Polymarket button. */
function SignalRow({ s, group, marketSlug, srFallback }: { s: MatchSignal; group?: WatchGroup; marketSlug?: string | null; srFallback?: string | null }) {
  const sourceLabel = signalSourceLabel(s.source, s.feed);
  // The watch-group is gone once a match ends (finished-cleanup), so history rows
  // fall back to the SIR page seen on the match's own sportradar-sourced signals.
  const srUrl = sportradarUrl(group?.links) ?? sportradarUrl(s.url ? [s.url] : []) ?? srFallback ?? null;
  // Operator-authored binding first, else the slug the bot resolved by itself —
  // most live matches only ever have the latter.
  const pmUrl = polymarketEventUrl(group?.polymarket ?? null) ?? polymarketEventUrl(marketSlug ?? null);
  const title = `${s.match} — ${s.player ?? ""} ${s.note ?? ""}`;
  const text = (
    <>
      {s.homeTeam && s.awayTeam ? `${s.homeTeam} – ${s.awayTeam}` : s.match}
      {s.team ? `  ·  ${s.team}` : ""}
      {s.minute != null ? `  ·  ${s.minute}'${s.addedTime ? `+${s.addedTime}` : ""}` : ""}
      {s.score ? `  ·  ${s.score}` : ""}
    </>
  );
  return (
    <div className="row">
      <span className="ts">{hhmmss(s.ts)}</span>
      <span className={chipClass(s.type)}>{s.type}</span>
      {srUrl ? (
        <a className="grow signal-match-link" href={srUrl} target="_blank" rel="noreferrer" title={`${title} · apri su Sportradar`}>
          {text}
        </a>
      ) : (
        <span className="grow" title={title}>
          {text}
        </span>
      )}
      {s.url ? (
        <a className="stat source-link" href={s.url} target="_blank" rel="noreferrer" title={`${sourceLabel} (${s.feed}) · open on ${s.source}: ${s.url}`}>
          {sourceLabel}
        </a>
      ) : (
        <span className="stat" title={`${sourceLabel} (${s.feed})`}>{sourceLabel}</span>
      )}
      {pmUrl && (
        <a className="pm-btn" href={pmUrl} target="_blank" rel="noreferrer" title="Apri il mercato su Polymarket">
          <PolymarketMark />
        </a>
      )}
    </div>
  );
}

// ── manual signals (stadium console) ─────────────────────────────────────────
// One LANE per watched match, shaped like a scoresheet: the two button columns
// ARE the teams (named once in the lane head, the tally sitting on the spine
// between them) and the rows are the event verbs — so a button carries a verb,
// never a team name repeated fourteen times. Desktop tiles as many lanes as fit;
// at ≤760px the switcher picks one and the rest are hidden, so a thumb at the
// stadium never leaves that match.
//
// Signals are injected into the SAME consolidator as the site feeds, so a later
// site report of the same event folds in — the bot opens ONE position.
//
// VAR is modelled as a STATE, not a permanent button bank. A lane with an open
// review — seen on the feed, or opened by the operator's own tap — swaps in a
// strip carrying only the two outcomes for THAT review, team already fixed, so
// an outcome can't go out for the wrong team or with no review pending. The full
// outcome bank stays one tap away under "Esiti VAR" for a review nobody reported.
//
// Lanes are NEVER reordered by activity: these buttons move real money, and a
// list that reflows under the cursor is how you tap the wrong match.

/** The match clock, as a best-effort stamp for an operator tap: the HIGHEST
 *  minute seen on this match's feeds, never a negative one.
 *
 *  Not "the last one that carried a minute", which is what this used to be. A
 *  feed minute is not monotonic across sources (a slow board back-fills an old
 *  event with a fresh timestamp) and Sportradar sends negative sentinels: `-1`
 *  for unknown, `-5` on cards shown off the running clock. On 29 Aug 2026 a
 *  `-5` YELLOW was the last minute-carrying signal on Sassuolo-Torino, so the
 *  goal Flavio tapped six minutes later went out stamped `-5'`; the bot's
 *  stale-by-clock gate read it as 45 minutes of re-delivered history and skipped
 *  every entry. The clock only goes forward, so take the maximum. */
export function lastMinute(signals: MatchSignal[]): number | null {
  let best: number | null = null;
  for (const s of signals) {
    const m = s.minute;
    if (typeof m === "number" && m >= 0 && (best == null || m > best)) best = m;
  }
  return best;
}

/** How far an operator tap may be carried past the last thing a feed said.
 *  Beyond this the feeds have been quiet long enough that the carry is worth
 *  less than admitting we don't know — and a half-time break would otherwise
 *  be counted as play. */
export const TAP_CARRY_CAP_MIN = 8;

/**
 * The match clock to stamp on an operator tap, carried forward to NOW.
 *
 * `lastMinute` is the FEEDS' clock, and a tap exists precisely because it beats
 * the feeds — so stamping it with their highest minute dates the tap to whenever
 * they last spoke. On 6 Sep 2026 in Bologna–Sassuolo the freshest minute-bearing
 * signal was a substitution at 86', sent 4m29s before the operator tapped the
 * 90+1' equaliser. The tap reached the bot as minute 86, walked through the
 * `minuteMax: 90` gate on `base-1x2-pari-casa-1`, and bought Bologna to win at
 * 14c — liquidated afterwards at 0.1c.
 *
 * So carry the freshest reading forward by the real time elapsed since it, and
 * report a clock past the 90th the way the wire reports one — `minute: 90` plus
 * `addedTime` — which is what the bot folds back together when it judges a
 * minute window. A reading of "86'" means the clock sat somewhere in
 * [86:00, 87:00), so time is accumulated in seconds and only then cut into
 * minutes: 86' + 4m29s is 90:29, i.e. the first minute of stoppage, not the 90th.
 */
export function tapClock(signals: MatchSignal[], nowMs: number = Date.now()): { minute: number; addedTime: number | null } | null {
  let best: { effSec: number; ms: number } | null = null;
  for (const s of signals) {
    const m = s.minute;
    if (typeof m !== "number" || !Number.isFinite(m) || m < 0) continue;
    const added = typeof s.addedTime === "number" && Number.isFinite(s.addedTime) && s.addedTime > 0 ? s.addedTime : 0;
    const effSec = (m + added) * 60;
    const parsed = Date.parse(s.ts);
    if (best == null || effSec > best.effSec) best = { effSec, ms: Number.isFinite(parsed) ? parsed : nowMs };
  }
  if (best == null) return null;
  const carriedSec = Math.max(0, Math.min(TAP_CARRY_CAP_MIN * 60, Math.floor((nowMs - best.ms) / 1000)));
  const totalSec = best.effSec + carriedSec;
  const excessSec = totalSec - 90 * 60;
  // 90:00 exactly is still the 90th minute — stoppage has not been entered, and
  // claiming "90+1" there would shut the 90th out of its own window. Past it,
  // any part of a minute over counts as a whole added minute.
  if (excessSec <= 0) return { minute: Math.floor(totalSec / 60), addedTime: null };
  return { minute: 90, addedTime: Math.ceil(excessSec / 60) };
}

/** Home/away display names: the most recent signal that carried them (authoritative),
 *  else the watch-list label split on a separator, else generic Casa/Ospite. */
function teamNames(signals: MatchSignal[], group: WatchGroup): { home: string; away: string } {
  for (let i = signals.length - 1; i >= 0; i--) {
    const s = signals[i]!;
    if (s.match === group.id && (s.homeTeam || s.awayTeam)) return { home: s.homeTeam || "Casa", away: s.awayTeam || "Ospite" };
  }
  // labels look like "England – DR Congo (01/07/2026)"; drop the trailing date/note
  // parenthetical so it doesn't cling to the away name after the split
  const parts = (group.label || "").replace(/\s*\([^)]*\)\s*$/, "").split(/\s+(?:[–—]|-|vs?\.?)\s+/i);
  if (parts.length === 2 && parts[0]!.trim() && parts[1]!.trim()) return { home: parts[0]!.trim(), away: parts[1]!.trim() };
  return { home: "Casa", away: "Ospite" };
}

/** An on-field review the operator still owes an outcome for. */
type Review = { team: "home" | "away" | null; kind: "penalty" | "goal" | "unknown"; ts: string };

/** Events that close an open review: the referee came back with a verdict, or
 *  play restarted with a goal on the board. */
const REVIEW_CLOSERS = new Set(["VAR_OVER", "GOAL_CANCELED", "PENALTY", "PENALTY_CANCELED", "PENALTY_GOAL", "PENALTY_MISS", "GOAL", "OWN_GOAL"]);
/** A review still open after this long is stale — no check at the monitor lasts
 *  six minutes, so the lane stops alarming instead of alarming forever. */
const REVIEW_MAX_MS = 6 * 60 * 1000;

const reviewKind = (note: string | null | undefined): Review["kind"] =>
  /pen|rigor/i.test(note ?? "") ? "penalty" : /goal|gol|rete/i.test(note ?? "") ? "goal" : "unknown";

/** The newest VAR on this match's feed with no verdict after it (null = none open). */
function openReview(signals: MatchSignal[], now: number): Review | null {
  for (let i = signals.length - 1; i >= 0; i--) {
    const s = signals[i]!;
    if (REVIEW_CLOSERS.has(s.type)) return null;
    if (s.type === "VAR") {
      if (now - Date.parse(s.ts) > REVIEW_MAX_MS) return null;
      return { team: s.team, kind: reviewKind(s.note), ts: s.ts };
    }
  }
  return null;
}

type Outcome = { type: string; note: string; verb: string; tone: "ok" | "void" };
const PENALTY_OUTCOMES: Outcome[] = [
  { type: "PENALTY", note: "", verb: "Rigore confermato", tone: "ok" },
  { type: "PENALTY_CANCELED", note: "Penalty Overturned", verb: "Rigore annullato", tone: "void" },
];
const GOAL_OUTCOMES: Outcome[] = [
  { type: "VAR_OVER", note: "Goal Stands", verb: "Gol confermato", tone: "ok" },
  { type: "GOAL_CANCELED", note: "Goal Overturned", verb: "Gol annullato", tone: "void" },
];

/** The scoresheet rows, in the order a match produces them. Each tap sends the
 *  DEFINITIVE signal: the old two-step "VAR rigore" then "Rigore confermato" is
 *  collapsed into one RIGORE, because a review opened to award a penalty and the
 *  penalty itself are the same event to the bot — only `PENALTY` arms the penalty
 *  ladders, the bare `VAR` armed nothing. NO GOL is the exception: it opens a
 *  `VAR` review on purpose, so the VAR NOGOL ladders enter BEFORE the verdict
 *  reprices the book, and `opens` raises the amber strip that collects the
 *  verdict ("Gol annullato" / "Gol confermato") right after the tap. */
const TAPS = [
  { key: "goal", verb: "GOL", tone: "goal", signal: { type: "GOAL" } },
  { key: "pen", verb: "RIGORE", tone: "review", signal: { type: "PENALTY" } },
  { key: "nogoal", verb: "NO GOL", tone: "void", signal: { type: "VAR", note: "Goal" }, opens: "goal" },
  { key: "nopen", verb: "NO RIGORE", tone: "void", signal: { type: "PENALTY_CANCELED", note: "Penalty Overturned" } },
] as const;

/** How long an armed "Elimina" stays armed. Long enough to read the question and
 *  aim, short enough that a lane left armed by a mis-click is disarmed again
 *  before the operator looks back at it. Kept in step with the fuse in styles.css. */
const ARM_MS = 6000;

/** How long a tap stays undoable. Three seconds is the whole budget: it has to
 *  cover "wrong button" — which the hand knows before the eye does — while the
 *  rungs are still resting on the book, since a fill is a position and no longer
 *  an undo. Entry TTLs run 13s and up, so the window sits well inside them.
 *  Kept in step with the fuse in styles.css. */
const UNDO_MS = 3000;

/** One tap cell of the scoresheet grid. The column says which team, so the face
 *  carries only the verb — the team goes in the accessible name and the tooltip.
 *
 *  For a few seconds after it fires, the cell turns into its own undo: red,
 *  "ANNULLA", with the fuse of the armed delete draining underneath. The signal
 *  has ALREADY gone out — waiting for a confirmation would cost the seconds this
 *  whole console exists to win — so the second press is a retraction, not a
 *  cancelled send. It stays on the same cell the finger just left, because that
 *  is the only target the hand can find again without looking. */
function Tap({ verb, team, tone, disabled, undoing, undoPending, onClick }: { verb: string; team: string; tone: string; disabled: boolean; undoing?: boolean; undoPending?: boolean; onClick: () => void }) {
  if (undoing)
    return (
      <button
        className={`tap undo ${undoPending ? "working" : ""}`}
        disabled={undoPending}
        onClick={onClick}
        title={`Annulla ${verb} — ${team}: ritira gli ordini dal book`}
        aria-label={`Annulla ${verb}, ${team}`}
      >
        {undoPending ? "Annullo…" : "Annulla"}
      </button>
    );
  return (
    <button className={`tap ${tone}`} disabled={disabled} onClick={onClick} title={`${verb} — ${team}`} aria-label={`${verb}, ${team}`}>
      {verb}
    </button>
  );
}

/** Compact echo of one signal on this match — the operator's own tap coming back
 *  through the consolidator, and the sites confirming it. */
function LaneEvent({ s }: { s: MatchSignal }) {
  const source = signalSourceLabel(s.source, s.feed);
  return (
    <div className="lane-ev">
      <span className="lane-ev-ts num">{hhmmss(s.ts)}</span>
      <span className={chipClass(s.type)}>{s.type}</span>
      <span className="lane-ev-meta num">
        {s.minute != null ? `${s.minute}'${s.addedTime ? `+${s.addedTime}` : ""}` : ""}
        {s.score ? `  ${s.score}` : ""}
      </span>
      <span className="lane-ev-src" title={`${source} (${s.feed})`}>{source}</span>
    </div>
  );
}

/** Everything the Segnali console hands a lane so it can be picked up, put down
 *  and marked. Panoramica's panel passes nothing: its lanes are ordered by what
 *  just happened, which is not the operator's to rearrange. */
type ResyncResult = { score: string; previous: string | null; source: string | null; changed: boolean; lowered: boolean; ordersPulled: boolean };

/**
 * "Risincronizza": put the score back on what Betfair counts.
 *
 * The board the bot plays on can be wrong in ways nothing fixes on its own — a
 * feed invented a goal, a tap landed on the wrong team, a cancellation took off
 * a goal that stood. Every score-relative rule then reads a match that isn't
 * being played, silently, for the rest of the game. This is the way back, and it
 * sits next to the tally it corrects rather than in a menu: the moment an
 * operator notices the two numbers disagree is the moment they need it.
 *
 * One click, no confirmation, like every other control on a lane — the operator
 * is watching a match, not filling in a form. What it cannot undo is a fill: an
 * order that already executed is a position, and the status line says so instead
 * of implying the whole thing rolled back.
 */
function ResyncScore({ id, className = "", onResult }: { id: string; className?: string; onResult?: (status: { ok: boolean; text: string }) => void }) {
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState<{ ok: boolean; text: string } | null>(null);

  // A confirmation is transient; an error stays until the next attempt.
  useEffect(() => {
    if (!local?.ok) return;
    const t = setTimeout(() => setLocal(null), 6000);
    return () => clearTimeout(t);
  }, [local]);

  const say = (status: { ok: boolean; text: string }) => {
    if (onResult) onResult(status);
    else setLocal(status);
  };

  const go = () => {
    if (busy) return;
    setBusy(true);
    setLocal(null);
    void post<ResyncResult>("/api/match-score/resync", { matchId: id })
      .then((r) => {
        const from = r.source ? ` (da ${r.source})` : "";
        const pulled = r.ordersPulled ? " Ordini in attesa ritirati dal book; quello che era già eseguito resta aperto." : "";
        // Three answers, because "already aligned" is only sayable when we KNEW
        // what the bot believed. Without that, claiming it changed nothing would
        // be a guess dressed as a confirmation.
        if (r.previous == null) say({ ok: true, text: `Punteggio impostato su ${r.score}${from}.${pulled}` });
        else if (!r.changed) say({ ok: true, text: `Punteggio già allineato: ${r.score}${from}.` });
        else say({ ok: true, text: `Punteggio risincronizzato: ${r.previous} → ${r.score}${from}.${pulled}` });
      })
      .catch((err) => say({ ok: false, text: err instanceof ApiError ? `${err.status}: ${err.message}` : "errore di rete" }))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <button
        className={`btn ghost sm ${className}`}
        disabled={busy}
        onClick={go}
        title="Rilegge il punteggio da Betfair e lo riporta ovunque — tabellone del bot compreso, così le prossime posizioni partono dal punteggio giusto. Se il punteggio scende, ritira anche gli ordini ancora in attesa."
      >
        {busy ? "Risincronizzo…" : "Risincronizza"}
      </button>
      {local && <span className={`resync-msg ${local.ok ? "ok" : "err"}`}>{local.text}</span>}
    </>
  );
}

type LaneHandle = {
  dragging: boolean;
  /** which side of this lane the dragged card would land on, while it hovers */
  edge: "before" | "after" | null;
  onGrab: (e: React.DragEvent) => void;
  onRelease: () => void;
  onOver: (e: React.DragEvent) => void;
  onOut: () => void;
  onDrop: (e: React.DragEvent) => void;
  /** one place left (-1) or right (+1) — the keyboard's answer to dragging */
  onNudge: (delta: number) => void;
  openMenu: (at: { x: number; y: number }) => void;
};

function MatchLane({ group, signals, score, marketSlug, selected, tint, handle, defaultOutcomesOpen = true, showPolymarketLabel = false }: { group: WatchGroup; signals: MatchSignal[]; score?: MatchStateEvent; marketSlug?: string; selected: boolean; tint?: string; handle?: LaneHandle; defaultOutcomesOpen?: boolean; showPolymarketLabel?: boolean }) {
  const names = teamNames(signals, group);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // open on the Segnali page (the console the operator watches); closed in the
  // Panoramica panel, where the lane is a compact secondary control
  const [outcomesOpen, setOutcomesOpen] = useState(defaultOutcomesOpen);
  // Reviews opened by our own tap show instantly instead of waiting for the
  // consolidator echo; `dismissedAt` hides one by timestamp, so the echo of a
  // review the operator just closed can't make the strip pop back.
  const [local, setLocal] = useState<Review | null>(null);
  const [dismissedAt, setDismissedAt] = useState("");
  // Removing the match lives here so a lane that turns out to be junk (no market,
  // feed that invents goals) can be dropped without leaving the console — hunting
  // for it in Partite while a VAR lands on another match is how events get missed.
  // Two clicks, because it sits one row under the taps the operator hits fast.
  const [armed, setArmed] = useState(false);
  const [removing, setRemoving] = useState(false);
  // The tap that can still be taken back, and until when. One at a time on
  // purpose: the offer is on the LAST thing sent, and a second tap means the
  // operator has moved on — the first one stands.
  const [undo, setUndo] = useState<{ type: string; team: "home" | "away"; verb: string } | null>(null);
  const [undoing, setUndoing] = useState(false);
  // A verdict closes the review strip — but not while its own undo is still on
  // offer, or the button would go down with the strip. The dismissal waits for
  // the window to run out, and a retraction cancels it: the review is open
  // again, because the verdict was taken back.
  const [pendingDismiss, setPendingDismiss] = useState<string | null>(null);
  /** Does this cell carry the open undo offer? Matched on the SIGNAL (type +
   *  team), not on which grid drew the button: the review strip closes the
   *  moment a verdict is sent, and an offer pinned to that strip would vanish
   *  with it. The fallback "Esito gol" grid picks it straight up instead. */
  const undoable = (type: string, team: "home" | "away") => undo?.type === type && undo.team === team;

  const fromFeed = openReview(signals, Date.now());
  const newest = local && (!fromFeed || local.ts > fromFeed.ts) ? local : fromFeed;
  const review = newest && newest.ts > dismissedAt ? newest : null;

  // A sent confirmation is transient; an error stays until the next tap.
  useEffect(() => {
    if (!status?.ok) return;
    const t = setTimeout(() => setStatus(null), 4000);
    return () => clearTimeout(t);
  }, [status]);

  // An armed delete disarms itself after the fuse burns down, and on Esc — the
  // operator's hands leave for the next goal, they don't come back to cancel.
  useEffect(() => {
    if (!armed || removing) return;
    const t = setTimeout(() => setArmed(false), ARM_MS);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setArmed(false);
    addEventListener("keydown", esc);
    return () => {
      clearTimeout(t);
      removeEventListener("keydown", esc);
    };
  }, [armed, removing]);

  // Every button fires immediately — no confirmation (the operator taps fast at the
  // stadium). Goals/cancellations carry NO score on purpose: the bot keeps the board by
  // counting per team (applyGoal/applyCancel), so a hand-typed score would OVERWRITE it
  // — a manual "1-0" once wiped an away goal already on the board. Only the team matters;
  // the last known minute is attached best-effort for the minute-gated rules (e.g. the
  // penalty lottery at ≥75').
  const fire = (payload: { type: string; team: "home" | "away"; note?: string }, describe: string) => {
    if (busy) return;
    const mn = tapClock(signals);
    setBusy(true);
    setStatus(null);
    setArmed(false); // segnalare una partita è il contrario di eliminarla
    // Armed before the POST resolves, not after: the undo has to be reachable
    // from the instant the finger lifts, and the round-trip is dead time the
    // operator would spend staring at the old face of the button.
    setUndo({ type: payload.type, team: payload.team, verb: describe });
    void post("/api/manual-signal", { matchId: group.id, ...payload, ...(mn != null ? { minute: mn.minute, ...(mn.addedTime != null ? { addedTime: mn.addedTime } : {}) } : {}) })
      // A tap the monitor recognised as a repeat of one already sent (double
      // click, or the same event tapped from two phones) is NOT a second event:
      // say it was ignored, or the operator reads the confirmation as proof the
      // bot took two goals.
      // Three outcomes, and two of them publish NOTHING:
      //   deduped — the monitor recognised a repeat of a tap already taken
      //     (double click, or the same event tapped from two phones);
      //   folded  — the tap reached the consolidator, which read it as a slower
      //     sighting of an event already on the wire. Right for the bot (one
      //     event, once) and invisible to whoever is standing in the stadium
      //     believing they just beat the feeds: on 24/08 Bologna–Lazio a tap was
      //     folded into a goal canceled 50 minutes earlier and the operator only
      //     found out from the price. Shown in the error colour on purpose — it
      //     is not a panel failure, but it is the opposite of "inviato".
      .then((res) => {
        const r = res as { deduped?: boolean; folded?: boolean };
        if (r?.deduped) return setStatus({ ok: true, text: `Già inviato, doppio tap ignorato: ${describe}` });
        if (r?.folded) return setStatus({ ok: false, text: `NON pubblicato: ${describe} è stato assorbito da un evento già segnalato dalle fonti` });
        setStatus({ ok: true, text: `Inviato: ${describe}` });
      })
      .catch((err) => {
        setStatus({ ok: false, text: err instanceof ApiError ? `${err.status}: ${err.message}` : "errore di rete" });
        setUndo(null); // nothing left the panel: there is nothing to take back
      })
      .finally(() => setBusy(false));
  };

  // The offer expires on its own. It does NOT expire while the undo is in
  // flight — the button has to stay put under the finger that just pressed it.
  useEffect(() => {
    if (!undo || undoing) return;
    const t = setTimeout(() => {
      setUndo(null);
      if (pendingDismiss) {
        setDismissedAt(pendingDismiss);
        setPendingDismiss(null);
      }
    }, UNDO_MS);
    return () => clearTimeout(t);
  }, [undo, undoing, pendingDismiss]);

  /**
   * Take back the last tap. The signal is already out and cannot be recalled —
   * what this retracts is its consequences: every resting order on the match
   * comes off Polymarket, and a goal also takes the bot's tally back down.
   * A rung that already FILLED is a position, not an order: closing it would be
   * a sale at market, so the undo leaves it and the status line says so instead
   * of claiming a clean rollback.
   */
  const takeBack = () => {
    if (!undo || undoing) return;
    const mn = tapClock(signals);
    const { type, team, verb } = undo;
    setUndoing(true);
    setStatus(null);
    void post("/api/manual-signal/undo", { matchId: group.id, type, team, ...(mn != null ? { minute: mn.minute, ...(mn.addedTime != null ? { addedTime: mn.addedTime } : {}) } : {}) })
      .then((res) => {
        const r = res as { ordersPulled?: boolean; tallyRolledBack?: boolean };
        const done = [r.ordersPulled ? "ordini ritirati dal book" : null, r.tallyRolledBack ? "punteggio riportato indietro" : null].filter(Boolean).join(", ");
        // A cancellation has no safe inverse: putting the goal back would mean
        // sending a GOAL, which is a tradable signal and would open the very
        // positions this is retracting. So the board stays one goal low and the
        // operator is told, rather than left to assume it came back.
        const lowered = type === "GOAL_CANCELED" ? " Il punteggio del bot resta abbassato di un gol." : "";
        setStatus({ ok: true, text: `Annullato ${verb}${done ? ` — ${done}` : ""}. Quello che era già eseguito resta aperto.${lowered}` });
      })
      .catch((err) => setStatus({ ok: false, text: err instanceof ApiError ? `annullamento fallito — ${err.status}: ${err.message}` : "annullamento fallito — errore di rete" }))
      .finally(() => {
        setUndoing(false);
        setUndo(null);
        setPendingDismiss(null); // the verdict is retracted: the review stands open
      });
  };

  const name = (t: "home" | "away") => (t === "home" ? names.home : names.away);

  const tap = (row: (typeof TAPS)[number], team: "home" | "away") => {
    fire({ ...row.signal, team }, `${row.verb} ${name(team)}`);
    // A review we opened ourselves raises its outcome strip instantly, instead of
    // waiting for the consolidator echo to come back.
    if ("opens" in row) setLocal({ team, kind: row.opens, ts: new Date().toISOString() });
  };

  // The lane disappears on the `watchlist` broadcast that follows, so there is
  // nothing to clean up here on success — only the failure needs a voice.
  const remove = () => {
    if (removing) return;
    setRemoving(true);
    setStatus(null);
    void del(`/api/watchlist/${encodeURIComponent(group.id)}`)
      .catch((err) => {
        setStatus({ ok: false, text: err instanceof ApiError ? `${err.status}: ${err.message}` : "errore di rete" });
        setArmed(false);
        setRemoving(false);
      });
  };

  const sendOutcome = (o: Outcome, team: "home" | "away") => {
    fire({ type: o.type, team, ...(o.note ? { note: o.note } : {}) }, `${o.verb} ${name(team)}`);
    setPendingDismiss(new Date().toISOString());
  };

  const reviewOutcomes = review ? (review.kind === "penalty" ? PENALTY_OUTCOMES : review.kind === "goal" ? GOAL_OUTCOMES : [...PENALTY_OUTCOMES, ...GOAL_OUTCOMES]) : [];
  const recent = signals.slice(-8).reverse();
  const minute = lastMinute(signals);
  const pmUrl = polymarketEventUrl(group.polymarket) ?? polymarketEventUrl(marketSlug ?? null);

  // The colour is the operator's own channel and never touches the left rail:
  // amber (review) and red (delete armed) are the console talking, and they have
  // to stay readable on a card the operator painted magenta.
  const hex = tintHex(tint);

  return (
    <section
      className={`lane ${selected ? "sel" : ""} ${review ? "reviewing" : ""} ${armed ? "arming" : ""} ${handle?.dragging ? "dragging" : ""}`}
      aria-label={group.label || group.id}
      style={hex ? ({ "--lane-tint": hex } as React.CSSProperties) : undefined}
      data-tint={hex ? tint : undefined}
      data-drop={handle?.edge ?? undefined}
      onContextMenu={handle ? (e) => { e.preventDefault(); handle.openMenu({ x: e.clientX, y: e.clientY }); } : undefined}
      onDragOver={handle?.onOver}
      onDragLeave={handle?.onOut}
      onDrop={handle?.onDrop}
    >
      <header className="lane-head" draggable={handle ? true : undefined} onDragStart={handle?.onGrab} onDragEnd={handle?.onRelease}>
        {handle && (
          <button
            className="lane-grip"
            aria-label={`Sposta o colora ${group.label || group.id}`}
            title="Trascina per riordinare · frecce ← → per spostare di un posto · clic per il colore"
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              handle.openMenu({ x: r.left, y: r.bottom + 4 });
            }}
            onKeyDown={(e) => {
              if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
              e.preventDefault();
              const grip = e.currentTarget;
              handle.onNudge(e.key === "ArrowLeft" ? -1 : 1);
              // React moves the card's DOM node into its new slot, and moving a
              // node blurs what was focused inside it — without this the second
              // arrow press would land on nothing.
              requestAnimationFrame(() => grip.focus());
            }}
          >
            <Icon name="grip" size={12} />
          </button>
        )}
        <div className="lane-team">
          <span className="lane-team-name" title={names.home}>{names.home}</span>
          <span className="lane-role">casa</span>
        </div>
        <div className="lane-score">
          <span className="lane-tally num" title="Punteggio che il bot pensa sia in corso (tally interno) — confrontalo con quello reale; se divergono, «Risincronizza» qui sotto lo rimette a posto">
            {score ? `${score.home}–${score.away}` : "–"}
          </span>
          <span className="lane-clock">{minute != null ? `${minute}' · ` : ""}tally bot</span>
        </div>
        <div className="lane-team away">
          <span className="lane-team-name" title={names.away}>{names.away}</span>
          <span className="lane-role">ospite</span>
        </div>
      </header>

      {group.status !== "resolved" && (
        <div className="lane-warn">Nessun mercato collegato: i segnali partono, ma il bot non può ancora aprire posizioni.</div>
      )}

      {review && (
        <div className="lane-review">
          <div className="lane-review-head">
            <span className="lane-review-dot" aria-hidden="true" />
            <span>Revisione in corso{review.team ? ` · ${name(review.team)}` : ""}</span>
            <button
              className="lane-review-x"
              onClick={() => setDismissedAt(new Date().toISOString())}
              title="Chiudi la revisione senza inviare un esito"
              aria-label="Chiudi la revisione senza inviare un esito"
            >
              ✕
            </button>
          </div>
          <div className={`lane-grid ${review.team ? "" : "teams"}`}>
            {review.team
              ? reviewOutcomes.map((o) =>
                  undoable(o.type, review.team!) ? (
                    <button key={o.type} className={`tap wide undo ${undoing ? "working" : ""}`} disabled={undoing} onClick={takeBack} title={`Annulla ${o.verb}: ritira gli ordini dal book`}>
                      {undoing ? "Annullo…" : "Annulla"}
                    </button>
                  ) : (
                    <button key={o.type} className={`tap wide ${o.tone}`} disabled={busy} onClick={() => sendOutcome(o, review.team!)}>
                      {o.verb}
                    </button>
                  ),
                )
              : reviewOutcomes.flatMap((o) =>
                  (["home", "away"] as const).map((t) => (
                    <Tap key={`${o.type}-${t}`} verb={o.verb} team={name(t)} tone={o.tone} disabled={busy} undoing={undoable(o.type, t)} undoPending={undoing} onClick={() => (undoable(o.type, t) ? takeBack() : sendOutcome(o, t))} />
                  )),
                )}
          </div>
        </div>
      )}

      {/* Cosa vedo succedere. Colonna = squadra, riga = evento. */}
      <div className="lane-grid teams" role="group" aria-label="Segnala quello che vedi">
        {TAPS.flatMap((row) =>
          (["home", "away"] as const).map((t) => (
            <Tap key={`${row.key}-${t}`} verb={row.verb} team={name(t)} tone={row.tone} disabled={busy} undoing={undoable(row.signal.type, t)} undoPending={undoing} onClick={() => (undoable(row.signal.type, t) ? takeBack() : tap(row, t))} />
          )),
        )}
      </div>

      {/* The foot carries the lane's secondary controls — and, armed, becomes the
          question instead of growing a row: the confirm has to be impossible to
          read past, and a lane two abreast has no width to spare. "Annulla" keeps
          the spot "Elimina" just vacated, so a reflex second click cancels. */}
      <div className={`lane-foot ${armed ? "arming" : ""}`}>
        {armed ? (
          <>
            <span className="lane-kill-ask">Elimini la partita?</span>
            <button
              className="lane-kill confirm"
              disabled={removing}
              onClick={remove}
              aria-label={`Elimina ${names.home} – ${names.away} dalla lista partite`}
            >
              {removing ? "Elimino…" : "Elimina"}
            </button>
            <button className="lane-kill cancel" disabled={removing} autoFocus onClick={() => setArmed(false)}>
              Annulla
            </button>
          </>
        ) : (
          <>
            {!review && (
              <button className="lane-more" aria-expanded={outcomesOpen} onClick={() => setOutcomesOpen(!outcomesOpen)} title="Chiudi una revisione su un gol quando la striscia ambra non è aperta">
                Esito gol {outcomesOpen ? "▴" : "▾"}
              </button>
            )}
            <ResyncScore id={group.id} className="lane-resync" onResult={setStatus} />
            <span className="lane-foot-gap" />
            {pmUrl && (
              <a className={`lane-pm ${showPolymarketLabel ? "labeled" : ""}`} href={pmUrl} target="_blank" rel="noreferrer" title="Apri il mercato su Polymarket" aria-label="Apri il mercato su Polymarket">
                <PolymarketMark size={14} />
                {showPolymarketLabel && <span>Apri su Polymarket</span>}
              </a>
            )}
            <button className="lane-kill" onClick={() => setArmed(true)} title="Elimina la partita dalla lista: il bot smette di seguirla, qui e in Partite">
              Elimina
            </button>
          </>
        )}
      </div>

      {/* Only the goal verdicts: RIGORE / NO RIGORE are already definitive taps on
          the grid above, so repeating them here would be two ways to send the same
          signal. And while the amber strip is up it already offers these two with
          the team decided, so showing them twice would be the same tap in two
          places — the fallback exists for when no review is tracked. */}
      {outcomesOpen && !review && (
        <div className="lane-grid teams lane-outcomes" role="group" aria-label="Esito di una revisione su un gol">
          {GOAL_OUTCOMES.flatMap((o) =>
            (["home", "away"] as const).map((t) => (
              <Tap key={`${o.type}-${t}`} verb={o.verb} team={name(t)} tone={o.tone} disabled={busy} undoing={undoable(o.type, t)} undoPending={undoing} onClick={() => (undoable(o.type, t) ? takeBack() : sendOutcome(o, t))} />
            )),
          )}
        </div>
      )}

      {status && <div className={`lane-status ${status.ok ? "ok" : "err"}`}>{status.text}</div>}

      {/* live echo: the operator's own signal + the sites' confirmations, newest first */}
      <div className="lane-tail">
        {recent.length === 0 ? <div className="empty">Nessun segnale ancora.</div> : recent.map((sig) => <LaneEvent s={sig} key={sig.id} />)}
      </div>
    </section>
  );
}

/** The card's own menu: right-click anywhere on a lane, or the grip in its
 *  nameplate. The colours sit in the menu itself rather than behind a "cambia
 *  colore" step — the whole point of a mark is that it costs one click, and a
 *  swatch says what it does better than a word for it would. */
function LaneMenu({ label, tint, at, onTint, onFirst, onClose }: { label: string; tint?: string; at: { x: number; y: number }; onTint: (id: string | null) => void; onFirst: () => void; onClose: () => void }) {
  // any move of the ground under the menu closes it — an operator console
  // reflows constantly, and a menu left floating over the wrong card is worse
  // than no menu
  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("pointerdown", onClose);
    addEventListener("keydown", key);
    addEventListener("resize", onClose);
    addEventListener("scroll", onClose, true);
    return () => {
      removeEventListener("pointerdown", onClose);
      removeEventListener("keydown", key);
      removeEventListener("resize", onClose);
      removeEventListener("scroll", onClose, true);
    };
  }, [onClose]);

  return (
    <div
      className="lane-menu"
      style={{ left: at.x, top: at.y }}
      role="menu"
      aria-label={`Scheda ${label}`}
      onPointerDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="lane-menu-head">
        <span className="lane-menu-kicker">Scheda</span>
        <span className="lane-menu-title" title={label}>{label}</span>
      </div>

      <div className="lane-menu-sec">
        <span className="lane-menu-label">Colore di sfondo</span>
        <div className="lane-menu-swatches">
          {LANE_TINTS.map((t) => (
            <button
              key={t.id}
              className={`lane-swatch ${tint === t.id ? "sel" : ""}`}
              style={{ "--lane-tint": t.hex } as React.CSSProperties}
              title={t.label}
              aria-label={t.label}
              aria-pressed={tint === t.id}
              onClick={() => onTint(t.id)}
            />
          ))}
          <button
            className={`lane-swatch none ${tint ? "" : "sel"}`}
            title="Nessun colore"
            aria-label="Nessun colore"
            aria-pressed={!tint}
            onClick={() => onTint(null)}
          />
        </div>
      </div>

      <button className="lane-menu-item" role="menuitem" onClick={onFirst}>
        Porta all’inizio
      </button>
    </div>
  );
}

function ManualSignalView({ groups, signals, matchScores, resolutions }: { groups: WatchGroup[]; signals: MatchSignal[]; matchScores: Record<string, MatchStateEvent>; resolutions: MarketResolution[] }) {
  const enabled = groups.filter((g) => g.enabled);
  const [selected, setSelected] = useState<string>("");
  const marks = useLaneMarks();
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<{ id: string; after: boolean } | null>(null);
  const [menu, setMenu] = useState<{ id: string; x: number; y: number } | null>(null);

  const byMatch = useMemo(() => {
    const map: Record<string, MatchSignal[]> = {};
    for (const s of signals) (map[s.match] ??= []).push(s);
    return map;
  }, [signals]);
  const slugs = useMemo(() => marketSlugByMatch(resolutions), [resolutions]);

  // Every group the panel knows, not just the enabled ones: a match switched off
  // for the evening keeps its colour AND its place, one deleted from the
  // watch-list loses both. Seeded by `createdAt` — the order the operator added
  // them in — because the server's own list order is `updated_at DESC` and every
  // automatic market pre-check rewrites that for a batch of groups at a time.
  const knownIds = useMemo(
    () => [...groups].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id)).map((g) => g.id),
    [groups],
  );
  useEffect(() => { syncLaneMarks(knownIds); }, [knownIds]);

  const lanes = useMemo(() => sortLanes(enabled, marks.order), [enabled, marks.order]);
  const marked = marks.touched || Object.keys(marks.tints).length > 0;

  if (enabled.length === 0)
    return (
      <div className="manual-view">
        <div className="empty">Nessuna partita attiva. Aggiungine una dalla sezione Partite per poter inviare segnali.</div>
      </div>
    );

  const current = enabled.find((g) => g.id === selected)?.id ?? enabled[0]!.id;
  const now = Date.now();
  const reviewing = new Set(enabled.filter((g) => openReview(byMatch[g.id] ?? [], now)).map((g) => g.id));
  // the switcher is a narrow strip — the label's trailing "(date)" earns no room there
  const chipLabel = (g: WatchGroup) => (g.label || g.id).replace(/\s*\([^)]*\)\s*$/, "");

  return (
    <div className="manual-view">
      <div className="manual-bar">
        <span className="manual-count num">{enabled.length}</span>
        <span className="manual-count-label">{enabled.length === 1 ? "partita seguita" : "partite seguite"}</span>
        {reviewing.size > 0 && <span className="manual-reviewing">{reviewing.size} in revisione</span>}
        <span className="spacer" />
        <span className="manual-hint">Ogni tasto invia subito, senza conferma.</span>
        <span className="manual-hint manual-hint-marks">Tasto destro per il colore · trascina l’intestazione per riordinare.</span>
        {marked && (
          <button className="manual-reset" onClick={clearLaneMarks} title="Rimuove i colori e rimette le partite nell’ordine di arrivo">
            Azzera colori e ordine
          </button>
        )}
      </div>

      {/* phone only: one lane at a time, this picks it — a match under review keeps
          its dot here, since the operator can't see the lane itself alarming */}
      <div className="manual-switch">
        {lanes.map((g) => (
          <button
            key={g.id}
            className={`manual-chip ${current === g.id ? "sel" : ""} ${reviewing.has(g.id) ? "rev" : ""}`}
            aria-pressed={current === g.id}
            onClick={() => setSelected(g.id)}
          >
            {reviewing.has(g.id) && <span className="manual-chip-dot" aria-label="revisione in corso" />}
            {chipLabel(g)}
          </button>
        ))}
      </div>

      {/* The drop lands on whichever half of a card the pointer is over, so a
          card can be put at the very end of the grid and not only before
          something. `dragId` is read at drop time, not carried in the
          dataTransfer: the payload would let a drag from another window rewrite
          this list. */}
      <div className="lanes">
        {lanes.map((g) => (
          <MatchLane
            key={g.id}
            group={g}
            signals={byMatch[g.id] ?? []}
            score={matchScores[g.id]}
            marketSlug={slugs[g.id]}
            selected={current === g.id}
            tint={marks.tints[g.id]}
            handle={{
              dragging: dragId === g.id,
              edge: over?.id === g.id && dragId && dragId !== g.id ? (over.after ? "after" : "before") : null,
              onGrab: (e) => {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", g.id); // Firefox starts no drag without a payload
                setDragId(g.id);
              },
              onRelease: () => { setDragId(null); setOver(null); },
              onOver: (e) => {
                if (!dragId) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                const r = e.currentTarget.getBoundingClientRect();
                const after = e.clientX > r.left + r.width / 2;
                setOver((o) => (o?.id === g.id && o.after === after ? o : { id: g.id, after }));
              },
              onOut: () => setOver((o) => (o?.id === g.id ? null : o)),
              onDrop: (e) => {
                e.preventDefault();
                if (dragId && dragId !== g.id) {
                  const r = e.currentTarget.getBoundingClientRect();
                  setLaneOrder(moveLane(marks.order, dragId, g.id, e.clientX > r.left + r.width / 2));
                }
                setDragId(null);
                setOver(null);
              },
              onNudge: (delta) => setLaneOrder(nudgeLane(marks.order, lanes.map((l) => l.id), g.id, delta)),
              openMenu: (at) => setMenu({ id: g.id, ...at }),
            }}
          />
        ))}
      </div>

      {menu && (
        <LaneMenu
          label={chipLabel(enabled.find((g) => g.id === menu.id) ?? enabled[0]!)}
          tint={marks.tints[menu.id]}
          at={{ x: Math.max(8, Math.min(menu.x, innerWidth - 226)), y: Math.max(8, Math.min(menu.y, innerHeight - 200)) }}
          onTint={(id) => { setLaneTint(menu.id, id); setMenu(null); }}
          onFirst={() => { setLaneOrder(moveLane(marks.order, menu.id, lanes[0]!.id, false)); setMenu(null); }}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}

/** How many lanes the Panoramica panel keeps. Enough to cover the matches
 *  actually in play at once without turning the overview into the Segnali page —
 *  four so the two-column grid fills as 2×2 instead of leaving a hole. */
const QUICK_LANES = 4;

/**
 * Panoramica's manual-send panel: the same lanes as the Segnali page, but only
 * the matches something just happened on, freshest first — so when a goal lands
 * the lane to tap is already at the top instead of being hunted for in a list of
 * fifty. A match that stops producing events falls off the bottom (it stays
 * reachable on the Segnali page, which always lists them all).
 */
function QuickSignalPanel({ groups, signals, matchScores, resolutions }: { groups: WatchGroup[]; signals: MatchSignal[]; matchScores: Record<string, MatchStateEvent>; resolutions: MarketResolution[] }) {
  const slugs = useMemo(() => marketSlugByMatch(resolutions), [resolutions]);
  const byMatch = useMemo(() => {
    const map: Record<string, MatchSignal[]> = {};
    for (const s of signals) (map[s.match] ??= []).push(s);
    return map;
  }, [signals]);

  // rank by the most recent RELEVANT event (a goal/VAR/penalty — the same set the
  // bell alarms on), by `ts` rather than arrival order so a source that reports
  // late can't push its match above one that scored after it. The WS hello carries
  // the signal backlog, so this is already ranked on a fresh page load.
  const recent = useMemo(() => {
    const latest: Record<string, number> = {};
    for (const s of signals) {
      if (!ALERT_TYPES.has(s.type)) continue;
      const at = Date.parse(s.ts);
      if (!Number.isFinite(at)) continue;
      if (at > (latest[s.match] ?? -Infinity)) latest[s.match] = at;
    }
    return groups
      .filter((g) => g.enabled && latest[g.id] != null)
      .sort((a, b) => latest[b.id]! - latest[a.id]!)
      .slice(0, QUICK_LANES);
  }, [signals, groups]);
  const marks = useLaneMarks();

  return (
    <div className="card">
      <div className="card-head">
        Invio rapido <span className="count">({recent.length}) · ultimi eventi</span>
      </div>
      <div className="card-body">
        {recent.length === 0 ? (
          <div className="empty">Nessun evento recente. Le partite compaiono qui appena succede qualcosa — tutte le altre restano in Segnali.</div>
        ) : (
          <div className="lanes overview-lanes">
            {recent.map((g) => (
              <MatchLane key={g.id} group={g} signals={byMatch[g.id] ?? []} score={matchScores[g.id]} marketSlug={slugs[g.id]} selected tint={marks.tints[g.id]} defaultOutcomesOpen={false} showPolymarketLabel />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── watch-list (operator-declared match groups) ──────────────────────────────
function WatchlistCard({
  groups,
  matchScores,
  marketSearch,
  resolutions,
  categoryNeeds,
  run,
  ask,
  botRunning,
}: {
  groups: WatchGroup[];
  matchScores: Record<string, MatchStateEvent>;
  marketSearch: Record<string, MarketSearchState>;
  resolutions: MarketResolution[];
  categoryNeeds: CategoryNeeds;
  run: (fn: () => Promise<unknown>) => Promise<void>;
  ask: (message: string, fn: () => Promise<unknown>) => void;
  botRunning: boolean;
}) {
  // I tipi di mercato che il bot ha davvero trovato, per partita.
  const kindsByMatch = useMemo(
    () => Object.fromEntries(resolutions.map((r) => [r.match, r.markets ?? []])) as Record<string, string[]>,
    [resolutions],
  );
  const mancantiDi = (g: WatchGroup) => missingCategories(kindsByMatch[g.id] ?? [], g.disabledCategories ?? [], categoryNeeds);
  const [label, setLabel] = useState("");
  const [links, setLinks] = useState("");
  const [cleanupMsg, setCleanupMsg] = useState("");
  const [cleaning, setCleaning] = useState(false);
  // split by what the operator can act on: a row still being searched is not a
  // problem to fix, it's a wait. Only the settled ones raise the card's alert.
  const live = groups.filter((g) => g.enabled).map((g) => rowMarket(g.status, marketSearch[g.id]?.phase, botRunning, mancantiDi(g)));
  const searching = live.filter((m) => m.tone === "searching").length;
  const needs = live.filter((m) => m.tone === "warn");

  const add = () => {
    const urls = links.split(/\s+/).map((l) => l.trim()).filter((l) => /^https?:\/\//.test(l));
    if (urls.length === 0) return;
    void run(() => post("/api/watchlist", { label: label.trim(), links: urls })).then(() => {
      setLabel("");
      setLinks("");
    });
  };

  const active = groups.filter((g) => g.enabled).length;
  // active matches first; stable sort keeps the existing order within each bucket
  const ordered = [...groups].sort((a, b) => Number(b.enabled) - Number(a.enabled));

  const cleanupFinished = () =>
    ask(
      "Eliminare le partite finite? Prima di cancellare, ognuna viene ricontrollata sulla fonte live (365scores/flashscore) — chi risulta ancora in corso, o non verificabile, resta in lista.",
      () => {
        setCleaning(true);
        setCleanupMsg("");
        return del<{ removed: string[]; kept: string[] }>("/api/watchlist/finished")
          .then((res) => {
            setCleanupMsg(
              res.removed.length === 0
                ? res.kept.length === 0
                  ? "Nessuna partita candidata alla pulizia."
                  : `Nessuna rimossa — ${res.kept.length} ancora in corso o non verificabili.`
                : `Rimosse ${res.removed.length} partite finite.` + (res.kept.length ? ` ${res.kept.length} lasciate (in corso o non verificabili).` : ""),
            );
          })
          .finally(() => setCleaning(false));
      },
    );

  return (
    <div className={`card wl-card ${needs.length ? "alert" : ""}`}>
      <div className="card-head">
        Partite
        <span className="count">{active}/{groups.length} attive</span>
        {searching > 0 && <span className="count search-count">{searching} in ricerca</span>}
        {needs.length > 0 && <span className="count alert-count">⚠ {needs.length} senza mercato</span>}
        <button
          className="btn ghost sm wl-cleanup"
          disabled={groups.length === 0 || cleaning}
          title="Ricontrolla sulla fonte live e rimuove solo le partite confermate finite (silenzio da 3+ ore)"
          onClick={cleanupFinished}
        >
          {cleaning ? "Verifica in corso…" : "Elimina partite finite"}
        </button>
      </div>
      {cleanupMsg && <div className="wl-cleanup-msg muted">{cleanupMsg}</div>}
      <div className="card-body">
        {/* add form: paste the per-site links of ONE match, one per line */}
        <div className="wl-add">
          <div className="wl-add-title">Aggiungi una partita</div>
          <input className="wl-label" placeholder="Etichetta (es. Safi – Meknes) — opzionale" value={label} onChange={(e) => setLabel(e.target.value)} spellCheck={false} />
          <textarea
            className="wl-links"
            placeholder={"Incolla i link dei siti della STESSA partita, uno per riga\nhttps://www.365scores.com/…\nhttps://www.flashscore.com/match/…"}
            value={links}
            onChange={(e) => setLinks(e.target.value)}
            rows={3}
            spellCheck={false}
          />
          <button className="btn primary" disabled={!links.trim()} onClick={add} title="Aggiunge la partita; verifica subito il mercato Polymarket">
            + Aggiungi partita
          </button>
        </div>

        {groups.length === 0 && <div className="empty">Nessuna partita. Incolla i link sopra per aggiungerne una.</div>}
        {ordered.map((g) => {
          const mancanti = mancantiDi(g);
          const m = rowMarket(g.status, marketSearch[g.id]?.phase, botRunning, mancanti);
          return (
          <div className={`wl-row ${!g.enabled ? "off" : m.tone}`} key={g.id}>
            <input
              type="checkbox"
              className="wl-toggle"
              checked={g.enabled}
              title={g.enabled ? "Partita attiva — clicca per spegnerla (non verrà più seguita)" : "Partita spenta — clicca per riattivarla"}
              onChange={() => void run(() => post(`/api/watchlist/${encodeURIComponent(g.id)}/enabled`, { enabled: !g.enabled }))}
            />
            <div className="grow">
              <div className="wl-title">
                <b>{g.label || g.id}</b>
                <span
                  className={`wl-badge ${!g.enabled ? "off" : m.tone}`}
                  title={g.enabled && m.tone === "partial" ? tooltipMancanti(mancanti, categoryNeeds, kindsByMatch[g.id] ?? []) : undefined}
                >
                  {!g.enabled ? "spenta" : m.badge}
                </span>
                {g.enabled && (
                  <span className="wl-badge" title="Punteggio che il bot pensa sia in corso (tally interno) — confrontalo con quello reale">
                    Bot: {matchScores[g.id] ? `${matchScores[g.id]!.home}-${matchScores[g.id]!.away}` : "—"}
                  </span>
                )}
              </div>
              {g.enabled && g.status === "resolved" && <div className="wl-market muted">{g.title ?? g.polymarket}</div>}
              {g.enabled && m.hint && <div className={`wl-hint ${m.tone}`}>{m.hint}</div>}
              <div className="wl-links-list">
                <PolymarketLink value={g.polymarket} />
                {g.links.map((l) => (
                  <a className="wl-link" href={l} target="_blank" rel="noreferrer" key={l}>
                    {hostOf(l)}
                  </a>
                ))}
              </div>
              <div className="wl-actions">
                <WatchlistPolymarket id={g.id} value={g.polymarket} run={run} botRunning={botRunning} />
                <WatchlistVideo id={g.id} url={g.videoUrl} run={run} />
                <WatchlistLinks id={g.id} links={g.links} run={run} />
                {g.enabled && <ResyncScore id={g.id} />}
              </div>
              {g.enabled && <WatchlistCategories id={g.id} disabled={g.disabledCategories} run={run} />}
            </div>
            <button className="btn ghost sm wl-remove" title="Rimuovi dalla watch-list" onClick={() => ask(`Rimuovere "${g.label || g.id}" dalla watch-list?`, () => del(`/api/watchlist/${encodeURIComponent(g.id)}`))}>
              Rimuovi
            </button>
          </div>
          );
        })}
      </div>
    </div>
  );
}

// ── import in blocco (risultato.json di match-link-resolver) ─────────────────

type RowAction = "create" | "enrich" | "unchanged" | "skip-enabled" | "skip-duplicate" | "skip-collision";
type ImportReviewNote = {
  source: string; score: number; sourceName: string; league: string | null;
  url: string; wide: boolean; drift: number;
};
type ImportPlanRow = {
  label: string; links: string[]; id: string; action: RowAction;
  addedLinks: string[]; existingLabel: string | null;
  review: ImportReviewNote[];
};
type ImportPlan = {
  rows: ImportPlanRow[];
  rejected: Array<{ index: number; reason: string }>;
  counts: Record<RowAction, number>;
  needingReview: number;
};
type ImportResponse = { applied: boolean; plan: ImportPlan; result?: { created: string[]; enriched: string[]; skipped: number } };

const ACTION_LABELS: Record<RowAction, string> = {
  create: "nuova",
  enrich: "aggiunge fonti",
  unchanged: "già presente",
  "skip-enabled": "accesa — non tocco",
  "skip-duplicate": "doppione nel file",
  "skip-collision": "collisione di id",
};

/** Import in blocco: si incolla il `risultato.json` prodotto da
 *  match-link-resolver, si guarda l'anteprima, si conferma. L'anteprima non
 *  scrive niente — è lì perché gli abbinamenti deboli segnalati dal resolver
 *  vanno guardati a occhio PRIMA che diventino righe nel database. */
function ImportCard({ run }: { run: (fn: () => Promise<unknown>) => Promise<void> }) {
  const [text, setText] = useState("");
  const [disabled, setDisabled] = useState<BetCategory[]>([]);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);
  // link dubbi che NON verranno importati. Si parte escludendoli tutti: tenere
  // un abbinamento che il resolver ha segnalato dev'essere una scelta esplicita,
  // non quello che succede a chi preme Conferma senza leggere.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [onlyAttention, setOnlyAttention] = useState(false);

  const parsed = (): unknown | null => {
    try { return JSON.parse(text) as unknown; } catch { return null; }
  };

  const send = (apply: boolean) => {
    const payload = parsed();
    if (payload === null) { setDone("Il testo incollato non è un JSON valido."); return; }
    setBusy(true);
    setDone("");
    // l'anteprima chiede sempre il quadro completo (nessuna esclusione), così le
    // spunte restano visibili e reversibili; le esclusioni contano alla conferma
    const body = apply
      ? { payload, disabled, apply: true, exclude: [...excluded] }
      : { payload, disabled, apply: false };
    void run(() => post<ImportResponse>("/api/watchlist/import", body)
      .then((res) => {
        setPlan(res.plan);
        if (!res.applied) {
          setExcluded(new Set(res.plan.rows.flatMap((r) => r.review.map((n) => n.url)).filter((u) => u !== "")));
        }
        if (res.applied && res.result) {
          setDone(`Inserite ${res.result.created.length} partite (spente), ${res.result.enriched.length} arricchite di fonti, ${res.result.skipped} lasciate stare.`);
          setText("");
        }
      })).finally(() => setBusy(false));
  };

  const toggleLink = (url: string) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url); else next.add(url);
      return next;
    });

  /** quanti link entrerebbero davvero per questa riga, spunte comprese */
  const effectiveLinks = (r: ImportPlanRow): number =>
    r.links.length - r.review.filter((n) => n.url !== "" && excluded.has(n.url)).length;

  /** tutti gli url segnalati del piano, nell'ordine in cui compaiono */
  const reviewUrls = plan === null
    ? []
    : plan.rows.flatMap((r) => r.review.map((n) => n.url)).filter((u) => u !== "");
  const allKept = reviewUrls.length > 0 && reviewUrls.every((u) => !excluded.has(u));
  const toggleAll = () => setExcluded(allKept ? new Set(reviewUrls) : new Set());

  // "da controllare" = ha un abbinamento segnalato, oppure è una collisione di id,
  // che pure vuole un occhio umano. Con 150 righe è l'unico modo di trovarle.
  const needsAttention = (r: ImportPlanRow) => r.review.length > 0 || r.action === "skip-collision";
  const attentionCount = plan === null ? 0 : plan.rows.filter(needsAttention).length;
  const visibleRows = plan === null ? [] : onlyAttention ? plan.rows.filter(needsAttention) : plan.rows;

  const readFile = (file: File | undefined) => {
    if (file === undefined) return;
    void file.text().then((t) => { setText(t); setPlan(null); setDone(""); });
  };

  const toggle = (cat: BetCategory) =>
    setDisabled((d) => (d.includes(cat) ? d.filter((c) => c !== cat) : [...d, cat]));

  const willChange = plan === null ? 0 : plan.counts.create + plan.counts.enrich;

  return (
    <div className="card">
      <div className="card-head">Importa partite <span className="count">risultato.json</span></div>
      <div className="card-body wl-import">
        <p className="config-hint">
          Incolla il <code>risultato.json</code> prodotto da <code>match-link-resolver</code>.
          Le partite entrano <strong>spente</strong>: le accendi tu quando ti servono.
        </p>
        <input type="file" accept=".json,application/json" onChange={(e) => readFile(e.target.files?.[0])} />
        <textarea
          className="editor"
          rows={5}
          placeholder='{ "matches": [ … ] }'
          value={text}
          onChange={(e) => { setText(e.target.value); setPlan(null); setDone(""); }}
        />

        <div className="wl-categories-grid">
          {BET_CATEGORIES.map((cat) => (
            <label key={cat} className="wl-category-toggle">
              <input type="checkbox" checked={!disabled.includes(cat)} onChange={() => toggle(cat)} />
              {CATEGORY_LABELS[cat]}
            </label>
          ))}
        </div>

        <div className="config-actions">
          <button className="btn" disabled={busy || text.trim() === ""} onClick={() => send(false)}>Anteprima</button>
          <button className="btn primary" disabled={busy || plan === null || willChange === 0} onClick={() => send(true)}>
            {plan === null ? "Conferma" : `Conferma (${willChange})`}
          </button>
        </div>

        {done !== "" && <div className="config-note">{done}</div>}

        {plan !== null && (
          <>
            <div className="config-note">
              {(Object.keys(ACTION_LABELS) as RowAction[])
                .filter((a) => plan.counts[a] > 0)
                .map((a) => `${plan.counts[a]} ${ACTION_LABELS[a]}`)
                .join(" · ") || "niente da fare"}
            </div>

            {plan.rejected.length > 0 && (
              <div className="config-note locked">
                Righe scartate ({plan.rejected.length}): {plan.rejected.map((r) => r.reason).join(" · ")}
              </div>
            )}

            {plan.needingReview > 0 && (
              <div className="config-note locked">
                {plan.needingReview} partite hanno abbinamenti da controllare. Sono <strong>esclusi</strong> di
                default: confronta il nome che hai scritto con quello della fonte e spunta <em>tieni</em> solo
                quelli che sono davvero la stessa partita.
              </div>
            )}

            {attentionCount > 0 && (
              <div className="wl-import-tools">
                {reviewUrls.length > 0 && (
                  <>
                    <button className="btn ghost sm" onClick={toggleAll}>
                      {allKept ? "Escludi tutti i segnalati" : "Tieni tutti i segnalati"}
                    </button>
                    <span className="wl-hint">
                      {reviewUrls.length - reviewUrls.filter((u) => excluded.has(u)).length} di {reviewUrls.length} da tenere
                    </span>
                  </>
                )}
                <label className="wl-import-keep">
                  <input type="checkbox" checked={onlyAttention} onChange={() => setOnlyAttention((v) => !v)} />
                  <span>mostra solo quelle da controllare ({attentionCount})</span>
                </label>
              </div>
            )}

            <table className="tbl">
              <thead>
                <tr><th>Partita</th><th>Fonti</th><th>Esito</th></tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => {
                  const left = effectiveLinks(r);
                  const empty = left === 0 && r.action === "create";
                  return (
                    <tr key={r.id + r.label} className={r.review.length > 0 ? "wl-import-warn" : ""}>
                      <td>
                        {r.label}
                        {r.review.map((n) => (
                          <label key={n.url || n.source} className="wl-import-keep">
                            <input
                              type="checkbox"
                              checked={n.url !== "" && !excluded.has(n.url)}
                              disabled={n.url === ""}
                              onChange={() => toggleLink(n.url)}
                            />
                            <span>
                              tieni <strong>{n.source}</strong> → «{n.sourceName}»
                              {n.league === null ? "" : ` [${n.league}]`}
                              {" — "}
                              {n.wide
                                ? `orario diverso da quello che hai scritto: ${(n.drift / 3600).toFixed(1)}h`
                                : `somiglianza dei nomi bassa (${n.score.toFixed(2)})`}
                            </span>
                          </label>
                        ))}
                        {r.action === "skip-collision" && (
                          <div className="wl-hint">stesso id di «{r.existingLabel}» — controlla a mano</div>
                        )}
                        {empty && (
                          <div className="wl-hint">senza quei link non resta niente: questa partita non verrà aggiunta</div>
                        )}
                      </td>
                      <td>{r.action === "enrich" ? `+${r.addedLinks.length}` : left}</td>
                      <td>{ACTION_LABELS[r.action]}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {onlyAttention && (
              // il filtro nasconde righe ma la conferma agisce su TUTTE: senza
              // dirlo, una tabella filtrata sembra l'elenco completo
              <div className="wl-hint">
                Mostrate {visibleRows.length} righe di {plan.rows.length}. La conferma vale per tutte,
                non solo per quelle visibili.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PolymarketLink({ value }: { value: string | null }) {
  const href = polymarketEventUrl(value);
  if (!href) return null;
  return (
    <a className="wl-link polymarket" href={href} target="_blank" rel="noreferrer" title="Apri questa partita su Polymarket">
      polymarket.com ↗
    </a>
  );
}

/**
 * Cosa dice il badge giallo quando ci si passa sopra.
 *
 * Tre righe, in quest'ordine: quali scommesse restano ferme, con che cosa
 * manca; cosa invece c'e'; e la scelta che l'operatore ha davanti. Non e' un
 * errore da correggere — Polymarket per certe partite quota solo l'esito — ma
 * una spunta accesa promette una scaletta che non puo' partire, e chi guarda
 * deve poterlo sapere senza aprire i log.
 */
function tooltipMancanti(mancanti: BetCategory[], needs: CategoryNeeds, trovati: readonly string[]): string {
  const famiglie = (cat: BetCategory) => (needs[cat] ?? []).map((f) => FAMILY_LABELS[f]).join(" e ");
  const presenti = [...new Set(trovati.map(familyOfKind).filter((f): f is MarketFamily => f != null))].map((f) => FAMILY_LABELS[f]);
  return [
    "Polymarket non quota tutto per questa partita.",
    "",
    "Senza mercato:",
    ...mancanti.map((c) => `  ${CATEGORY_LABELS[c]} — serve ${famiglie(c)}`),
    "",
    `Trovati: ${presenti.join(", ") || "nessuno"}`,
    "",
    "Queste scommesse non partiranno. Spegnile qui sotto, oppure lasciale",
    "accese se ti aspetti che Polymarket aggiunga i mercati.",
  ].join("\n");
}

const CATEGORY_LABELS: Record<BetCategory, string> = {
  cancellation: "Annullamenti",
  penalty: "Rigori",
  "over-under": "Over/Under & Gol",
  btts: "BTTS",
  winner: "1X2/Vantaggio",
  "first-to-score": "Primo a segnare",
  "exact-score": "Punteggio esatto",
  "fair-value": "Valore (quote book)",
};

/** Per-match "Tipi di scommessa" checkboxes: unchecking one stops the bot from
 *  opening NEW positions of that category on this match (already-open positions
 *  keep being managed by their exit rules — see the bot's RulesStrategy).
 *
 *  It governs the BOT's own signals only: a tap from the Segnali console carries
 *  `source: "manual"` and trades regardless — the checkbox means "don't do this
 *  by yourself", not "don't do this". That is deliberate (the taps are a speed
 *  play and would otherwise no-op on nearly every match), but it surprised the
 *  operator on 22 Aug 2026 — Inter-Monza, "over-under" off, a GOL tap opened the
 *  Over ladder anyway — so the note below says it where the checkboxes are. */
function WatchlistCategories({ id, disabled, run }: { id: string; disabled: BetCategory[]; run: (fn: () => Promise<unknown>) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const activeCount = BET_CATEGORIES.length - disabled.length;
  const toggle = (cat: BetCategory) => {
    const next = disabled.includes(cat) ? disabled.filter((c) => c !== cat) : [...disabled, cat];
    void run(() => post(`/api/watchlist/${encodeURIComponent(id)}/categories`, { disabled: next }));
  };
  return (
    <div className="wl-categories">
      <button className="btn ghost sm wl-categories-toggle" onClick={() => setOpen((o) => !o)}>
        {open ? "▾" : "▸"} Tipi di scommessa{activeCount < BET_CATEGORIES.length ? ` (${activeCount}/${BET_CATEGORIES.length} attivi)` : ""}
      </button>
      {open && (
        <>
          <div className="wl-categories-grid">
            {BET_CATEGORIES.map((cat) => (
              <label key={cat} className="wl-category-toggle">
                <input type="checkbox" checked={!disabled.includes(cat)} onChange={() => toggle(cat)} />
                {CATEGORY_LABELS[cat]}
              </label>
            ))}
          </div>
          <p className="config-hint wl-categories-note">
            Valgono per i gol che il bot vede <strong>da solo</strong>. Un tasto premuto a mano
            in <strong>Segnali</strong> opera lo stesso, anche su un tipo spento.
          </p>
        </>
      )}
    </div>
  );
}

/** Per-match live-video URL: the page the panel pops open on a goal/VAR/penalty
 *  (see web/auto-open.ts). Empty = this match never opens anything. Panel-only —
 *  neither the monitor nor the bot reads it. */
function WatchlistVideo({ id, url, run }: { id: string; url: string | null; run: (fn: () => Promise<unknown>) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(url ?? "");
  // re-sync when the row's value changes under us (another tab, a WS broadcast)
  useEffect(() => setValue(url ?? ""), [url]);
  const save = (next: string) => void run(() => post(`/api/watchlist/${encodeURIComponent(id)}/video`, { url: next })).then(() => setOpen(false));
  return (
    <div className="wl-edit">
      <button className={`btn ghost sm ${url ? "wl-edit-set" : ""}`} onClick={() => setOpen((o) => !o)} title={url ? `Video live: ${url}` : "Nessun video live — il pannello non aprirà nulla per questa partita"}>
        {open ? "▾" : "▸"} Video live{url ? " ✓" : ""}
      </button>
      {open && (
        <div className="wl-edit-body">
          <input className="wl-label" placeholder="https://… la pagina del video live da aprire su goal/VAR" value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save(value.trim())} spellCheck={false} />
          <div className="wl-edit-row">
            <button className="btn primary sm" disabled={value.trim() === (url ?? "")} onClick={() => save(value.trim())}>Salva</button>
            {url && <button className="btn ghost sm" onClick={() => { setValue(""); save(""); }}>Rimuovi</button>}
            <span className="muted wl-edit-hint">Si apre solo da desktop, con l'interruttore video acceso in alto.</span>
          </div>
        </div>
      )}
    </div>
  );
}

/** Edit the per-site links the monitor follows for this match. The group id is
 *  kept, so the Polymarket binding, the on/off flag and the category overrides all
 *  survive; the monitor re-reads watchlist.json within ~10s (watch.ts RELOAD_MS). */
function WatchlistLinks({ id, links, run }: { id: string; links: string[]; run: (fn: () => Promise<unknown>) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(links.join("\n"));
  useEffect(() => setText(links.join("\n")), [links]);
  const parsed = text.split(/\s+/).map((l) => l.trim()).filter((l) => /^https?:\/\//.test(l));
  const changed = parsed.join("\n") !== links.join("\n");
  const save = () => {
    if (parsed.length === 0) return;
    void run(() => post(`/api/watchlist/${encodeURIComponent(id)}/links`, { links: parsed })).then(() => setOpen(false));
  };
  return (
    <div className="wl-edit">
      <button className="btn ghost sm" onClick={() => setOpen((o) => !o)} title="Aggiungi o togli le fonti che il monitor segue per questa partita">
        {open ? "▾" : "▸"} Modifica link ({links.length})
      </button>
      {open && (
        <div className="wl-edit-body">
          <textarea className="wl-links" value={text} onChange={(e) => setText(e.target.value)} rows={Math.max(3, links.length + 1)} spellCheck={false} placeholder={"Un link per riga\nhttps://www.365scores.com/…"} />
          <div className="wl-edit-row">
            <button className="btn primary sm" disabled={parsed.length === 0 || !changed} onClick={save}>Salva</button>
            <button className="btn ghost sm" onClick={() => { setText(links.join("\n")); setOpen(false); }}>Annulla</button>
            <span className="muted wl-edit-hint">La partita resta la stessa: mercato Polymarket, stato e categorie non cambiano.</span>
          </div>
        </div>
      )}
    </div>
  );
}

/** Change the Polymarket binding of any watch-list row, including one that is
 * already resolved. The bot validates the new event before the bridge replaces
 * the persisted binding, so a typo does not overwrite the last known-good URL. */
function WatchlistPolymarket({
  id,
  value,
  run,
  botRunning,
}: {
  id: string;
  value: string | null;
  run: (fn: () => Promise<unknown>) => Promise<void>;
  botRunning: boolean;
}) {
  const [open, setOpen] = useState(false);
  const current = polymarketEventUrl(value) ?? value ?? "";
  const [slug, setSlug] = useState(current);
  useEffect(() => setSlug(current), [current]);

  const submit = () => {
    // HTTP success only means "queued on the bot's stdin"; the actual
    // resolved/resolve-failed event arrives later over WS. Keep the editor open
    // and the pasted URL visible until that answer updates the row.
    const next = slug.trim();
    if (botRunning && next && next !== current) {
      void run(() => post(`/api/watchlist/${encodeURIComponent(id)}/resolve`, { slug: next }));
    }
  };
  return (
    <div className="wl-edit">
      <button
        className={`btn ghost sm ${value ? "wl-edit-set" : ""}`}
        onClick={() => setOpen((o) => !o)}
        title={value ? "Sostituisci il mercato Polymarket collegato a questa partita" : "Collega un mercato Polymarket a questa partita"}
      >
        {open ? "▾" : "▸"} Polymarket{value ? " ✓" : ""}
      </button>
      {open && (
        <div className="wl-edit-body">
          <input
            className="wl-label"
            placeholder="https://polymarket.com/event/… o slug dell'evento"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            spellCheck={false}
          />
          <div className="wl-edit-row">
            <button
              className="btn primary sm"
              disabled={!slug.trim() || slug.trim() === current || !botRunning}
              onClick={submit}
              title={botRunning ? "" : "Il bot deve essere in esecuzione per verificare il mercato"}
            >
              {value ? "Cambia mercato" : "Collega mercato"}
            </button>
            <button className="btn ghost sm" onClick={() => { setSlug(current); setOpen(false); }}>Annulla</button>
            <span className="muted wl-edit-hint">
              {botRunning ? "Il nuovo link sostituisce quello attuale solo dopo la verifica del bot." : "Avvia il bot per verificare e salvare il link."}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── secondary copy-trade wallets ─────────────────────────────────────────────
const SIG_TYPES: Array<{ v: number; label: string }> = [
  { v: 1, label: "1 · Email/Magic (account Polymarket via email)" },
  { v: 0, label: "0 · EOA (chiave = wallet dei fondi)" },
  { v: 2, label: "2 · Gnosis Safe (serve il funder)" },
  { v: 3, label: "3 · EIP-1271 (serve il funder)" },
];
const shortAddr = (a: string) => (a.length > 12 ? `${a.slice(0, 8)}…${a.slice(-4)}` : a);

/** Per-wallet copy counters over the last 24h (mirrors /api/wallets/stats). */
type WalletStats = Record<string, { label: string; orders: number; skips: number; errors: number; lastError: { ts: string; op: string; message: string } | null }>;

function WalletsCard({
  wallets,
  run,
  ask,
  botRunning,
  dirty,
  setDirty,
}: {
  wallets: WalletView[];
  run: (fn: () => Promise<unknown>) => Promise<void>;
  ask: (message: string, fn: () => Promise<unknown>) => void;
  botRunning: boolean;
  /** "restart required" reminder, lifted to the Dashboard so it survives closing the popup. */
  dirty: boolean;
  setDirty: (b: boolean) => void;
}) {
  const [label, setLabel] = useState("");
  const [key, setKey] = useState("");
  const [sigType, setSigType] = useState(1);
  const [funder, setFunder] = useState("");
  const [scale, setScale] = useState("1");

  // copy counters (last 24h) refresh on their own; the balance check is manual —
  // it derives creds and hits the CLOB per wallet, too slow/loud for a poll
  const [stats, setStats] = useState<WalletStats>({});
  useEffect(() => {
    const load = () => void api<WalletStats>("/api/wallets/stats").then(setStats).catch(() => {});
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);
  const [status, setStatus] = useState<WalletStatus[] | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkErr, setCheckErr] = useState("");
  const check = () => {
    setChecking(true);
    setCheckErr("");
    void api<WalletStatus[]>("/api/wallets/status")
      .then(setStatus)
      .catch((e) => setCheckErr(e instanceof ApiError ? e.message : "network error"))
      .finally(() => setChecking(false));
  };

  const needsFunder = sigType === 2 || sigType === 3;
  const canAdd = label.trim() && /^(0x)?[0-9a-fA-F]{64}$/.test(key.trim()) && Number(scale) > 0 && (!needsFunder || /^0x[0-9a-fA-F]{40}$/.test(funder.trim()));

  const mutate = (fn: () => Promise<unknown>) => void run(fn).then(() => setDirty(true));

  const add = () =>
    void run(() =>
      post("/api/wallets", {
        label: label.trim(),
        privateKey: key.trim(),
        signatureType: sigType,
        scale: Number(scale),
        ...(needsFunder ? { funder: funder.trim() } : {}),
      }),
    ).then(() => {
      // the key leaves the browser exactly once — clear it no matter what
      setKey("");
      setLabel("");
      setFunder("");
      setScale("1");
      setDirty(true);
    });

  const enabled = wallets.filter((w) => w.enabled).length;

  return (
    <div className="card">
      <div className="card-head">
        Wallet copy-trade <span className="count">{enabled}/{wallets.length} attivi</span>
      </div>
      <div className="card-body">
        {dirty && (
          <div className="wallet-restart">
            ⚠ Le modifiche ai wallet si applicano al prossimo riavvio del bot.
            <button
              className="btn primary sm"
              disabled={!botRunning}
              onClick={() =>
                ask("Riavviare il bot per applicare le modifiche ai wallet? Gli ordini appoggiati restano sull'exchange, ma il tracking delle copie riparte da zero.", async () => {
                  await post("/api/proc/bot/restart");
                  setDirty(false);
                })
              }
            >
              Riavvia il bot
            </button>
          </div>
        )}

        <div className="wl-add">
            <div className="wl-add-title">Aggiungi un wallet secondario</div>
            <input className="wl-label" placeholder="Etichetta (es. wallet di Marco)" value={label} onChange={(e) => setLabel(e.target.value)} spellCheck={false} />
            <input
              className="wl-label"
              type="password"
              placeholder="Chiave privata (64 hex) — inviata una volta sola, mai rimostrata"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
            <div className="wallet-form-row">
              <select className="wl-label" value={sigType} onChange={(e) => setSigType(Number(e.target.value))} title="Tipo di firma dell'account Polymarket">
                {SIG_TYPES.map((t) => (
                  <option key={t.v} value={t.v}>{t.label}</option>
                ))}
              </select>
              <input className="wl-label wallet-scale" type="number" min="0.05" max="10" step="0.05" value={scale} onChange={(e) => setScale(e.target.value)} title="Moltiplicatore di size rispetto al wallet primario" />
              <span className="muted">× size</span>
            </div>
            {needsFunder && <input className="wl-label" placeholder="Funder 0x… (indirizzo che detiene gli USDC)" value={funder} onChange={(e) => setFunder(e.target.value)} spellCheck={false} />}
            <button className="btn primary" disabled={!canAdd} onClick={add} title="La chiave viene scritta solo in wallets.json sul server (mai nel database del pannello)">
              + Aggiungi wallet
            </button>
        </div>

        {wallets.length === 0 && <div className="empty">Nessun wallet secondario. Gli ordini del bot vengono replicati sui wallet aggiunti qui.</div>}
        {wallets.length > 0 && (
          <div className="wallet-check">
            <button className="btn sm" disabled={checking} onClick={check} title="Interroga il CLOB per saldo pUSD e allowance di ogni wallet (qualche secondo a wallet)">
              {checking ? "Verifica in corso…" : "Verifica saldi"}
            </button>
            {checkErr && <span className="wallet-err">⚠ {checkErr}</span>}
          </div>
        )}
        {wallets.map((w) => (
          <div className={`wl-row ${w.enabled ? "ok" : "off"}`} key={w.id}>
            <input
              type="checkbox"
              className="wl-toggle"
              checked={w.enabled}
              title={w.enabled ? "Attivo — clicca per sospendere la copia su questo wallet" : "Sospeso — clicca per riattivarlo"}
              onChange={() => mutate(() => post(`/api/wallets/${encodeURIComponent(w.id)}/enabled`, { enabled: !w.enabled }))}
            />
            <div className="grow">
              <div className="wl-title">
                <b>{w.label}</b>
                <span className={`wl-badge ${w.enabled ? "ok" : "off"}`}>{w.enabled ? `copia ×${w.scale}` : "sospeso"}</span>
              </div>
              <div className="muted wallet-addr" title={`funder ${w.funder} · eoa ${w.eoa}`}>
                {shortAddr(w.funder)}
                <WalletScale id={w.id} scale={w.scale} onSave={(v) => mutate(() => post(`/api/wallets/${encodeURIComponent(w.id)}/scale`, { scale: v }))} />
                <WalletBalance st={status?.find((x) => x.id === w.id)} />
              </div>
              <WalletCounters s={stats[w.id]} />
            </div>
            <button className="btn ghost sm wl-remove" title="Rimuovi il wallet (la sua chiave viene cancellata da wallets.json)" onClick={() => ask(`Rimuovere il wallet "${w.label}"? Le posizioni già aperte su quell'account restano e vanno chiuse a mano.`, async () => {
              await del(`/api/wallets/${encodeURIComponent(w.id)}`);
              setDirty(true);
            })}>
              Rimuovi
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Balance/allowance chip after a manual "Verifica saldi" run (null = not checked yet). */
function WalletBalance({ st }: { st: WalletStatus | undefined }) {
  if (!st) return null;
  if (!st.ok) return <span className="wallet-bal bad" title={st.error}>● errore CLOB</span>;
  const funded = (st.usdc ?? 0) > 0;
  const ready = funded && st.allowanceOk;
  return (
    <span className={`wallet-bal ${ready ? "ok" : "bad"}`} title={st.allowanceOk ? "saldo e allowance ok" : "manca l'allowance verso l'exchange (deposita/abilita da polymarket.com)"}>
      ● ${(st.usdc ?? 0).toFixed(2)}
      {!funded ? " · vuoto" : st.allowanceOk ? "" : " · no allowance"}
    </span>
  );
}

/** Copy counters over the last 24h; silent when the wallet never copied. */
function WalletCounters({ s }: { s: WalletStats[string] | undefined }) {
  if (!s || (s.orders === 0 && s.skips === 0 && s.errors === 0)) return <div className="muted wallet-stats">24h: nessuna copia</div>;
  return (
    <div className="muted wallet-stats">
      24h: {s.orders} copie · {s.skips} skip · <span className={s.errors ? "wallet-err" : ""}>{s.errors} errori</span>
      {s.lastError && (
        <div className="wallet-err wallet-last-err" title={s.lastError.ts}>
          ultimo errore ({s.lastError.op}): {s.lastError.message}
        </div>
      )}
    </div>
  );
}

function WalletScale({ id, scale, onSave }: { id: string; scale: number; onSave: (v: number) => void }) {
  const [value, setValue] = useState(String(scale));
  useEffect(() => setValue(String(scale)), [scale, id]);
  const commit = () => {
    const v = Number(value);
    if (Number.isFinite(v) && v > 0 && v <= 10 && v !== scale) onSave(v);
    else setValue(String(scale));
  };
  return (
    <span className="wallet-scale-edit">
      ×
      <input type="number" min="0.05" max="10" step="0.05" value={value} onChange={(e) => setValue(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} title="Moltiplicatore di size — Invio per salvare" />
    </span>
  );
}

const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

// ── manual market resolution ─────────────────────────────────────────────────
const teamsLabel = (r: MarketResolution) => (r.homeTeam && r.awayTeam ? `${r.homeTeam} – ${r.awayTeam}` : r.match);

function MarketResolutions({
  resolutions,
  errors,
  run,
  botRunning,
  precheck,
}: {
  resolutions: MarketResolution[];
  errors: Record<string, string>;
  run: (fn: () => Promise<unknown>) => Promise<void>;
  botRunning: boolean;
  precheck: { ts: string; rows: PrecheckRow[] } | null;
}) {
  const pending = resolutions.filter((r) => r.status === "unresolved");
  const resolved = resolutions.filter((r) => r.status === "resolved");

  const [busy, setBusy] = useState(false);
  const lastTs = precheck?.ts;
  useEffect(() => setBusy(false), [lastTs]); // a fresh result clears the spinner
  const runPrecheck = () => {
    setBusy(true);
    void run(() => post("/api/bot/precheck")).catch(() => setBusy(false));
  };

  return (
    <div className={`card ${pending.length ? "alert" : ""}`}>
      <div className="card-head">
        Mercati non risolti
        {pending.length > 0 && <span className="count alert-count">({pending.length} da sistemare)</span>}
        <span className="spacer" />
        <button className="btn ghost sm" disabled={busy || !botRunning} onClick={runPrecheck} title="Verifica ora i mercati Polymarket di tutte le partite in lista, prima del kickoff">
          {busy ? "Controllo…" : "Pre-check mercati"}
        </button>
      </div>
      <div className="card-body">
        {precheck && <PrecheckResults precheck={precheck} run={run} botRunning={botRunning} resolvedIds={new Set(resolved.map((r) => r.match))} />}
        {resolutions.length === 0 && <div className="empty">Nessun mercato in sospeso — il bot li trova da solo.</div>}
        {pending.map((r) => (
          <ResolveRow key={r.match} r={r} error={errors[r.match]} run={run} botRunning={botRunning} />
        ))}
        {resolved.map((r) => (
          <div className="row resolved-row" key={r.match}>
            <span className="grow">
              {/* A resolution without the WINNER market keeps every 1X2 rule
                  silently skipped (norway-england 11 Jul: anchored to the
                  "- Halftime Result" sibling) — flag it until it's re-resolved. */}
              {r.markets.includes("WINNER") ? "✓ " : <b className="pc-no-winner">⚠️ 1X2 ASSENTE </b>}
              {teamsLabel(r)} <span className="muted">→ {r.markets.join(" · ") || "—"}</span>
              {r.slug && <span className="muted"> · override: {r.slug}</span>}
            </span>
            {/* Panel-side dismissal: drops the row (and any saved override). Shown
                for every resolved row so a no-override leftover can be cleared too. */}
            <button className="btn ghost sm" onClick={() => void run(() => post("/api/bot/clear-override", { match: r.match }))}>
              {r.slug ? "Rimuovi override" : "Rimuovi"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PrecheckResults({
  precheck,
  run,
  botRunning,
  resolvedIds,
}: {
  precheck: { ts: string; rows: PrecheckRow[] };
  run: (fn: () => Promise<unknown>) => Promise<void>;
  botRunning: boolean;
  resolvedIds: Set<string>;
}) {
  // The raw team-name search never consults a manual override (see bot's
  // precheck()), so a group already resolved via override always comes back
  // `ok: false` here even though it trades fine — that row would just be a
  // false alarm re-appearing on every click. It's already shown, correctly,
  // as a ✓ resolved row above; drop it from this raw list instead.
  const rows = precheck.rows.filter((r) => r.ok || !r.id || !resolvedIds.has(r.id));
  const ready = rows.filter((r) => r.ok && r.winner).length;
  return (
    <div className="precheck">
      <div className="precheck-head">
        Pre-check {hhmmss(precheck.ts)} — <b>{ready}/{rows.length}</b> pronte su Polymarket
      </div>
      {rows.map((r) => (
        <div className={`precheck-row ${r.ok && r.winner ? "good" : r.ok ? "warn" : "bad"}`} key={r.label + r.key}>
          <div className="precheck-line">
            <span className="pc-icon">{r.ok && r.winner ? "✅" : r.ok ? "⚠️" : "❌"}</span>
            <span className="grow">
              <b>{r.label}</b>
              {r.ok ? (
                <span className="muted">
                  {" "}
                  · {r.title ?? "?"} ·{" "}
                  {r.winner ? "1X2 ✓" : <b className="pc-no-winner">1X2 ASSENTE — le regole WINNER resteranno spente</b>} · OU {r.ouLines} · BTTS{" "}
                  {r.btts ? "✓" : "✗"}
                </span>
              ) : (
                <span className="muted">{r.title?.startsWith("error:") ? ` · ${r.title}` : " · nessun mercato Polymarket trovato"}</span>
              )}
            </span>
          </div>
          {/* No auto-match, or a match whose 1X2 is missing (anchored to a
              sibling event) → let the operator paste the Polymarket event link
              now, before kickoff (the live alarm's ResolveRow only appears once
              a signal arrives). A group-originated row resolves under its group
              id (the live match key); a legacy row under the pre-check key. */}
          {(!r.ok || !r.winner) && <PrecheckResolve matchKey={r.id ?? r.key} run={run} botRunning={botRunning} />}
        </div>
      ))}
    </div>
  );
}

function PrecheckResolve({ matchKey, run, botRunning }: { matchKey: string; run: (fn: () => Promise<unknown>) => Promise<void>; botRunning: boolean }) {
  const [slug, setSlug] = useState("");
  const submit = () => {
    // Resolution is asynchronous; keep the input until the result removes this
    // row or shows an error, rather than clearing it on the enqueue response.
    if (slug.trim()) void run(() => post("/api/bot/resolve-market", { match: matchKey, slug: slug.trim() }));
  };
  return (
    <div className="resolve-input precheck-resolve">
      <input
        placeholder="incolla l'URL Polymarket o lo slug dell'evento"
        value={slug}
        onChange={(e) => setSlug(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        spellCheck={false}
      />
      <button className="btn primary sm" disabled={!slug.trim() || !botRunning} onClick={submit} title={botRunning ? "" : "il bot non è in esecuzione"}>
        Risolvi
      </button>
    </div>
  );
}

function ResolveRow({ r, error, run, botRunning }: { r: MarketResolution; error?: string; run: (fn: () => Promise<unknown>) => Promise<void>; botRunning: boolean }) {
  const [slug, setSlug] = useState("");
  const submit = () => {
    if (slug.trim()) void run(() => post("/api/bot/resolve-market", { match: r.match, slug: slug.trim() }));
  };
  return (
    <div className="resolve-item">
      <div className="resolve-teams">
        ⚠ <b>{teamsLabel(r)}</b> <span className="muted">{r.match}</span>
      </div>
      <div className="resolve-input">
        <input
          placeholder="incolla l'URL Polymarket o lo slug dell'evento"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          spellCheck={false}
        />
        <button className="btn primary sm" disabled={!slug.trim() || !botRunning} onClick={submit} title={botRunning ? "" : "il bot non è in esecuzione"}>
          Risolvi
        </button>
        {/* Dismiss an unresolved alarm that will never resolve (no Polymarket
            market, or the match is over). Drops the row via the same path as the
            resolved-row "Rimuovi"; works even when the bot is stopped. */}
        <button
          className="btn ghost sm"
          onClick={() => void run(() => post("/api/bot/clear-override", { match: r.match }))}
          title="Rimuovi dalla lista: nessun mercato Polymarket, o partita finita"
        >
          Scarta
        </button>
      </div>
      {error && <div className="resolve-error">✗ {error}</div>}
    </div>
  );
}

// ── open orders (resting on the book) — cancel a specific one ─────────────────
function OpenOrders({ orders, botRunning, run }: { orders: OpenOrder[]; botRunning: boolean; run: (fn: () => Promise<unknown>) => Promise<void> }) {
  if (orders.length === 0) return null;
  return (
    <div className="open-orders">
      <div className="open-orders-head">
        Ordini aperti <span className="count">({orders.length})</span>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>Token</th>
            <th>Match</th>
            <th>Side</th>
            <th>Prezzo</th>
            <th>Size</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.orderId}>
              <td className="num" data-label="Token">{o.tokenId.slice(0, 10)}…</td>
              <td data-label="Match">{o.match ?? "—"}</td>
              <td className={o.side === "BUY" ? "pnl-pos" : "pnl-neg"} data-label="Side">{o.side}</td>
              <td className="num" data-label="Prezzo">{o.price.toFixed(3)}</td>
              <td className="num" data-label="Size">{o.size.toFixed(1)}</td>
              <td data-label="">
                <button className="btn ghost sm" disabled={!botRunning} title="Cancella questo ordine in attesa" onClick={() => void run(() => post(`/api/bot/cancel/${o.orderId}`))}>
                  Cancella
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PnlChart({ mode }: { mode: "PAPER" | "LIVE" }) {
  const [rows, setRows] = useState<Array<{ ts: string; realized: number }>>([]);
  // the endpoint filters by the active mode server-side, so refetch on a switch
  useEffect(() => {
    const load = () => void api<Array<{ ts: string; realized: number }>>("/api/history/pnl?limit=720").then(setRows).catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [mode]);
  return (
    <div className="pnl-chart">
      <span className="stat">PnL realizzato · {mode.toLowerCase()} · ultimi {rows.length}m</span>
      <Sparkline points={rows.map((r) => r.realized)} />
    </div>
  );
}

// ── decisions (condensed try/open/close/rejected feed, from bot_events) ───────
/** One-line description of the bet the row represents. */
function decisionBet(d: Decision): string {
  if (d.category === "close" && d.avgEntry != null && d.avgExit != null) {
    return `${d.avgEntry.toFixed(2)}→${d.avgExit.toFixed(2)}${d.size != null ? ` · ${d.size.toFixed(0)}sh` : ""}`;
  }
  const bits: string[] = [];
  if (d.side) bits.push(d.side);
  if (d.price != null) bits.push(`@${d.price.toFixed(2)}`);
  if (d.size != null) bits.push(`${d.size.toFixed(0)}sh`);
  return bits.join(" ") || "—";
}

function Decisions({ activity }: { activity: number }) {
  const [rows, setRows] = useState<Decision[]>([]);
  const load = () => void api<Decision[]>("/api/history/decisions?limit=200").then(setRows).catch(() => {});
  // refresh on mount, every 30s, and whenever the bot logs new activity
  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);
  useEffect(load, [activity]);

  return (
    <div className="card">
      <div className="card-head">
        Decisioni del bot <span className="count">({rows.length} · prova/apre/chiude/rifiutata)</span>
      </div>
      <div className="card-body">
        {rows.length === 0 ? (
          <div className="empty">Nessuna decisione ancora — qui compaiono i tentativi, le aperture e le chiusure.</div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Orario</th>
                <th>Tipo</th>
                <th>Partita</th>
                <th>Scommessa</th>
                <th>Motivo</th>
                <th>PnL</th>
              </tr>
            </thead>
            <tbody>
              {[...rows].reverse().map((d, i) => (
                <tr key={i} title={d.tokenId ? `${d.tokenId.slice(0, 12)}…` : undefined}>
                  <td className="ts" data-label="Orario">{hhmmss(d.ts)}</td>
                  <td data-label="Tipo">
                    <span className={decisionChip(d.category)}>{DECISION_LABEL[d.category]}</span>
                    {d.rungs > 1 && <span className="muted"> ×{d.rungs}</span>}
                  </td>
                  <td data-label="Partita">{d.match ?? (d.tokenId ? `${d.tokenId.slice(0, 10)}…` : "—")}</td>
                  <td className="num" data-label="Scommessa">{decisionBet(d)}</td>
                  <td className="grow" data-label="Motivo" title={`${d.strategy ?? ""} ${d.reason ?? ""}`.trim()}>
                    {d.strategy ?? "—"}
                    {d.reason && d.reason !== d.strategy ? <span className="muted"> · {d.reason}</span> : ""}
                  </td>
                  <td className={`num ${d.pnl == null ? "" : d.pnl >= 0 ? "pnl-pos" : "pnl-neg"}`} data-label="PnL">
                    {d.pnl == null ? "" : `${d.pnl >= 0 ? "+" : ""}${d.pnl.toFixed(2)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── goal / cancellation statistics ───────────────────────────────────────────
const STAT_RANGES: Array<{ key: string; label: string }> = [
  { key: "30", label: "30 giorni" },
  { key: "90", label: "90 giorni" },
  { key: "365", label: "1 anno" },
  { key: "all", label: "Tutto" },
];

/** Below this many goals a percentage is noise, so it is shown dimmed. */
const PCT_MIN_SAMPLE = 20;

const pctText = (p: number | null) => (p == null ? "—" : `${p.toFixed(1)}%`);
const teamLabel = (t: "home" | "away" | null) => (t === "home" ? "Casa" : t === "away" ? "Ospiti" : "—");
const ddmm = (iso: string) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });

/** Statistiche gol/annullamenti. Fetches only while the tab is on screen — every
 *  section stays mounted, so an always-on poll here would run in the background
 *  for the whole session. */
function GoalStatsView({ active }: { active: boolean }) {
  const [days, setDays] = useState("90");
  const [stats, setStats] = useState<GoalStats | null>(null);
  const [league, setLeague] = useState<string | null>(null);
  const [detail, setDetail] = useState<CanceledGoalRow[]>([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!active) return;
    let dead = false;
    const load = () =>
      void api<GoalStats>(`/api/stats/goals?days=${days}`)
        .then((r) => {
          if (dead) return;
          setStats(r);
          setErr("");
        })
        .catch((e) => {
          if (!dead) setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : "errore di rete");
        });
    load();
    const t = setInterval(load, 60_000);
    return () => {
      dead = true;
      clearInterval(t);
    };
  }, [active, days]);

  useEffect(() => {
    if (!active) return;
    let dead = false;
    const qs = league == null ? "" : `&league=${encodeURIComponent(league)}`;
    void api<CanceledGoalRow[]>(`/api/stats/goals/canceled?days=${days}&limit=200${qs}`)
      .then((r) => !dead && setDetail(r))
      .catch(() => !dead && setDetail([]));
    return () => {
      dead = true;
    };
  }, [active, days, league]);

  const total = stats?.total;
  // Bars are scaled against the busiest bucket, not against 100% — the interesting
  // shape is where cancellations pile up, and absolute rates are all single digits.
  const maxCanceled = Math.max(1, ...(stats?.byMinute ?? []).map((b) => b.canceled));

  return (
    <div className="page-grid two">
      <div className="card span-all">
        <div className="card-head">
          Riepilogo
          <span className="spacer" />
          <div className="gs-range">
            {STAT_RANGES.map((r) => (
              <button key={r.key} className={`btn sm ${days === r.key ? "primary" : "ghost"}`} onClick={() => setDays(r.key)}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <div className="card-body">
          {err && <div className="empty">Impossibile caricare le statistiche — {err}</div>}
          <div className="metric-grid gs-metrics">
            <div className="metric">
              <span className="metric-label">Gol segnalati</span>
              <strong className="num">{total?.goals ?? "—"}</strong>
              <span className="metric-meta">{total?.matches ?? 0} partite</span>
            </div>
            <div className="metric">
              <span className="metric-label">Gol annullati</span>
              <strong className="num">{total?.canceled ?? "—"}</strong>
              <span className="metric-meta">VAR e decisioni arbitrali</span>
            </div>
            <div className="metric">
              <span className="metric-label">% annullati</span>
              <strong className="num">{pctText(total?.pct ?? null)}</strong>
              <span className="metric-meta">annullati su gol segnalati</span>
            </div>
            <div className="metric">
              <span className="metric-label">Sospetti</span>
              <strong className="num">{total?.canceledGhost ?? "—"}</strong>
              <span className="metric-meta">gol inventati da una sola fonte</span>
            </div>
          </div>
          <p className="gs-note">
            La percentuale è <b>annullati / gol segnalati</b> nel periodo: i segnali di annullamento non portano un
            riferimento al gol che cancellano, quindi non è un abbinamento uno-a-uno. Gli <b>annullamenti sospetti</b>
            (gol visto da una fonte sola e poi sparito: errore del feed, non decisione arbitrale) sono contati a parte e
            mai sommati agli altri. Nota: <b>betfair-sca non segnala mai un annullamento</b>, quindi le partite viste
            solo da quella fonte contribuiscono gol ma nessuna cancellazione.
            {stats != null && stats.unresolvedLeagues > 0 && (
              <> {stats.unresolvedLeagues} partite non hanno ancora una lega risolta e stanno sotto «Sconosciuta».</>
            )}
          </p>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          Per lega <span className="count">({stats?.byLeague.length ?? 0})</span>
        </div>
        <div className="card-body">
          {stats == null || stats.byLeague.length === 0 ? (
            <div className="empty">Nessun gol registrato nel periodo scelto.</div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Lega</th>
                  <th>Partite</th>
                  <th>Gol</th>
                  <th>Annullati</th>
                  <th>%</th>
                  <th>Sospetti</th>
                </tr>
              </thead>
              <tbody>
                {stats.byLeague.map((l) => (
                  <tr
                    key={l.league}
                    className={`gs-clickable ${league === l.league ? "gs-selected" : ""}`}
                    onClick={() => setLeague(league === l.league ? null : l.league)}
                    title="Mostra gli annullamenti di questa lega"
                  >
                    <td className="grow" data-label="Lega">{l.league}</td>
                    <td className="num" data-label="Partite">{l.matches}</td>
                    <td className="num" data-label="Gol">{l.goals}</td>
                    <td className="num" data-label="Annullati">{l.canceled}</td>
                    <td
                      className={`num ${l.goals < PCT_MIN_SAMPLE ? "muted" : ""}`}
                      data-label="%"
                      title={l.goals < PCT_MIN_SAMPLE ? `solo ${l.goals} gol: campione troppo piccolo` : undefined}
                    >
                      {pctText(l.pct)}
                    </td>
                    <td className="num muted" data-label="Sospetti">{l.canceledGhost || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">Per fascia di minuto</div>
        <div className="card-body">
          {stats == null || stats.total.goals + stats.total.canceled === 0 ? (
            <div className="empty">Nessun dato nel periodo scelto.</div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Minuto</th>
                  <th>Gol</th>
                  <th>Annullati</th>
                  <th>%</th>
                  <th>Distribuzione</th>
                </tr>
              </thead>
              <tbody>
                {stats.byMinute.map((b) => (
                  <tr key={b.bucket}>
                    <td data-label="Minuto">{b.bucket}</td>
                    <td className="num" data-label="Gol">{b.goals}</td>
                    <td className="num" data-label="Annullati">{b.canceled}</td>
                    <td className={`num ${b.goals < PCT_MIN_SAMPLE ? "muted" : ""}`} data-label="%">{pctText(b.pct)}</td>
                    <td className="grow" data-label="Distribuzione">
                      <span className="gs-bar" title={`${b.canceled} annullamenti`}>
                        {b.canceled > 0 && <span style={{ width: `${(b.canceled / maxCanceled) * 100}%` }} />}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card span-all">
        <div className="card-head">
          Annullamenti <span className="count">({detail.length}{league ? ` · ${league}` : " · tutte le leghe"})</span>
          {league && (
            <>
              <span className="spacer" />
              <button className="btn sm ghost" onClick={() => setLeague(null)}>
                Tutte le leghe
              </button>
            </>
          )}
        </div>
        <div className="card-body">
          {detail.length === 0 ? (
            <div className="empty">Nessun gol annullato nel periodo{league ? " per questa lega" : ""}.</div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Partita</th>
                  <th>Lega</th>
                  <th>Min</th>
                  <th>Squadra</th>
                  <th>Punteggio</th>
                  <th>Motivo</th>
                  <th>Fonte</th>
                </tr>
              </thead>
              <tbody>
                {detail.map((r) => (
                  <tr key={r.id} className={r.corroborated === false ? "gs-ghost" : ""}>
                    <td className="ts" data-label="Data" title={r.ts}>{ddmm(r.ts)} {hhmmss(r.ts).slice(0, 5)}</td>
                    <td className="grow" data-label="Partita">{r.label}</td>
                    <td data-label="Lega">{r.league}</td>
                    <td className="num" data-label="Min">{r.minute ?? "—"}</td>
                    <td data-label="Squadra">{teamLabel(r.team)}</td>
                    <td className="num" data-label="Punteggio">{r.score ?? "—"}</td>
                    <td className="grow" data-label="Motivo">
                      {r.corroborated === false ? (
                        <span className="chip neutral" title="Il gol l'aveva visto una sola fonte: probabile errore del feed, non una decisione arbitrale">
                          sospetto
                        </span>
                      ) : null}{" "}
                      {r.note ?? "—"}
                    </td>
                    <td data-label="Fonte">{signalSourceLabel(r.source, r.feed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Posizioni ────────────────────────────────────────────────────────────────
/**
 * L'esposizione aperta, raggruppata per EVENTO invece che per token.
 *
 * La Panoramica elenca le posizioni una riga per token, nell'ordine in cui le
 * manda il bot: va bene per sapere quanto c'è a mercato, non per uscirne. Chi
 * apre questa pagina ha una domanda sola — "su questa partita sto rischiando
 * tanto, tirami fuori" — e la partita è l'unità in cui la pensa, perché è
 * l'unità in cui si muove il campo: un VAR non annulla un token, annulla un gol.
 *
 * PERCHÉ CHIUDERE UN EVENTO NON È "VENDI" RIPETUTO
 * `Bot.sellPosition` cancella gli ordini appoggiati SUL TOKEN che vende, non
 * quelli sugli altri mercati della stessa partita: venduto l'Over 2.5, un
 * gradino ancora appoggiato sull'Over 3.5 può riempirsi un secondo dopo e
 * riaprire l'esposizione appena chiusa. Il pulsante d'evento passa quindi da
 * `/api/positions/close-event`, che tira via PRIMA tutti gli ordini della
 * partita e vende solo dopo — in quell'ordine, sulla stessa corsia stdin.
 *
 * L'ORDINE DELLE TESSERE NON CAMBIA MAI
 * Stanno nell'ordine in cui la partita è entrata in watch-list, lo stesso delle
 * corsie in Segnali, e non si riordinano per esposizione: la pagina si aggiorna
 * una volta al secondo e sotto ogni tessera c'è un pulsante che vende a mercato.
 * Una tessera che sale di posto mentre il dito scende è esattamente come si
 * chiude la partita sbagliata.
 */

/** Un colore per tipo di scommessa, per leggere la barra senza leggere le righe.
 *  Niente verde né rosso: in questo pannello sono il segno del PnL, e un mercato
 *  colorato di verde si legge come una posizione in guadagno. */
const CATEGORY_COLOR: Record<BetCategory, string> = {
  winner: "#4f7df9",
  "over-under": "#38bdf8",
  btts: "#a78bfa",
  penalty: "#f472b6",
  cancellation: "#f5b74e",
  "first-to-score": "#fb923c",
  "exact-score": "#e879f9",
  "fair-value": "#7f8ea3",
};
/** Token di cui non sappiamo dire la scommessa (nessun fill leggibile). */
const UNKNOWN_COLOR = "#48566e";

const usd = (n: number) => `$${n.toFixed(2)}`;
const cents = (p: number) => `${(p * 100).toFixed(1)}¢`;
/** Posizioni e ordini che il bot non ha saputo attribuire a una partita. */
const NO_MATCH = " senza-partita";

type EventBook = {
  match: string;
  group?: WatchGroup;
  legs: Position[];
  /** BUY appoggiati: esposizione che può ancora partire da sola. */
  buys: OpenOrder[];
  /** SELL appoggiati: sono i take-profit del bot, non soldi che escono. */
  sells: OpenOrder[];
  cost: number;
  payout: number;
  pending: number;
};

/**
 * Raggruppa per partita. Quando lo snapshot non la porta si ripiega su quella
 * delle proprie fill (`labels`): il bot tiene il legame token→partita solo in
 * RAM e lo perde a ogni riavvio — le posizioni ripescate dall'exchange tornano
 * senza, e per una gara finita non lo riacquista mai. Restano in "Senza partita"
 * solo i token di cui non sa niente nemmeno il DB.
 */
function buildBooks(positions: Position[], orders: OpenOrder[], groups: Record<string, WatchGroup>, labels: Record<string, PositionLabel>): EventBook[] {
  const books = new Map<string, EventBook>();
  const matchOf = (m: string | undefined, tokenId: string) => m || labels[tokenId]?.match || NO_MATCH;
  const book = (match: string): EventBook => {
    const found = books.get(match);
    if (found) return found;
    const fresh: EventBook = { match, group: groups[match], legs: [], buys: [], sells: [], cost: 0, payout: 0, pending: 0 };
    books.set(match, fresh);
    return fresh;
  };
  for (const p of positions) {
    const b = book(matchOf(p.match, p.tokenId));
    b.legs.push(p);
    b.cost += p.size * p.avgPrice;
    b.payout += p.size;
  }
  for (const o of orders) {
    const b = book(matchOf(o.match, o.tokenId));
    if (o.side === "BUY") {
      b.buys.push(o);
      b.pending += o.price * o.size;
    } else b.sells.push(o);
  }
  // Ordine di entrata in watch-list, come le corsie in Segnali. Le partite che
  // la watch-list non ha più (spazzate a fine gara, con la posizione ancora in
  // mano) finiscono in fondo, ordinate per id: sempre lo stesso posto.
  const key = (b: EventBook) => (b.group ? `0${b.group.createdAt}${b.match}` : `1${b.match}`);
  return [...books.values()].sort((a, b) => key(a).localeCompare(key(b)));
}

function PositionsView({
  active,
  positions,
  orders,
  groups,
  signals,
  matchScores,
  botRunning,
  ask,
  run,
}: {
  active: boolean;
  positions: Position[];
  orders: OpenOrder[];
  groups: WatchGroup[];
  signals: MatchSignal[];
  matchScores: Record<string, MatchStateEvent>;
  botRunning: boolean;
  ask: (m: string, fn: () => Promise<unknown>) => void;
  run: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  // Che scommessa è ogni token. Sta in un endpoint a parte, non nello snapshot:
  // il mercato di un token non cambia mai, mentre lo snapshot passa una volta al
  // secondo — rileggerlo lì sarebbe la stessa risposta sessanta volte al minuto.
  const [labels, setLabels] = useState<Record<string, PositionLabel>>({});
  // Nomi delle squadre di partite che la watch-list non ha piu': senza, la
  // tessera di una posizione sopravvissuta alla gara si intitola con l'id del
  // gruppo (`defensor-sporting-ca-cerro-04-08-00-00`), che si legge male e si
  // distingue peggio quando le tessere sono tre.
  const [matchNames, setMatchNames] = useState<Record<string, { home: string; away: string }>>({});
  const tokens = useMemo(
    () => [...new Set([...positions.map((p) => p.tokenId), ...orders.map((o) => o.tokenId)])].sort().join(","),
    [positions, orders],
  );
  useEffect(() => {
    if (!active || tokens === "") return;
    void api<{ labels: PositionLabel[]; names: Record<string, { home: string; away: string }> }>("/api/positions/labels")
      .then((r) => {
        setLabels(Object.fromEntries(r.labels.map((l) => [l.tokenId, l])));
        setMatchNames(r.names);
      })
      .catch(() => {
        /* i nomi sono un di più: senza, le righe mostrano il token */
      });
  }, [active, tokens]);

  const byId = useMemo(() => Object.fromEntries(groups.map((g) => [g.id, g])), [groups]);
  const books = useMemo(() => buildBooks(positions, orders, byId, labels), [positions, orders, byId, labels]);

  const cost = books.reduce((x, b) => x + b.cost, 0);
  const payout = books.reduce((x, b) => x + b.payout, 0);
  const pending = books.reduce((x, b) => x + b.pending, 0);
  const buys = books.reduce((x, b) => x + b.buys.length, 0);
  const sells = books.reduce((x, b) => x + b.sells.length, 0);
  const withLegs = books.filter((b) => b.legs.length > 0).length;
  // La barra di ogni tessera è larga in proporzione alla partita più esposta,
  // non al proprio totale: normalizzata su sé stessa direbbe "pieno" tanto su
  // $373 quanto su $19, e la classifica del rischio è la prima cosa che questa
  // pagina deve far vedere senza leggere una cifra. È anche il motivo per cui le
  // tessere possono restare in ordine fisso: a ordinare è la lunghezza, non la posizione.
  const scale = Math.max(...books.map((b) => b.cost + b.pending), 1e-6);

  if (books.length === 0) {
    return (
      <div className="card">
        <div className="card-head">Posizioni aperte</div>
        <div className="card-body">
          <div className="empty">
            {botRunning ? "Niente a mercato. Quando il bot compra, le posizioni compaiono qui raggruppate per partita." : "Il bot non è in esecuzione."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="metric-grid three">
        <div className="metric">
          <span className="metric-label">A mercato</span>
          <strong className="num">{usd(cost)}</strong>
          <span className="metric-meta">
            {positions.length} {positions.length === 1 ? "posizione" : "posizioni"} su {withLegs} {withLegs === 1 ? "partita" : "partite"}
          </span>
        </div>
        <div className="metric">
          <span className="metric-label">Se vincono tutte</span>
          <strong className="num">{usd(payout)}</strong>
          <span className="metric-meta">{usd(payout - cost)} di utile massimo</span>
        </div>
        <div className="metric">
          <span className="metric-label">Può ancora partire</span>
          <strong className="num">{usd(pending)}</strong>
          <span className="metric-meta">
            {buys} {buys === 1 ? "acquisto appoggiato" : "acquisti appoggiati"}
            {sells > 0 ? ` · ${sells} take-profit` : ""}
          </span>
        </div>
      </div>

      <div className="pos-events">
        {books.map((b) => (
          <EventBookCard
            key={b.match}
            book={b}
            scale={scale}
            labels={labels}
            names={matchNames[b.match]}
            signals={signals}
            score={matchScores[b.match]}
            botRunning={botRunning}
            ask={ask}
            run={run}
          />
        ))}
      </div>
    </>
  );
}

/** Nome della scommessa di una riga, o il token quando non lo sappiamo dire. */
const legName = (tokenId: string, label?: PositionLabel): string => betName(label?.kind, label?.outcome) ?? `${tokenId.slice(0, 10)}…`;
const legColor = (label?: PositionLabel): string => (label?.category ? CATEGORY_COLOR[label.category] : UNKNOWN_COLOR);

function EventBookCard({
  book,
  scale,
  labels,
  names: fallbackNames,
  signals,
  score,
  botRunning,
  ask,
  run,
}: {
  book: EventBook;
  /** Esposizione della partita più esposta: la barra piena della pagina. */
  scale: number;
  labels: Record<string, PositionLabel>;
  /** Squadre di una partita non più in watch-list, dal DB del pannello. */
  names?: { home: string; away: string };
  signals: MatchSignal[];
  score?: MatchStateEvent;
  botRunning: boolean;
  ask: (m: string, fn: () => Promise<unknown>) => void;
  run: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [done, setDone] = useState("");
  const [hot, setHot] = useState<string | null>(null);
  const [ordersOpen, setOrdersOpen] = useState(false);

  // L'esito resta un po': dice cosa è stato mandato, mentre quello che il bot ne
  // ha fatto si legge da solo nelle righe che spariscono.
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(""), 12_000);
    return () => clearTimeout(t);
  }, [done]);

  const names = book.group ? teamNames(signals, book.group) : fallbackNames ?? null;
  const orphan = book.match === NO_MATCH;
  const title = names ? `${names.home} – ${names.away}` : book.group?.label || (orphan ? "Senza partita" : book.match);
  const minute = useMemo(() => lastMinute(signals.filter((s) => s.match === book.match)), [signals, book.match]);
  const resting = book.buys.length + book.sells.length;
  // Cosa chiudere, detto per token: il bot attribuisce alla partita solo quello
  // che ha aperto in QUESTA sua vita, mentre la tessera raggruppa anche le
  // posizioni la cui partita l'abbiamo ripescata noi dalle fill. Senza questa
  // lista il server non troverebbe niente da vendere (ed e' il motivo per cui
  // "Chiudi evento" sulla tessera senza partita non faceva nulla).
  const tokenIds = useMemo(
    () => [...new Set([...book.legs.map((p) => p.tokenId), ...book.buys.map((o) => o.tokenId), ...book.sells.map((o) => o.tokenId)])],
    [book],
  );

  // La conferma racconta le due mosse NELL'ORDINE in cui partono: è l'unica cosa
  // che distingue "chiudi evento" da una fila di "vendi", e va detta prima.
  const ordini = resting === 1 ? "l’unico ordine appoggiato" : `i ${resting} ordini appoggiati`;
  const posizioni = book.legs.length === 1 ? "la posizione aperta" : `le ${book.legs.length} posizioni aperte`;

  const closeEvent = () =>
    ask(
      `Chiudere tutto su ${title}? ${
        resting > 0 ? `Prima si annullano ${ordini} (take-profit compresi), poi si vendono a mercato ${posizioni}` : `Si vendono a mercato ${posizioni}`
      }, ${usd(book.cost)} di costo. La vendita a mercato prende qualsiasi prezzo nel book.`,
      async () => {
        const r = await post<{ orders: number; sold: number }>("/api/positions/close-event", { ...(orphan ? {} : { match: book.match }), tokenIds });
        setDone(
          `Inviato: ${r.sold} ${r.sold === 1 ? "posizione venduta" : "posizioni vendute"} a mercato, ${r.orders} ${r.orders === 1 ? "ordine annullato" : "ordini annullati"}. Il bot può riaprire al prossimo segnale: per impedirlo spegni i tipi di scommessa su Partite.`,
        );
      },
    );

  const cancelOrders = () =>
    ask(
      `Annullare ${ordini} su ${title}? Comprende i take-profit, quindi quello che resta in mano non ha più una vendita programmata.`,
      async () => {
        // Senza gambe aperte close-event annulla e basta: e' la stessa mossa,
        // ma sa annullare anche gli ordini che il bot non lega a una partita.
        if (orphan) await post("/api/positions/close-event", { tokenIds });
        else await post("/api/bot/cancel-match", { match: book.match });
        setDone(`Inviato: annullamento dei ${resting} ordini di ${title}.`);
      },
    );

  const sellLeg = (p: Position) =>
    ask(
      `Vendere ${p.size.toFixed(1)} quote di ${legName(p.tokenId, labels[p.tokenId])} su ${title}? Ordine SELL a mercato: prende qualsiasi prezzo nel book.`,
      () => post(`/api/bot/sell/${p.tokenId}`),
    );

  return (
    <section className="card pos-event">
      <header className="pos-head">
        <div className="pos-id">
          <h3 className="pos-title">{title}</h3>
          {score || minute != null ? (
            <span className="pos-live">
              {score ? <span className="pos-score num">{score.home}-{score.away}</span> : null}
              {minute != null ? <span className="pos-minute num">{minute}&#39;</span> : null}
            </span>
          ) : null}
        </div>
        <div className="pos-figures">
          <span className="pos-cost num">{usd(book.cost)}</span>
          <span className="pos-cost-meta">a mercato{book.payout > 0 ? ` · ${usd(book.payout)} se vince` : ""}</span>
        </div>
        {book.legs.length > 0 ? (
          <button className="btn sm pos-close" disabled={!botRunning} onClick={closeEvent}>
            Chiudi evento
          </button>
        ) : (
          <button className="btn ghost sm pos-close" disabled={!botRunning} onClick={cancelOrders}>
            Annulla ordini
          </button>
        )}
      </header>

      <div
        className="pos-bar"
        role="img"
        aria-label={`Esposizione: ${book.legs.map((p) => `${legName(p.tokenId, labels[p.tokenId])} ${usd(p.size * p.avgPrice)}`).join("; ") || "nessuna posizione"}${book.pending > 0 ? `; ${usd(book.pending)} in acquisti appoggiati` : ""}`}
      >
        <div className="pos-bar-fill" style={{ width: `${((book.cost + book.pending) / scale) * 100}%` }}>
          {book.legs.map((p) => (
            <span
              key={p.tokenId}
              className={`pos-seg ${hot && hot !== p.tokenId ? "dim" : ""}`}
              style={{ flexGrow: Math.max(p.size * p.avgPrice, 0.001), background: legColor(labels[p.tokenId]) }}
              onMouseEnter={() => setHot(p.tokenId)}
              onMouseLeave={() => setHot(null)}
            />
          ))}
          {book.pending > 0 ? <span className="pos-seg pending" style={{ flexGrow: book.pending }} title={`${usd(book.pending)} di acquisti ancora appoggiati`} /> : null}
        </div>
      </div>

      {book.legs.length > 0 ? (
        <table className="tbl pos-legs">
          <thead>
            <tr>
              <th>Scommessa</th>
              <th className="num">Quote</th>
              <th className="num">Prezzo medio</th>
              <th className="num">Costo</th>
              <th className="num">Se vince</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {book.legs.map((p) => {
              const label = labels[p.tokenId];
              return (
                <tr key={p.tokenId} className={hot && hot !== p.tokenId ? "dim" : ""} onMouseEnter={() => setHot(p.tokenId)} onMouseLeave={() => setHot(null)}>
                  <td data-label="Scommessa">
                    <span className="pos-leg-name">
                      <span className="pos-dot" style={{ background: legColor(label) }} aria-hidden="true" />
                      {legName(p.tokenId, label)}
                    </span>
                    {label?.tag ? <span className="pos-leg-rule">{label.tag}</span> : null}
                    {p.realizedPnl !== 0 ? (
                      <span className={`pos-leg-rule ${p.realizedPnl > 0 ? "pnl-pos" : "pnl-neg"}`}>
                        {p.realizedPnl > 0 ? "+" : ""}
                        {p.realizedPnl.toFixed(2)} già chiusi su questo mercato
                      </span>
                    ) : null}
                  </td>
                  <td className="num" data-label="Quote">{p.size.toFixed(1)}</td>
                  <td className="num" data-label="Prezzo">{cents(p.avgPrice)}</td>
                  <td className="num" data-label="Costo">{usd(p.size * p.avgPrice)}</td>
                  <td className="num" data-label="Se vince">{usd(p.size)}</td>
                  <td data-label="">
                    <button className="btn ghost sm" disabled={!botRunning || p.size <= 0} onClick={() => sellLeg(p)}>
                      Vendi
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}

      {resting > 0 ? (
        <div className="pos-resting">
          <button className="pos-resting-head" onClick={() => setOrdersOpen(!ordersOpen)} aria-expanded={ordersOpen}>
            <span className={`pos-caret ${ordersOpen ? "open" : ""}`} aria-hidden="true">
              ▸
            </span>
            {book.buys.length > 0
              ? `${book.buys.length} ${book.buys.length === 1 ? "acquisto appoggiato" : "acquisti appoggiati"} · ${usd(book.pending)} possono ancora partire`
              : "nessun acquisto appoggiato"}
            {book.sells.length > 0 ? ` · ${book.sells.length} take-profit` : ""}
          </button>
          {ordersOpen ? (
            <>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Mercato</th>
                    <th>Tipo</th>
                    <th className="num">Prezzo</th>
                    <th className="num">Quote</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {[...book.buys, ...book.sells].map((o) => (
                    <tr key={o.orderId}>
                      <td data-label="Mercato">
                        <span className="pos-leg-name">
                          <span className="pos-dot" style={{ background: legColor(labels[o.tokenId]) }} aria-hidden="true" />
                          {legName(o.tokenId, labels[o.tokenId])}
                        </span>
                      </td>
                      <td data-label="Tipo">{o.side === "BUY" ? "acquisto" : "take-profit"}</td>
                      <td className="num" data-label="Prezzo">{cents(o.price)}</td>
                      <td className="num" data-label="Quote">{o.size.toFixed(1)}</td>
                      <td data-label="">
                        <button className="btn ghost sm" disabled={!botRunning} onClick={() => void run(() => post(`/api/bot/cancel/${o.orderId}`))}>
                          Cancella
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {book.legs.length > 0 ? (
                <div className="pos-resting-foot">
                  <button className="btn ghost sm" disabled={!botRunning} onClick={cancelOrders}>
                    Annulla tutti gli ordini della partita
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {done ? <p className="pos-done">{done}</p> : null}
    </section>
  );
}

// ── closed positions (round-trips, from the bot's "position-closed" event) ────
function ClosedPositions({ rows, mode }: { rows: ClosedPosition[]; mode: "PAPER" | "LIVE" }) {
  // Rows are fetched by the parent (Dashboard) so the open list can reuse them to
  // hide already-closed phantoms — this card just renders them.
  const total = rows.reduce((x, r) => x + r.realizedPnl, 0);
  return (
    <div className="card">
      <div className="card-head">
        Posizioni chiuse <span className="count">({rows.length} · {mode.toLowerCase()})</span>
        {rows.length > 0 && (
          <>
            <span className="spacer" />
            <span className="stat">
              realizzato <b className={`num ${total >= 0 ? "pnl-pos" : "pnl-neg"}`}>{total >= 0 ? "+" : ""}{total.toFixed(2)}</b>
            </span>
          </>
        )}
      </div>
      <div className="card-body">
        {rows.length === 0 ? (
          <div className="empty">Nessuna posizione chiusa ancora — i round-trip compaiono qui man mano.</div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Chiusa</th>
                <th>Match</th>
                <th>Strategia</th>
                <th>Shares</th>
                <th>Entry→Exit</th>
                <th>Realized</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} title={`${hhmmss(r.openedAt)}→${hhmmss(r.closedAt)}  ·  open: ${r.openReason ?? "—"}  ·  close: ${r.closeReason ?? "—"}`}>
                  <td className="ts" data-label="Chiusa">{hhmmss(r.closedAt)}</td>
                  <td data-label="Match">{r.match ?? `${r.tokenId.slice(0, 10)}…`}</td>
                  <td data-label="Strategia">{r.tag ?? "—"}</td>
                  <td className="num" data-label="Shares">{r.shares.toFixed(1)}</td>
                  <td className="num" data-label="Entry→Exit">{r.avgEntry.toFixed(2)}→{r.avgExit.toFixed(2)}</td>
                  <td className={`num ${r.realizedPnl >= 0 ? "pnl-pos" : "pnl-neg"}`} data-label="Realized">{r.realizedPnl >= 0 ? "+" : ""}{r.realizedPnl.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Processes({ units, ask, run }: { units: UnitStatus[]; ask: (m: string, fn: () => Promise<unknown>) => void; run: (fn: () => Promise<unknown>) => Promise<void> }) {
  return (
    <div>
      {units.map((u) => (
        <div className="proc" key={u.name}>
          <span className={`dot ${u.running ? "on" : "off"}`} />
          <span className="name">{u.name}</span>
          <span className="meta">
            {u.running
              ? `pid ${u.pid} · attivo da ${hhmmss(u.startedAt ?? undefined)}`
              : u.desired === "running"
                ? `fermo (exit ${u.lastExitCode ?? "?"}) — riavvio…`
                : "fermo"}
            {u.restarts > 0 ? ` · ${u.restarts} riavvii` : ""}
          </span>
          <span className="spacer" />
          {u.running ? (
            <>
              <button className="btn ghost sm" onClick={() => ask(`Riavviare ${u.name}?`, () => post(`/api/proc/${u.name}/restart`))}>
                Riavvia
              </button>
              <button className="btn ghost sm" onClick={() => ask(`Fermare ${u.name}? Resterà fermo finché non lo riavvii.`, () => post(`/api/proc/${u.name}/stop`))}>
                Ferma
              </button>
            </>
          ) : (
            <button className="btn sm" onClick={() => void run(() => post(`/api/proc/${u.name}/start`))}>
              Avvia
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function Logs({ logs }: { logs: LogLine[] }) {
  const [proc, setProc] = useState<"all" | "monitor" | "bot" | "fairodds">("all");
  const shown = logs.filter((l) => proc === "all" || l.proc === proc).slice(-150);
  return (
    <div className="card">
      <div className="card-head">
        Log
        <div className="tabs">
          {(["all", "monitor", "bot", "fairodds"] as const).map((p) => (
            <button key={p} className={`tab ${proc === p ? "active" : ""}`} onClick={() => setProc(p)}>
              {p}
            </button>
          ))}
        </div>
      </div>
      <div className="logs">
        {shown.length === 0 ? (
          <span className="stdout">— nessun output —</span>
        ) : (
          shown.map((l, i) => (
            <div key={i} className={l.stream}>
              <span className="lts">{hhmmss(l.ts)} </span>
              {proc === "all" ? `[${l.proc}] ` : ""}
              {l.line}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── in-panel documentation ───────────────────────────────────────────────────
function DocsView() {
  const [chapters, setChapters] = useState<Array<{ slug: string; title: string }>>([]);
  const [active, setActive] = useState("");
  const [html, setHtml] = useState("");

  useEffect(() => {
    void api<Array<{ slug: string; title: string }>>("/api/docs").then((list) => {
      setChapters(list);
      if (list[0]) setActive(list[0].slug);
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    void api<{ content: string }>(`/api/docs/${active}`).then(({ content }) => setHtml(marked.parse(content) as string));
  }, [active]);

  return (
    <div className="docs-layout">
      <nav className="docs-nav">
        {chapters.map((c) => (
          <button key={c.slug} className={`docs-link ${c.slug === active ? "active" : ""}`} onClick={() => setActive(c.slug)}>
            {c.title}
          </button>
        ))}
      </nav>
      <article className="docs-content" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}

// ── config editor (M3) ──────────────────────────────────────────────────────
type ConfigName = "matches" | "rules" | "markets";
type ConfigData = { name: ConfigName; path: string; restartRequired: boolean; ownerOnly: boolean; note: string; content: string };
type Version = { seq: number; ts: string; user: string; bytes: number };

function ConfigEditor({ me, ask }: { me: Me; ask: (m: string, fn: () => Promise<unknown>) => void }) {
  const [tab, setTab] = useState<ConfigName>("rules");
  const [cfg, setCfg] = useState<ConfigData | null>(null);
  const [text, setText] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [savedNeedsRestart, setSavedNeedsRestart] = useState(false);
  const [versions, setVersions] = useState<Version[]>([]);
  const [revertSeq, setRevertSeq] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (name: ConfigName) => {
    setBusy(true);
    setErrors([]);
    setSavedNeedsRestart(false);
    try {
      const c = await api<ConfigData>(`/api/config/${name}`);
      setCfg(c);
      setText(c.content);
      setVersions(await api<Version[]>(`/api/config/${name}/versions`));
      setRevertSeq("");
    } catch (err) {
      setErrors([err instanceof ApiError ? err.message : "network error"]);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    void load(tab);
  }, [tab]);

  const editable = !!cfg && (!cfg.ownerOnly || me.role === "owner");
  const dirty = !!cfg && text !== cfg.content;

  const save = async () => {
    if (!cfg) return;
    setBusy(true);
    setErrors([]);
    try {
      const r = await api<{ restartRequired: boolean }>(`/api/config/${tab}`, { method: "PUT", body: { content: text } });
      setCfg({ ...cfg, content: text });
      setSavedNeedsRestart(r.restartRequired);
      setVersions(await api<Version[]>(`/api/config/${tab}/versions`));
    } catch (err) {
      setErrors(err instanceof ApiError ? (err.errors.length ? err.errors : [err.message]) : ["network error"]);
    } finally {
      setBusy(false);
    }
  };

  const revert = (seq: number) =>
    ask(`Ripristinare ${tab} alla versione #${seq}? Il contenuto attuale resta nella cronologia.`, async () => {
      await post(`/api/config/${tab}/revert`, { seq });
      await load(tab);
    });

  return (
    <div className="card config-card">
      <div className="card-head">
        Config
        {cfg && <span className="count">{cfg.restartRequired ? "richiede riavvio del bot" : "si applica subito"}</span>}
        <div className="tabs">
          {(["rules", "markets"] as const).map((n) => (
            <button key={n} className={`tab ${tab === n ? "active" : ""}`} onClick={() => setTab(n)}>
              {n}
            </button>
          ))}
        </div>
      </div>
      <div className="config-body">
        {cfg && <div className="config-note">{cfg.note}</div>}
        {cfg?.ownerOnly && me.role !== "owner" && <div className="config-note locked">Sola lettura — per modificare questo file serve il ruolo owner.</div>}
        <textarea className="editor" value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} disabled={!editable || busy} rows={14} />
        {errors.length > 0 && (
          <div className="config-errors">
            {errors.map((e, i) => (
              <div key={i}>✗ {e}</div>
            ))}
          </div>
        )}
        {savedNeedsRestart && (
          <div className="config-saved">
            ✓ Salvato. Il bot va riavviato per applicarlo.{" "}
            <button className="btn sm" onClick={() => ask("Riavviare il bot ora per applicare la nuova config?", () => post("/api/proc/bot/restart"))}>
              Riavvia il bot ora
            </button>
          </div>
        )}
        {!savedNeedsRestart && !dirty && cfg && errors.length === 0 && <div className="config-hint">Nessuna modifica non salvata.</div>}
        <div className="config-actions">
          <button className="btn primary" onClick={() => void save()} disabled={!editable || !dirty || busy}>
            {busy ? "In corso…" : "Valida e salva"}
          </button>
          <button className="btn ghost" onClick={() => cfg && setText(cfg.content)} disabled={!dirty || busy}>
            Annulla modifiche
          </button>
          <span className="spacer" />
          {versions.length > 0 && (
            <>
              <select className="version-select" value={revertSeq} onChange={(e) => setRevertSeq(e.target.value)} disabled={!editable}>
                <option value="">cronologia versioni…</option>
                {versions.map((v) => (
                  <option key={v.seq} value={v.seq}>
                    #{v.seq} · {v.ts.slice(0, 16).replace("T", " ")} · {v.user} · {v.bytes}B
                  </option>
                ))}
              </select>
              <button className="btn ghost sm" disabled={!editable || !revertSeq} onClick={() => revert(Number(revertSeq))}>
                Ripristina
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

type AuditRow = { ts: string; user: string; action: string; payload: string | null; ip: string | null };

function Audit() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const load = () => void api<AuditRow[]>("/api/audit?limit=100").then(setRows).catch(() => {});
  useEffect(load, []);
  return (
    <div className="card">
      <div className="card-head">
        Audit log <span className="count">(solo owner)</span>
        <div className="tabs">
          <button className="tab" onClick={load}>
            aggiorna
          </button>
        </div>
      </div>
      <div className="card-body">
        {rows.length === 0 ? (
          <div className="empty">Nessuna voce.</div>
        ) : (
          rows.map((r, i) => (
            <div className="row" key={i}>
              <span className="ts">{hhmmss(r.ts)}</span>
              <span className="grow">
                <b>{r.user}</b> {r.action}
                {r.payload ? ` ${r.payload}` : ""}
              </span>
              <span className="stat">{r.ip}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── fair value (quote dei bookmaker) ─────────────────────────────────────────
/**
 * La lavagna operativa: cosa vale ogni esito secondo i bookmaker, cosa chiede
 * Polymarket adesso, e — se il secondo sta sotto il primo — comprarlo.
 *
 * LA DOMANDA E' CAMBIATA, E CON LEI LA PAGINA
 * Prima questa pagina rispondeva a "questa misura avrebbe fatto scattare una
 * regola, e se no perche'". Continua a farlo (i verdetti ci sono tutti), ma la
 * domanda che si fa davvero guardandola in partita e' un'altra: "conviene
 * comprarlo adesso, e quanto?". Sono numeri diversi in primo piano — il fair
 * era un'opinione, l'ask e' un prezzo — e una casella larga un terzo di riga
 * non ne regge quattro grandi. Ne resta grande uno: quello che si paga.
 *
 * IL VERDE VUOL DIRE UNA COSA SOLA
 * "Questo prezzo sta sotto il tuo limite, si compra". Prima voleva dire "passa
 * le gate", che e' un'altra cosa e per giunta una che non porta a nessun gesto.
 * Un colore che significa due cose non ne significa nessuna: il verdetto delle
 * gate resta scritto, non colorato.
 *
 * E SI SPEGNE DA SOLO
 * Il verde pretende un verdetto "passa", che si ricalcola ogni secondo su
 * `now` — quindi una misura che supera il proprio `maxFairAgeSeconds` (25s dal
 * vivo) smette di essere verde da se'. E' la stessa ragione per cui la pagina
 * ribatte anche quando i dati non cambiano: un numero scaduto scritto come
 * attuale e' esattamente la bugia che questa pagina esiste per non dire.
 */
type FairValueRowUi = {
  match: string;
  kind: string;
  line: string;
  outcome: string;
  fairProb: number;
  buyMaxCents: number;
  engines: number;
  platforms: number | null;
  sigmaPoints: number;
  capturedAt: string;
  live: number;
  home: string | null;
  away: string | null;
};
type FairGateUi = {
  id: string;
  fairKind: string;
  requireLive: boolean | null;
  maxFairAgeSeconds: number;
  minPlatforms: number;
  minEngines: number;
  maxSigmaPoints: number;
  enabled: boolean;
};
/** Una riga dell'evento `prices` del bot: la cima del libro di un token, gia'
 *  legata a partita/mercato/esito (la traduzione la fa il registry, che e' il
 *  solo posto dove quel legame esiste). */
type QuoteUi = {
  match: string;
  kind: string;
  line: string;
  outcome: string;
  tokenId: string;
  bid?: number;
  ask?: number;
  askShares?: number;
  askUsd?: number;
  mid?: number;
  at?: number;
};
type FairModeUi = { mode: "auto" | "manuale" | "misto"; attive: number; spente: number };
type FairValuesResponse = {
  connected: boolean;
  detail: string;
  values: FairValueRowUi[];
  gates: FairGateUi[];
  shots?: string[];
  quotes?: QuoteUi[];
  mode?: FairModeUi;
  botMode?: "PAPER" | "LIVE";
  botRunning?: boolean;
};

type FairVerdict =
  | { esito: "nessuna-regola" }
  | { esito: "passa"; regola: string; enabled: boolean }
  | { esito: "bloccata"; regola: string; gate: string; enabled: boolean };

/** Mirror esatto di `judgeMeasure` (src/fair-gates.ts), che a sua volta mirror-a
 *  `fairValueFires` del bot. L'ordine delle prove è quello, e va tenuto: cambia
 *  il MOTIVO riportato, che è tutto quello che questa pagina aggiunge. */
function judgeUi(m: FairValueRowUi, gates: FairGateUi[], nowMs: number): FairVerdict {
  const live = m.live === 1;
  const applicabili = gates.filter((g) => (g.fairKind === "*" || g.fairKind === m.kind) && (g.requireLive === null || g.requireLive === live));
  if (applicabili.length === 0) return { esito: "nessuna-regola" };
  const capturedMs = Date.parse(m.capturedAt);
  const ageSeconds = Number.isFinite(capturedMs) ? (nowMs - capturedMs) / 1000 : null;

  let migliore: { gate: string; passate: number; regola: string; enabled: boolean } | null = null;
  for (const g of applicabili) {
    const prove: Array<[string, boolean]> = [
      ["minEngines", m.engines >= g.minEngines],
      ["minPlatforms", (m.platforms ?? 0) >= g.minPlatforms],
      ["maxSigmaPoints", m.sigmaPoints <= g.maxSigmaPoints],
      ["maxFairAgeSeconds", !Number.isFinite(g.maxFairAgeSeconds) || (ageSeconds !== null && ageSeconds <= g.maxFairAgeSeconds)],
    ];
    const i = prove.findIndex(([, ok]) => !ok);
    if (i === -1) return { esito: "passa", regola: g.id, enabled: g.enabled };
    if (!migliore || i > migliore.passate) migliore = { gate: prove[i]![0], passate: i, regola: g.id, enabled: g.enabled };
  }
  return { esito: "bloccata", regola: migliore!.regola, gate: migliore!.gate, enabled: migliore!.enabled };
}

const etaTesto = (iso: string, nowMs: number) => {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "?";
  const s = Math.max(0, Math.round((nowMs - ms) / 1000));
  return s < 90 ? `${s}s` : `${Math.round(s / 60)}m`;
};

/**
 * Oltre questa età un prezzo di Polymarket non è più un prezzo.
 *
 * Il ponte del bot rimanda TUTTE le quote ogni 30 secondi anche quando non si
 * muovono, quindi una riga più vecchia di così non è un mercato fermo: è il
 * filo che si è interrotto (bot riavviato, WebSocket del CLOB caduto). La
 * differenza conta: un mercato fermo si può comprare, un filo interrotto no.
 */
const QUOTE_MAX_AGE_MS = 45_000;

/**
 * Le due lingue in cui si può leggere lo stesso numero.
 *
 * Un bookmaker scrive la **quota decimale** (1.74: quanto torna per ogni euro),
 * Polymarket scrive il **prezzo in centesimi** (57¢: quanto costa un contratto
 * che ne paga 100). Dicono la stessa cosa — sono l'una il reciproco dell'altra —
 * ma chi guarda ha in testa una delle due, e tradurre a mente mentre il mercato
 * si muove è il modo di sbagliarsi.
 *
 * L'INVERSIONE DA NON SBAGLIARE
 * Il nostro BUY MAX è il prezzo **massimo** che pagheremmo. In centesimi resta un
 * massimo; in quota decimale diventa un **minimo**, perché pagare meno significa
 * quota più alta. Un'etichetta "max" sopra una quota decimale direbbe l'esatto
 * contrario del vero, e su un limite d'acquisto è l'errore che costa.
 */
type Formato = "decimale" | "centesimi";

/**
 * Il formato scelto, che sopravvive al ricaricamento.
 *
 * Non è una preferenza da tenere sul server: è come QUESTA persona legge i
 * numeri, su questo schermo — due operatori davanti alla stessa lavagna possono
 * volerne due diverse, e una impostazione condivisa gliene imporrebbe una.
 * Sta accanto alle altre scelte locali della pagina (avvisi, video, segnaposto
 * delle corsie), che per la stessa ragione non passano da `panel.db`.
 *
 * Il default è **centesimi**: è la lingua di Polymarket, cioè quella in cui si
 * decide di comprare, e questa pagina ora si usa per comprare. Uno storage
 * illeggibile o un valore che non riconosciamo tornano al default invece di
 * propagare una scelta che nessuno ha fatto.
 */
const FORMATO_KEY = "panel.quote-formato.v1";

function formatoSalvato(): Formato {
  try {
    return localStorage.getItem(FORMATO_KEY) === "decimale" ? "decimale" : "centesimi";
  } catch {
    return "centesimi";
  }
}

function salvaFormato(f: Formato): void {
  try {
    localStorage.setItem(FORMATO_KEY, f);
  } catch {
    /* storage pieno o negato: la pagina funziona lo stesso, si ricomincia dal
       default al prossimo caricamento */
  }
}

/** Probabilità 0..1 → quota decimale. Una probabilità nulla non ha quota. */
const quotaDecimale = (prob: number): string => (prob > 0 ? (1 / prob).toFixed(2) : "—");
/** Centesimi → quota decimale (100¢ = certezza). */
const centesimiInQuota = (cents: number): string => (cents > 0 ? (100 / cents).toFixed(2) : "—");

/** Il nome che un bookmaker italiano dà a questo mercato. */
function nomeMercato(kind: string, line: string): string {
  if (kind === "WINNER") return "Esito finale";
  if (kind === "OU") return `Under / Over ${line}`;
  if (kind === "BTTS") return "Gol / No Gol";
  if (kind === "FTS") return "Prima rete";
  return line ? `${kind} ${line}` : kind;
}

/** …e il nome che dà all'esito: sulla lavagna sta scritto 1, X, 2. */
const NOME_ESITO: Record<string, string> = {
  home: "1", draw: "X", away: "2",
  over: "Over", under: "Under",
  yes: "Gol", no: "No Gol",
};

/** L'ordine in cui una lavagna li scrive. Non alfabetico: 1 X 2, mai 1 2 X. */
const ORDINE_ESITO = ["home", "draw", "away", "over", "under", "yes", "no"];

/**
 * E l'ordine dei mercati: esito finale in cima, poi gli Under/Over per soglia
 * crescente, poi il resto.
 *
 * Non è cosmesi. Arrivando dall'API i mercati sono nell'ordine in cui sono stati
 * letti, quindi la stessa partita cambia disposizione a ogni aggiornamento: chi
 * guarda perde il posto ogni dieci secondi. Una lavagna vera ha un ordine fisso,
 * ed è quello che la rende leggibile a colpo d'occhio.
 */
function pesoMercato(kind: string, line: string): number {
  if (kind === "WINNER") return 0;
  if (kind === "OU") return 100 + (Number(line) || 0);
  if (kind === "BTTS") return 300;
  if (kind === "FTS") return 400;
  return 500;
}

/** La chiave con cui una misura incontra la sua quota. Identica a `quoteKey` in
 *  `src/bot-bridge.ts`: è il contratto fra i due lati. */
const chiaveQuota = (match: string, kind: string, line: string, outcome: string) => `${match}|${kind}|${line}|${outcome}`;

type CellaLavagna = { v: FairValueRowUi; giudizio: FairVerdict; q?: QuoteUi };
type RigaMercato = { chiave: string; nome: string; celle: CellaLavagna[]; bloccoComune: string | null; giaAperto: boolean };
type PartitaLavagna = { match: string; titolo: string; live: boolean; capturedAt: string; righe: RigaMercato[] };

/** Raggruppa le misure per partita e per mercato, nell'ordine della lavagna. */
function inLavagne(
  righe: CellaLavagna[],
  spesi: ReadonlySet<string> = new Set(),
): PartitaLavagna[] {
  const perPartita = new Map<string, CellaLavagna[]>();
  for (const r of righe) {
    const slot = perPartita.get(r.v.match);
    if (slot) slot.push(r);
    else perPartita.set(r.v.match, [r]);
  }
  const out: PartitaLavagna[] = [];
  for (const [match, celle] of perPartita) {
    const perMercato = new Map<string, CellaLavagna[]>();
    for (const c of celle) {
      const k = `${c.v.kind}|${c.v.line}`;
      const slot = perMercato.get(k);
      if (slot) slot.push(c);
      else perMercato.set(k, [c]);
    }
    const prima = celle[0]!.v;
    out.push({
      match,
      titolo: prima.home && prima.away ? `${prima.home} – ${prima.away}` : match,
      live: prima.live === 1,
      // La più vecchia fra le misure della partita: è quella che decide se
      // l'insieme è ancora buono, esattamente come per le lavagne dentro un fair.
      capturedAt: celle.reduce((piuVecchia, c) => (c.v.capturedAt < piuVecchia ? c.v.capturedAt : piuVecchia), prima.capturedAt),
      righe: [...perMercato.entries()]
        .map(([chiave, gruppo]) => {
          const celle = [...gruppo].sort((a, b) => ORDINE_ESITO.indexOf(a.v.outcome) - ORDINE_ESITO.indexOf(b.v.outcome));
          // Quando TUTTA la riga è ferma per lo stesso motivo, il motivo è della
          // riga: ripeterlo sotto ogni esito riempie la card di tre etichette
          // identiche e nasconde i numeri, che sono la ragione per cui si guarda.
          const motivi = new Set(celle.map((c) => (c.giudizio.esito === "bloccata" ? c.giudizio.gate : "")));
          const bloccoComune = motivi.size === 1 && !motivi.has("") ? [...motivi][0]! : null;
          // Il colpo `oneEntryPerMatch` e' per MERCATO (tutte le linee di un
          // OU ne condividono uno), quindi la chiave non porta la soglia. Sta
          // sulla riga e non sulla cella per la stessa ragione.
          const giaAperto = spesi.has(`${gruppo[0]!.v.match}|${gruppo[0]!.v.kind}`);
          return { chiave, nome: nomeMercato(gruppo[0]!.v.kind, gruppo[0]!.v.line), celle, bloccoComune, giaAperto };
        })
        .sort((a, b) => pesoMercato(a.celle[0]!.v.kind, a.celle[0]!.v.line) - pesoMercato(b.celle[0]!.v.kind, b.celle[0]!.v.line)),
    });
  }
  return out;
}

/** Le lavagne dietro a una misura, come le serve /api/fair-values/boards. */
type BoardUi = { bookmaker: string; bookmakerName: string; engine: string; odds: Record<string, number>; fetchedAt: string | null };
type MarketBoardsUi = { kind: string; line: string; capturedAt: string; live: boolean; boards: BoardUi[] };
type BoardsResponse = { markets: MarketBoardsUi[]; detail?: string };

/**
 * L'esito nel vocabolario delle lavagne.
 *
 * Il motore traduce in quello del bot (`OVER` → `over`) prima di pubblicare; qui
 * si torna indietro, perché le lavagne grezze non sono state tradotte — sono
 * l'ingresso, e l'ingresso si conserva com'è.
 */
const ESITO_LAVAGNA: Record<string, string> = {
  home: "1", draw: "X", away: "2",
  over: "OVER", under: "UNDER",
  yes: "SI", no: "NO",
};

/**
 * Il colore di un marchio è quello del suo MOTORE, non del marchio.
 *
 * È la cosa meno ovvia e più utile di questa tendina: nove marchi possono essere
 * un solo ufficio prezzi, e allora le loro quote non sono nove conferme ma una
 * ripetuta nove volte. Colorando per motore, i cloni si vedono senza leggere
 * niente — e la dispersione bassa smette di sembrare un accordo.
 */
function coloreMotore(engine: string): string {
  let h = 0;
  for (let i = 0; i < engine.length; i++) h = (h * 31 + engine.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 62% 58%)`;
}

/** Due lettere, per i marchi di cui non abbiamo l'icona. Vedi `brand-icons.ts`:
 *  tre siti su venti non ne servono una utilizzabile, e la sigla è il ripiego —
 *  che vale anche per qualunque marchio aggiunto in futuro. */
const sigla = (nome: string) => nome.replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase();

/**
 * Le lavagne di una partita, chieste una volta sola per tutti quelli che le
 * guardano.
 *
 * Servono a due cose ora, non più a una: alla tendina "chi quota così" e alla
 * riga in fondo a ogni casella, dove sta la quota MIGLIORE fra i bookmaker. La
 * risposta vale per l'intera partita — tutti i suoi mercati arrivano insieme —
 * quindi si tiene finché è plausibilmente fresca, e le richieste in volo si
 * condividono: senza, aprire una pagina con venti partite ne faceva partire
 * quaranta (una per card più una per ogni cella attraversata dal mouse).
 *
 * La durata è tarata sul giro del motore (~45s): oltre, le lavagne mostrate
 * sarebbero più vecchie del numero che spiegano.
 */
const CACHE_BOARDS_MS = 30_000;
const cacheBoards = new Map<string, { at: number; dati: BoardsResponse }>();
const boardsInVolo = new Map<string, Promise<BoardsResponse>>();

function chiediBoards(match: string): Promise<BoardsResponse> {
  const fresca = cacheBoards.get(match);
  if (fresca && Date.now() - fresca.at < CACHE_BOARDS_MS) return Promise.resolve(fresca.dati);
  const inVolo = boardsInVolo.get(match);
  if (inVolo) return inVolo;
  const p = api<BoardsResponse>(`/api/fair-values/boards?match=${encodeURIComponent(match)}`)
    .then((r) => {
      cacheBoards.set(match, { at: Date.now(), dati: r });
      return r;
    })
    .catch(() => ({ markets: [], detail: "errore di rete" }) as BoardsResponse)
    .finally(() => boardsInVolo.delete(match));
  boardsInVolo.set(match, p);
  return p;
}

/** Le lavagne di una partita, ricaricate quando la partita cambia o quando
 *  `generazione` avanza (una rilettura è appena arrivata). */
function useBoards(match: string, generazione: number): BoardsResponse | null {
  const [dati, setDati] = useState<BoardsResponse | null>(() => cacheBoards.get(match)?.dati ?? null);
  useEffect(() => {
    let morto = false;
    void chiediBoards(match).then((r) => !morto && setDati(r));
    return () => {
      morto = true;
    };
  }, [match, generazione]);
  return dati;
}

/** La quota migliore fra i bookmaker su un esito, e chi la fa. Quota alta = si
 *  paga meno, quindi il "migliore" è il massimo. */
function migliorLavagna(boards: BoardsResponse | null, kind: string, line: string, outcome: string): { quota: number; nome: string } | null {
  const mercato = boards?.markets.find((m) => m.kind === kind && (m.line ?? "") === line);
  if (!mercato) return null;
  const chiave = ESITO_LAVAGNA[outcome] ?? outcome;
  let best: { quota: number; nome: string } | null = null;
  for (const b of mercato.boards) {
    const q = b.odds?.[chiave];
    if (typeof q !== "number" || !(q > 0)) continue;
    if (!best || q > best.quota) best = { quota: q, nome: b.bookmakerName || b.bookmaker };
  }
  return best;
}

/**
 * Chi quota così: le lavagne che stanno dietro a un numero.
 *
 * Si chiedono solo quando qualcuno le guarda — non viaggiano sul filo dei
 * segnali, dove moltiplicherebbero il flusso per venti ripetendosi identiche su
 * ogni esito dello stesso mercato.
 */
function TendinaLavagne({
  boards,
  kind,
  line,
  outcome,
  formato,
}: {
  boards: BoardsResponse | null;
  kind: string;
  line: string;
  outcome: string;
  formato: Formato;
}) {
  const mercato = boards?.markets.find((m) => m.kind === kind && (m.line ?? "") === line);
  const chiave = ESITO_LAVAGNA[outcome] ?? outcome;
  const righe = (mercato?.boards ?? [])
    .map((b) => ({ b, quota: b.odds?.[chiave] }))
    .filter((r): r is { b: BoardUi; quota: number } => typeof r.quota === "number" && r.quota > 0)
    // Dal più conveniente: quota alta = si paga meno. L'ordine è la risposta a
    // "chi è il migliore su questo esito", che è la domanda che si fa guardando.
    .sort((a, b) => b.quota - a.quota);

  /**
   * Da che parte aprirsi, e fin dove.
   *
   * Venti marchi fanno una tendina alta cinquecento pixel: sull'ultima riga di
   * una card in fondo alla pagina finiva sotto il bordo dello schermo, e la
   * barra di scorrimento interna non era un rimedio — sta dentro un pannello
   * che si apre al passaggio del mouse, e per raggiungerla bisognava
   * attraversare uno stacco di sei pixel che chiudeva tutto.
   *
   * Quindi: si misura lo spazio vero sopra e sotto la casella, si sceglie il
   * lato che ne ha di più, e si scorre SOLO se non basta nemmeno quello. Il
   * calcolo gira una volta per apertura (le lavagne arrivano insieme, e
   * `righe.length` e' l'unica cosa che ne cambia l'altezza).
   */
  // Un solo istante per tutte le righe: letto al render, come i numeri che
  // spiega. La tendina vive pochi secondi, quindi non serve che ribatta.
  const adesso = Date.now();
  const box = useRef<HTMLDivElement | null>(null);
  const [posa, setPosa] = useState<{ sopra: boolean; maxH: number | null }>({ sopra: false, maxH: null });
  useLayoutEffect(() => {
    const el = box.current;
    const cella = el?.parentElement?.getBoundingClientRect();
    if (!el || !cella) return;
    const MARGINE = 14; // un filo di respiro dal bordo della finestra
    const sotto = window.innerHeight - cella.bottom - MARGINE;
    const sopraSpazio = cella.top - MARGINE;
    const serve = el.scrollHeight;
    const sopra = serve > sotto && sopraSpazio > sotto;
    const spazio = sopra ? sopraSpazio : sotto;
    // `null` = nessun tetto, cioè nessuna barra: è il caso normale e va tenuto
    // distinto da "un tetto che per caso basta", perché solo il primo garantisce
    // che tutte le lavagne siano leggibili senza toccare niente.
    setPosa({ sopra, maxH: serve > spazio ? Math.max(160, spazio) : null });
  }, [righe.length]);

  return (
    <div
      className={`lav-tendina ${posa.sopra ? "sopra" : ""}`}
      role="tooltip"
      ref={box}
      style={posa.maxH != null ? { maxHeight: `${posa.maxH}px` } : undefined}
    >
      <div className="lav-tendina-testa">
        <span>Chi quota così</span>
        <span className="lav-tendina-mercato">
          {nomeMercato(kind, line)} · {NOME_ESITO[outcome] ?? outcome}
        </span>
      </div>
      {boards === null ? (
        <div className="lav-tendina-vuota">Leggo le lavagne…</div>
      ) : righe.length === 0 ? (
        <div className="lav-tendina-vuota">{boards.detail ?? "Lavagne non disponibili per questo mercato."}</div>
      ) : (
        <>
          <ul className="lav-tendina-lista">
            {righe.map(({ b, quota }) => (
              <li key={b.bookmaker}>
                {/* L'anello resta colorato per motore anche quando c'è l'icona:
                    il logo dice QUALE marchio, il colore dice CHI decide il
                    prezzo — ed è la seconda a cambiare la lettura. */}
                <span
                  className={`lav-marchio ${iconaMarchio(b.bookmaker) ? "con-icona" : ""}`}
                  style={{ ["--motore" as string]: coloreMotore(b.engine) }}
                  title={`motore ${b.engine}`}
                >
                  {/* Niente `loading="lazy"` sull'icona: i byte sono già nel
                      documento (data URI), quindi non c'è niente da rimandare, e
                      l'attributo ritardava soltanto il momento in cui compare. */}
                  {iconaMarchio(b.bookmaker) ? (
                    <img src={iconaMarchio(b.bookmaker)!} alt="" width={16} height={16} decoding="sync" />
                  ) : (
                    sigla(b.bookmakerName || b.bookmaker)
                  )}
                </span>
                <span className="lav-marchio-nome">{b.bookmakerName || b.bookmaker}</span>
                {/* Quando quella lavagna e' stata letta. Non e' un dettaglio:
                    il giro del motore tocca le piattaforme in parallelo ma
                    ognuna risponde con i suoi tempi, e una che ha impiegato
                    novanta secondi sta quotando un'altra partita rispetto a
                    quella che ha risposto in due. Il fair prende comunque la
                    piu' vecchia come eta' della misura, ma qui si vede QUALE. */}
                <span className="lav-marchio-eta num">{b.fetchedAt ? etaTesto(b.fetchedAt, adesso) : "—"}</span>
                {/* Un decimale in più che nella cella: lì il numero è un titolo,
                    qui serve a distinguere venti lavagne fra loro. A zero
                    decimali 1.76 e 1.70 diventano entrambi "57¢" e il confronto
                    — l'unica ragione per cui questa tendina esiste — sparisce. */}
                <span className="lav-marchio-quota num">
                  {formato === "decimale" ? quota.toFixed(2) : `${(100 / quota).toFixed(1)}¢`}
                </span>
              </li>
            ))}
          </ul>
          <div className="lav-tendina-piede">
            {righe.length} lavagne · {new Set(righe.map((r) => r.b.engine)).size} motori — stesso colore, stesso ufficio prezzi
          </div>
        </>
      )}
    </div>
  );
}

/** Lo stato di un acquisto a mano, sotto la riga che l'ha chiesto. La conferma
 *  non è una fase: vive a parte, perché è di RIGA e ce n'è una sola. */
type StatoAcquisto =
  | { fase: "invio" }
  | { fase: "fatto"; ok: boolean; testo: string; tokenId?: string }
  | { fase: "venduto" };

/** I tagli rapidi nella conferma. Il primo è quello che le regole userebbero
 *  (`shares: 25` con `sizing: "usdc"`), gli altri due sono per quando la misura
 *  vale davvero e 25$ sarebbero stati un peccato. */
const TAGLI = [25, 100, 250];

/**
 * Una partita, scritta come la scriverebbe una lavagna: righe di mercato,
 * colonne di esito, numeri in monospazio.
 */
function Lavagna({
  partita,
  formato,
  now,
  quote,
  botMode,
  operabile,
  generazione,
  onRilettura,
  rileggendo,
}: {
  partita: PartitaLavagna;
  formato: Formato;
  now: number;
  quote: Map<string, QuoteUi>;
  botMode: "PAPER" | "LIVE";
  /** false quando il bot non gira: le caselle si guardano, non si comprano. */
  operabile: boolean;
  generazione: number;
  onRilettura: () => void;
  rileggendo: boolean;
}) {
  const boards = useBoards(partita.match, generazione);
  return (
    <section className="lav">
      {/* Il nome della partita sta su una riga SUA.
          Sulla stessa riga dello stato, dell'età e del pulsante finiva troncato
          a meta' ("Cruzeiro – …"), e un titolo troncato e' la prima cosa che si
          legge di una card: due partite della stessa squadra diventano
          indistinguibili proprio dove serve distinguerle. Va a capo invece di
          troncare — una card alta una riga in piu' costa molto meno. */}
      <header className="lav-testa">
        <span className="lav-squadre">{partita.titolo}</span>
        <span className="lav-testa-meta">
          <span className={`lav-stato ${partita.live ? "vivo" : ""}`}>{partita.live ? "in corso" : "prepartita"}</span>
          <span className="lav-eta">{etaTesto(partita.capturedAt, now)}</span>
          <span className="spacer" />
          {/* Dice cosa fa DAVVERO. Non esiste una lettura per partita — ogni
              piattaforma serve il suo listino intero in una chiamata — e un
              pulsante che promette una cosa e ne fa un'altra si smette di premere. */}
          <button
            type="button"
            className={`lav-rileggi ${rileggendo ? "girando" : ""}`}
            onClick={onRilettura}
            disabled={rileggendo}
            title="Rilegge tutte le lavagne adesso invece di aspettare il giro: i bookmaker si leggono in blocco, non partita per partita. Con il giro a ~24s serve a poco — le quote si rinfrescano da sole."
          >
            <Icon name="refresh" />
            {rileggendo ? "giro in corso" : "rileggi"}
          </button>
        </span>
      </header>

      {partita.righe.map((riga) => (
        <RigaLavagna
          key={riga.chiave}
          riga={riga}
          formato={formato}
          quote={quote}
          boards={boards}
          botMode={botMode}
          operabile={operabile}
        />
      ))}
    </section>
  );
}

/**
 * Una riga di mercato. È un componente a sé perché tiene lo stato della tendina
 * e delle conferme: una tendina sola aperta per volta, e una conferma sola.
 */
function RigaLavagna({
  riga,
  formato,
  quote,
  boards,
  botMode,
  operabile,
}: {
  riga: RigaMercato;
  formato: Formato;
  quote: Map<string, QuoteUi>;
  boards: BoardsResponse | null;
  botMode: "PAPER" | "LIVE";
  operabile: boolean;
}) {
  const [apertaSu, setApertaSu] = useState<string | null>(null);
  /** La casella in attesa di conferma, con il prezzo che l'operatore ha VISTO —
   *  non quello che il prossimo aggiornamento porterà: si conferma quello, o si
   *  conferma un prezzo che nessuno ha mai guardato. */
  const [conferma, setConferma] = useState<{ v: FairValueRowUi; prezzo: number; tokenId?: string } | null>(null);
  const [acquisto, setAcquisto] = useState<Record<string, StatoAcquisto | undefined>>({});
  const [taglio, setTaglio] = useState(TAGLI[0]!);
  const testa = riga.celle[0]!.v;

  const compra = async (v: FairValueRowUi, prezzoCents: number, tokenId?: string) => {
    const key = v.outcome;
    const sizeUsd = taglio;
    setConferma(null);
    setAcquisto((s) => ({ ...s, [key]: { fase: "invio" } }));
    try {
      const r = await post<{ ok: boolean; detail?: string; error?: string }>("/api/fair-values/buy", {
        match: v.match,
        kind: v.kind,
        line: v.line,
        outcome: v.outcome,
        priceCents: prezzoCents,
        sizeUsd,
        note: `fair ${(v.fairProb * 100).toFixed(1)}`,
      });
      setAcquisto((s) => ({
        ...s,
        [key]: { fase: "fatto", ok: r.ok, testo: r.ok ? (r.detail ?? "inviato") : (r.error ?? "non riuscito"), tokenId },
      }));
    } catch (e) {
      setAcquisto((s) => ({ ...s, [key]: { fase: "fatto", ok: false, testo: e instanceof ApiError ? e.message : "errore di rete" } }));
    }
  };

  const vendi = async (key: string, tokenId: string) => {
    setAcquisto((s) => ({ ...s, [key]: { fase: "invio" } }));
    try {
      await post(`/api/bot/sell/${encodeURIComponent(tokenId)}`);
      setAcquisto((s) => ({ ...s, [key]: { fase: "venduto" } }));
    } catch (e) {
      setAcquisto((s) => ({ ...s, [key]: { fase: "fatto", ok: false, testo: e instanceof ApiError ? e.message : "errore di rete", tokenId } }));
    }
  };

  return (
    <div className="lav-riga">
      <div className="lav-mercato">
        <span className="lav-nome">{riga.nome}</span>
        <span className="lav-meta">
          {riga.bloccoComune
            ? (GATE_TESTO[riga.bloccoComune] ?? riga.bloccoComune)
            : `${testa.platforms ?? "—"} aziende · ${testa.engines} motori · σ ${testa.sigmaPoints.toFixed(1)}`}
        </span>
        {riga.giaAperto && (
          <span className="lav-gia-aperto" title="Una posizione su questo mercato è già stata aperta su questa partita: le regole fair-value ne aprono una sola (oneEntryPerMatch). Un click qui la apre lo stesso — la politica è dell'automatico, e chi clicca la sta scavalcando.">
            già aperto
          </span>
        )}
      </div>
      <div className="lav-celle-box">
      <div className="lav-celle">
        {riga.celle.map((c) => {
          const { v, giudizio, q } = c;
          const bloccata = giudizio.esito === "bloccata";
          const aperta = apertaSu === v.outcome;

          // Il prezzo è l'ASK, non il midpoint: è quello che si paga. Una quota
          // più vecchia del giro completo del ponte non è un mercato fermo, è
          // un filo interrotto — e non si compra su un filo interrotto.
          const quotaViva = q?.at != null && Date.now() - q.at < QUOTE_MAX_AGE_MS;
          const askCents = quotaViva && q?.ask != null ? q.ask * 100 : null;
          const margine = askCents == null ? null : v.buyMaxCents - askCents;
          // Verde = "sotto il limite, e la misura è buona". Il verdetto si
          // ricalcola ogni secondo, quindi il verde si spegne da sé quando la
          // misura supera il proprio maxFairAgeSeconds.
          const utilizzabile = margine != null && margine > 0 && giudizio.esito === "passa";
          const book = migliorLavagna(boards, v.kind, v.line, v.outcome);

          const fair = formato === "decimale" ? quotaDecimale(v.fairProb) : `${(v.fairProb * 100).toFixed(0)}`;
          const limite = formato === "decimale" ? centesimiInQuota(v.buyMaxCents) : v.buyMaxCents.toFixed(1);

          // La tacca del limite sta fissa al centro; il pallino è l'ask, e ogni
          // centesimo di scarto vale il 5% della larghezza. Oltre ±10¢ non
          // serve sapere di quanto: serve sapere da che parte.
          const pos = margine == null ? 50 : Math.max(4, Math.min(96, 50 - (margine / 10) * 50));

          return (
            <div
              className="lav-cella-wrap"
              key={v.outcome}
              onMouseEnter={() => setApertaSu(v.outcome)}
              onMouseLeave={() => setApertaSu((o) => (o === v.outcome ? null : o))}
            >
              <button
                type="button"
                className={`lav-cella ${bloccata ? "ferma" : ""} ${utilizzabile ? "buona" : ""}`}
                onClick={() => {
                  if (!operabile || askCents == null) return;
                  setConferma({ v, prezzo: askCents, tokenId: q?.tokenId });
                }}
                onFocus={() => setApertaSu(v.outcome)}
                onBlur={() => setApertaSu((o) => (o === v.outcome ? null : o))}
                aria-label={
                  askCents == null
                    ? `${NOME_ESITO[v.outcome] ?? v.outcome}: nessun prezzo su Polymarket`
                    : `${NOME_ESITO[v.outcome] ?? v.outcome} a ${askCents.toFixed(0)} centesimi, limite ${v.buyMaxCents.toFixed(1)} — apri la conferma d'acquisto`
                }
                title={
                  askCents == null
                    ? "Polymarket non sta quotando questo esito (mercato non risolto, o il prezzo non è ancora arrivato)"
                    : bloccata
                      ? `${GATE_TESTO[giudizio.gate] ?? giudizio.gate} — la misura non è affidabile, il limite che ne esce nemmeno`
                      : giudizio.esito === "passa"
                        ? `passa ${giudizio.regola}${giudizio.enabled ? "" : " (regola spenta: il bot non compra da solo, tu sì)"}`
                        : "nessuna regola copre questo mercato"
                }
              >
                <span className="c-testa">
                  <span className="lav-esito">{NOME_ESITO[v.outcome] ?? v.outcome}</span>
                  <span className={`c-chip ${utilizzabile ? "buono" : ""}`}>
                    {margine == null ? "no prezzo" : `${margine > 0 ? "+" : ""}${margine.toFixed(1)}¢`}
                  </span>
                </span>
                <span className="c-prezzo">
                  {askCents == null ? <i className="c-vuoto">—</i> : formato === "decimale" ? centesimiInQuota(askCents) : askCents.toFixed(0)}
                  {askCents != null && formato === "centesimi" && <i className="lav-unita">¢</i>}
                  <small>{askCents == null ? "polymarket" : "polymarket chiede"}</small>
                </span>
                <span className="c-mis" aria-hidden="true">
                  {margine != null && (
                    <i className={`c-mis-fill ${margine < 0 ? "sotto" : ""}`} style={{ left: `${Math.min(50, pos)}%`, width: `${Math.abs(50 - pos)}%` }} />
                  )}
                  <i className="c-mis-tacca" />
                  {margine != null && <i className="c-mis-pallino" style={{ left: `${pos}%` }} />}
                </span>
                <span className="c-piede">
                  <span>
                    {formato === "decimale" ? "min" : "max"} {limite}
                    {formato === "centesimi" ? "¢" : ""}
                  </span>
                  <span>{book ? `book ${formato === "decimale" ? book.quota.toFixed(2) : `${(100 / book.quota).toFixed(0)}¢`}` : "book —"}</span>
                </span>
                <span className="c-nota">
                  {bloccata && !riga.bloccoComune
                    ? (GATE_TESTO[giudizio.gate] ?? giudizio.gate)
                    : `fair ${fair}${formato === "centesimi" ? "¢" : ""}${q?.askShares != null ? ` · ${Math.round(q.askShares)} quote` : ""}`}
                </span>
              </button>

              {aperta && conferma == null && <TendinaLavagne boards={boards} kind={v.kind} line={v.line} outcome={v.outcome} formato={formato} />}
            </div>
          );
        })}
      </div>

      {/* LA CONFERMA E' DI RIGA, NON DI CASELLA.
          Una casella e' larga un centinaio di pixel: "Compra 250$" ci si tronca,
          e un pulsante che spende soldi troncato e' esattamente quello che non
          deve succedere. Mentre si conferma, gli altri esiti della riga non
          servono — quindi la barra ci passa sopra, e ripete prezzo e mercato
          cosi' non si conferma "a memoria" di quello che si era cliccato. */}
      {conferma && (
        <div className={`lav-conferma ${botMode === "LIVE" ? "live" : ""}`} role="group" aria-label="Conferma acquisto">
          <span className="lav-conferma-cosa">
            {/* Nella lingua in cui è scritta la pagina: chi legge ne ha in
                testa una sola, e leggere "37¢" sotto una casella che dice 2.70
                è il modo di confermare il prezzo sbagliato. */}
            {riga.nome} · <b>{NOME_ESITO[conferma.v.outcome] ?? conferma.v.outcome}</b> a{" "}
            <b>{formato === "decimale" ? centesimiInQuota(conferma.prezzo) : `${conferma.prezzo.toFixed(0)}¢`}</b>
          </span>
          <span className={`lav-conferma-modo ${botMode === "LIVE" ? "live" : ""}`}>{botMode}</span>
          <span className="spacer" />
          <span className="lav-conferma-tagli">
            {TAGLI.map((t) => (
              <button key={t} type="button" aria-pressed={t === taglio} onClick={() => setTaglio(t)}>
                {t}$
              </button>
            ))}
          </span>
          <button type="button" className="annulla" onClick={() => setConferma(null)}>
            Annulla
          </button>
          {/* Il limite e' l'ask che si aveva sotto gli occhi: se nel frattempo si
              e' mosso l'ordine non riempie, che e' la risposta giusta e non un
              fallimento. */}
          <button type="button" className="vai" onClick={() => void compra(conferma.v, conferma.prezzo, conferma.tokenId)}>
            {botMode === "LIVE" ? `Compra ${taglio}$ — LIVE` : `Compra ${taglio}$`}
          </button>
        </div>
      )}
      </div>

      {/* Gli esiti stanno sotto la riga e non dentro la casella: "riempito 67.6
          quote a 37.0c" non entra in cento pixel, e un `fak` non riempie spesso
          abbastanza da valere una riga sua. Uno per esito, cosi' due acquisti
          nella stessa riga non si scavalcano. */}
      {Object.entries(acquisto).map(([outcome, stato]) =>
        !stato ? null : (
          <div key={outcome} className={`lav-ordine ${stato.fase === "fatto" ? (stato.ok ? "ok" : "no") : stato.fase === "venduto" ? "ok" : ""}`}>
            <span className="lav-ordine-esito">{NOME_ESITO[outcome] ?? outcome}</span>
            <span className="grow">
              {stato.fase === "invio" ? "invio…" : stato.fase === "venduto" ? "venduto" : stato.testo}
            </span>
            {stato.fase === "fatto" && stato.ok && stato.tokenId && (
              <button type="button" onClick={() => void vendi(outcome, stato.tokenId!)}>
                vendi
              </button>
            )}
            {stato.fase !== "invio" && (
              <button type="button" aria-label="chiudi" onClick={() => setAcquisto((s) => ({ ...s, [outcome]: undefined as never }))}>
                ×
              </button>
            )}
          </div>
        ),
      )}
    </div>
  );
}

const GATE_TESTO: Record<string, string> = {
  minEngines: "pochi motori",
  minPlatforms: "poche aziende",
  maxSigmaPoints: "fonti in disaccordo",
  maxFairAgeSeconds: "quote vecchie",
};

function FairValuesView({ active }: { active: boolean }) {
  const [data, setData] = useState<FairValuesResponse | null>(null);
  const [err, setErr] = useState("");
  const [soloPassanti, setSoloPassanti] = useState(false);
  // La lingua in cui si leggono i numeri. Vive qui e non nella cella: due
  // formati sullo stesso schermo si leggerebbero l'uno per l'altro. E vive
  // anche nel browser, così chi ne preferisce una la ritrova.
  const [formato, setFormato] = useState<Formato>(formatoSalvato);
  const switchFormato = () =>
    setFormato((f) => {
      const next = f === "decimale" ? "centesimi" : "decimale";
      salvaFormato(next);
      return next;
    });
  // Le età si misurano su ADESSO, quindi la pagina deve ribattere anche quando i
  // dati non cambiano: senza, una misura scaduta resterebbe scritta "12s" per
  // sempre, che è esattamente il tipo di bugia che questa pagina esiste per non dire.
  const [now, setNow] = useState(() => Date.now());
  // Una rilettura in corso, e quante ne sono finite: la seconda fa ricaricare le
  // lavagne dei bookmaker, che hanno una cache loro.
  const [rileggendo, setRileggendo] = useState(false);
  const [generazione, setGenerazione] = useState(0);
  const [messaggio, setMessaggio] = useState("");
  const [cambioModo, setCambioModo] = useState(false);

  const carica = () =>
    api<FairValuesResponse>("/api/fair-values?maxAge=1800").then((r) => {
      setData(r);
      setNow(Date.now());
      setErr("");
    });

  useEffect(() => {
    if (!active) return;
    let dead = false;
    const load = () =>
      void carica().catch((e) => {
        if (!dead) setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : "errore di rete");
      });
    load();
    const t = setInterval(load, 10_000);
    const tick = setInterval(() => setNow(Date.now()), 1_000);
    return () => {
      dead = true;
      clearInterval(t);
      clearInterval(tick);
    };
  }, [active]);

  /** «Rileggi adesso». Il motore risponde a giro FINITO, quindi il pulsante può
   *  dire "fatto" invece di sperarlo — e un rifiuto per cadenza torna con dentro
   *  quanto manca, perché non è un errore ma la risposta. */
  const rileggi = async () => {
    if (rileggendo) return;
    setRileggendo(true);
    setMessaggio("");
    try {
      const r = await post<{ ok: boolean; detail?: string }>("/api/fair-values/refresh");
      setMessaggio(r.ok ? `riletto: ${r.detail}` : (r.detail ?? "non riletto"));
      if (r.ok) {
        cacheBoards.clear();
        setGenerazione((g) => g + 1);
        await carica().catch(() => {});
      }
    } catch (e) {
      setMessaggio(e instanceof ApiError ? `${e.status}: ${e.message}` : "errore di rete");
    } finally {
      setRileggendo(false);
    }
  };

  const cambiaModo = async (mode: "auto" | "manuale") => {
    if (cambioModo || data?.mode?.mode === mode) return;
    setCambioModo(true);
    setMessaggio("");
    try {
      const r = await post<{ moved: number; restartRequired: boolean; detail: string }>("/api/fair-values/mode", { mode });
      setMessaggio(r.detail);
      await carica().catch(() => {});
    } catch (e) {
      setMessaggio(e instanceof ApiError ? `${e.status}: ${e.message}` : "errore di rete");
    } finally {
      setCambioModo(false);
    }
  };

  const gates = data?.gates ?? [];
  const quote = useMemo(() => {
    const m = new Map<string, QuoteUi>();
    for (const q of data?.quotes ?? []) m.set(chiaveQuota(q.match, q.kind, q.line ?? "", q.outcome), q);
    return m;
  }, [data]);
  const righe = useMemo(() => {
    const vs = (data?.values ?? []).map((v) => ({
      v,
      giudizio: judgeUi(v, gates, now),
      q: quote.get(chiaveQuota(v.match, v.kind, v.line, v.outcome)),
    }));
    return soloPassanti ? vs.filter((r) => r.giudizio.esito === "passa") : vs;
  }, [data, gates, now, soloPassanti, quote]);
  // I colpi gia' spesi: una misura puo' passare ogni gate e non comprare
  // niente perche' quel mercato e' gia' aperto su quella partita. Resta fuori
  // dal verdetto di proposito — quello parla della misura, questo di cosa e'
  // gia' successo (vedi src/fair-gates.ts).
  const spesi = useMemo(() => new Set(data?.shots ?? []), [data]);
  const lavagne = useMemo(() => inLavagne(righe, spesi), [righe, spesi]);

  // Il conto per gate è il vero contenuto informativo: dice dove intervenire.
  const perGate = useMemo(() => {
    const m = new Map<string, number>();
    for (const { giudizio } of (data?.values ?? []).map((v) => ({ giudizio: judgeUi(v, gates, now) }))) {
      const k = giudizio.esito === "passa" ? "passa" : giudizio.esito === "nessuna-regola" ? "nessuna regola" : giudizio.gate;
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [data, gates, now]);

  // Quante caselle si potrebbero comprare in questo momento: è il numero che
  // dice se vale la pena guardare la pagina, e non lo dava nessuna delle altre
  // metriche (una misura può passare ogni gate e costare più del limite).
  const comprabili = useMemo(
    () =>
      righe.filter((r) => {
        const ask = r.q?.at != null && Date.now() - r.q.at < QUOTE_MAX_AGE_MS && r.q.ask != null ? r.q.ask * 100 : null;
        return ask != null && r.giudizio.esito === "passa" && r.v.buyMaxCents - ask > 0;
      }).length,
    [righe, now],
  );

  const modo = data?.mode?.mode ?? "misto";
  const passanti = perGate.find(([k]) => k === "passa")?.[1] ?? 0;

  return (
    <div className="page-grid two">
      <div className="card span-all">
        <div className="card-head">
          Motore quote
          <span className="spacer" />
          <span className={`pill ${data?.botMode === "LIVE" ? "bad" : "warn"}`} title="In PAPER una casella verde apre una posizione finta; in LIVE spende soldi veri.">
            {data?.botMode ?? "—"}
          </span>
          <span className={`pill ${data?.connected ? "ok" : "warn"}`}>{data?.connected ? "collegato" : "scollegato"}</span>
        </div>
        <div className="card-body">
          {err && <div className="empty">Impossibile caricare i fair value — {err}</div>}
          <div className="row">
            <span className="grow">{data?.detail ?? "…"}</span>
          </div>
          {/* L'interruttore è dove stanno le regole nel file del bot, non una
              preferenza del pannello: vedi src/fair-mode.ts. */}
          <div className="row">
            <span className="grow">
              {modo === "auto"
                ? "Il bot compra da solo appena una misura passa le sue soglie. I click sulla lavagna funzionano lo stesso."
                : modo === "manuale"
                  ? "Il bot non compra niente su queste misure: si compra solo cliccando una casella."
                  : "Le regole fair-value sono divise fra attive e spente — l'interruttore non indovina quale intendevi."}
            </span>
            <div className="lav-modo" role="group" aria-label="Modalità delle regole fair-value">
              <button type="button" aria-pressed={modo === "auto"} disabled={cambioModo} onClick={() => void cambiaModo("auto")}>
                Automatico
              </button>
              <button type="button" aria-pressed={modo === "manuale"} disabled={cambioModo} onClick={() => void cambiaModo("manuale")}>
                Manuale
              </button>
            </div>
          </div>
          {messaggio && (
            <div className="row">
              <span className="grow">{messaggio}</span>
            </div>
          )}
          {gates.length === 0 && !err && (
            <div className="row">
              <span className="grow">Nessuna regola fair-value nelle regole del bot — i valori si mostrano senza verdetto, e nessuna casella diventa verde.</span>
            </div>
          )}
          {data && !data.botRunning && (
            <div className="row">
              <span className="grow">
                <b>Il bot non è in esecuzione.</b> Non arrivano prezzi da Polymarket e le caselle non si possono comprare.
              </span>
            </div>
          )}
          <div className="metric-grid gs-metrics">
            <div className="metric">
              <span className="metric-label">Misure correnti</span>
              <strong className="num">{data?.values.length ?? "—"}</strong>
            </div>
            <div className="metric">
              <span className="metric-label">Passerebbero</span>
              <strong className="num">{data ? passanti : "—"}</strong>
            </div>
            <div className="metric">
              <span className="metric-label">Sotto il limite</span>
              <strong className={`num ${comprabili > 0 ? "pnl-pos" : ""}`}>{data ? comprabili : "—"}</strong>
            </div>
            <div className="metric">
              <span className="metric-label">Partite</span>
              <strong className="num">{data ? new Set(data.values.map((v) => v.match)).size : "—"}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="card span-all">
        <div className="card-head">
          Dove si fermano
          <span className="spacer" />
          <button className={`btn sm ${soloPassanti ? "primary" : "ghost"}`} onClick={() => setSoloPassanti((s) => !s)}>
            {soloPassanti ? "Mostra tutte" : "Solo quelle che passano"}
          </button>
        </div>
        <div className="card-body">
          {perGate.length === 0 ? (
            <div className="empty">Nessuna misura nell'ultima mezz'ora.</div>
          ) : (
            perGate.map(([k, n]) => (
              <div className="row" key={k}>
                <span className="grow">{k === "passa" ? "Passa tutte le gate" : (GATE_TESTO[k] ?? k)}</span>
                <span className="stat">{n}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="card span-all">
        <div className="card-head">
          Lavagna
          <span className="spacer" />
          {/* Il comando esplicito: cliccare una casella ora COMPRA, quindi il
              cambio di lingua vive solo qui — dov'era già, e dove non si rischia
              di ordinare volendo leggere. */}
          <button type="button" className="lav-format" onClick={switchFormato} aria-live="polite">
            <span className={formato === "decimale" ? "on" : ""}>quota decimale</span>
            <span className="lav-format-sep" aria-hidden="true">⇄</span>
            <span className={formato === "centesimi" ? "on" : ""}>centesimi</span>
          </button>
        </div>
        <div className="card-body lav-body">
          {lavagne.length === 0 ? (
            <div className="empty">{soloPassanti ? "Nessuna misura passa le gate in questo momento." : "Nessuna misura."}</div>
          ) : (
            <div className="lav-griglia">
              {lavagne.map((partita) => (
                <Lavagna
                  key={partita.match}
                  partita={partita}
                  formato={formato}
                  now={now}
                  quote={quote}
                  botMode={data?.botMode ?? "PAPER"}
                  operabile={data?.botRunning !== false}
                  generazione={generazione}
                  onRilettura={() => void rileggi()}
                  rileggendo={rileggendo}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
